import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./App.css";
import { toMacroZoneId } from "./macroSplits";
import AdminPanel from "./pricing/AdminPanel";
import { useI18n } from "./i18n/i18n";
import LangDropdown from "./components/LangDropdown";
import type { TKey } from "./i18n/translations";

// ================================================
//          AUTH (sessionStorage only)
//          (token clears when the tab/window closes)
// ================================================
const BACKEND_URL =
  (process.env.REACT_APP_BACKEND_URL as string) ||
  "https://mytslmap-worker.rob3kop.workers.dev";
const SS_AUTH_TOKEN = "mytsl_authToken_v1";


const SS_ADMIN_UNLOCK = "mytsl_adminUnlock_v1";
type MeResponse = {
  isLoggedIn: boolean;
  email?: string;
  plan: "DEMO" | "FULL";
  trialActive: boolean;
  subscriptionActive: boolean;
  trialEndsAt?: string | null;
  hasAdminPin?: boolean;
  hasCustomRates?: boolean;
};

function getToken(): string | null {
  try {
    return sessionStorage.getItem(SS_AUTH_TOKEN);
  } catch {
    return null;
  }
}

function setToken(token: string) {
  try {
    sessionStorage.setItem(SS_AUTH_TOKEN, token);
  } catch {}
}

function clearToken() {
  try {
    sessionStorage.removeItem(SS_AUTH_TOKEN);
  } catch {}
}

async function apiMe(token?: string | null): Promise<MeResponse> {
  const headers: Record<string, string> = {};
  const t = token ?? getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  const res = await fetch(`${BACKEND_URL}/me`, { headers });

  // In DEMO (no token), /me may return 401 "Missing token" — treat as normal logged-out state.
  if (!t && res.status === 401) {
    return { isLoggedIn: false, plan: "DEMO", trialActive: false, subscriptionActive: false, trialEndsAt: null };
  }

  if (!res.ok) throw new Error(`ME_HTTP_${res.status}`);

  const data = (await res.json().catch(() => null)) as MeResponse | null;
  if (!data) throw new Error("ME_BAD_JSON");
  return data;
}


async function apiAdminPinStatus(token: string): Promise<{ hasPin: boolean }> {
  const res = await fetch(`${BACKEND_URL}/api/admin/pin/status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `PIN_STATUS_HTTP_${res.status}`);
  return { hasPin: !!data?.hasPin };
}

async function apiAdminPinSetup(token: string, pin: string): Promise<string> {
  const res = await fetch(`${BACKEND_URL}/api/admin/pin/setup`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ pin })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `PIN_SETUP_HTTP_${res.status}`);
  return String(data?.adminUnlockToken || "");
}

async function apiAdminPinVerify(token: string, pin: string): Promise<string> {
  const res = await fetch(`${BACKEND_URL}/api/admin/pin/verify`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ pin })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `PIN_VERIFY_HTTP_${res.status}`);
  return String(data?.adminUnlockToken || "");
}

async function apiAdminPinChange(token: string, oldPin: string, newPin: string): Promise<string> {
  const res = await fetch(`${BACKEND_URL}/api/admin/pin/change`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ oldPin, newPin })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `PIN_CHANGE_HTTP_${res.status}`);
  return String(data?.adminUnlockToken || "");
}

async function apiRegister(
  email: string,
  password: string,
  startTrial: boolean,
  acceptTerms: boolean,
  acceptPrivacy: boolean
): Promise<{ token: string }> {
  const res = await fetch(`${BACKEND_URL}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      startTrial,
      acceptTerms,
      acceptPrivacy
    })
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || `REGISTER_HTTP_${res.status}`);
  }

  return { token: data.token as string };
}

async function apiLogin(
  email: string,
  password: string
): Promise<{ token: string }> {
  const res = await fetch(`${BACKEND_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || `LOGIN_HTTP_${res.status}`);
  }

  return { token: data.token as string };
}

type PostalResolveResponse =
  | {
      ok: true;
      country: string;
      postal: string;
      matchedPostal: string;
      fallbackUsed: boolean;
      name: string;
      admin1?: string;
      lat: number;
      lng: number;
    }
  | {
      ok: false;
      error: "bad_request" | "not_found" | "load_failed";
    };

async function apiResolvePostal(
  country: string,
  postal: string
): Promise<PostalResolveResponse> {
  const qs = new URLSearchParams({
    country: String(country || "").trim().toUpperCase(),
    postal: String(postal || "").trim()
  });

  const res = await fetch(`${BACKEND_URL}/api/postal/resolve?${qs.toString()}`);
  const data = (await res.json().catch(() => null)) as PostalResolveResponse | null;

  if (!data) {
    throw new Error(`POSTAL_BAD_JSON_${res.status}`);
  }

  if (!res.ok) {
    return data;
  }

  return data;
}

// ================================================
//            TYPES
// ================================================
type PriceResult = {
  total: number;
  distanceKm: number;
  pickup_base: number;
  delivery_base: number;
  per_km: number;
  distance_fee: number;
  surcharges: number;
  min_price: number;
  pricingMode?: "IN" | "OUT" | null;
};

type ExtraOptionId = "motorways" | "tolls" | "ferries" | "tunnels" | "lift";

interface ExtraOptionConfig {
  id: ExtraOptionId;
  labelKey: TKey;
  type: "perKm" | "fixed";
  amount: number;
}

const EXTRA_OPTIONS: ExtraOptionConfig[] = [
  {
    id: "motorways",
    labelKey: "optMotorways",
    type: "fixed",
    amount: 0
  },
  {
    id: "tolls",
    labelKey: "optTolls",
    type: "fixed",
    amount: 150
  },
  {
    id: "ferries",
    labelKey: "optFerries",
    type: "fixed",
    amount: 220
  },
  {
    id: "tunnels",
    labelKey: "optTunnels",
    type: "fixed",
    amount: 40
  },
  {
    id: "lift",
    labelKey: "optLift",
    type: "fixed",
    amount: 0
  }
];

const LS_EXTRA_OPTIONS_KEY = "mytsl_extraOptions_v1";
const LS_EXTRA_USER_AMOUNTS_KEY = "mytsl_extraUserAmounts_v1";
const LS_DEADHEAD_KM_KEY = "mytsl_deadheadKm_v1"; // "dolot" (km) doliczany do dystansu
const LS_DEADHEAD_ENABLED_KEY = "mytsl_deadheadEnabled_v1"; // ON/OFF dla dolotu

// ================================================
//            BASE RATES (pickup/delivery/min)
// ================================================
const RATES = {
  default: {
    pickup_base: 0,
    delivery_base: 0,
    per_km: 1,
    min_price: 30,
    cross_border_fee: 0
  },
  byZones: {} as Record<
    string,
    {
      pickup_base: number;
      delivery_base: number;
      per_km: number;
      min_price: number;
      cross_border_fee: number;
    }
  >
};

function resolveRates(oz: string, dz: string) {
  const key = `${oz}|${dz}`;
  return RATES.byZones[key] || RATES.default;
}

// ============================
// DEMO fallback price (marketing)
// ============================
function demoFallbackPriceResult(
  distanceKm: number,
  vehicleType: string
): PriceResult {
  const perKmBase = 0.1;

  const mult =
    vehicleType === "artic" ? 1.0 :
    vehicleType === "solo" ? 0.75 :
    vehicleType === "van" ? 0.5 :
    1.0;

  const per_km = perKmBase * mult;
  const distance_fee = distanceKm * per_km;

  return {
    total: distance_fee,
    distanceKm,
    pickup_base: 0,
    delivery_base: 0,
    per_km,
    distance_fee,
    surcharges: 0,
    min_price: 0
  };
}

// ================================================
//            HAVERSINE DISTANCE (fallback)
// ================================================
function haversineKm(a: [number, number], b: [number, number]) {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371;

  const lon1 = toRad(a[0]);
  const lat1 = toRad(a[1]);
  const lon2 = toRad(b[0]);
  const lat2 = toRad(b[1]);

  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(h));
}

// ================================================
//            CENTROID
// ================================================
function centroidOfGeom(geom: any): [number, number] {
  const coords: [number, number][] = [];

  function collect(v: any) {
    if (!v) return;
    if (typeof v[0] === "number" && typeof v[1] === "number") {
      coords.push([v[0], v[1]]);
    } else if (Array.isArray(v)) {
      for (const x of v) collect(x);
    }
  }

  collect(geom.coordinates);

  if (coords.length === 0) return [0, 0];

  const lon = coords.reduce((s, v) => s + v[0], 0) / coords.length;
  const lat = coords.reduce((s, v) => s + v[1], 0) / coords.length;

  return [lon, lat];
}

function roundTo(x: number, step = 0.5) {
  return Math.round(x / step) * step;
}

function formatDriveMinutes(totalMin: number): string {
  if (!Number.isFinite(totalMin) || totalMin <= 0) return "—";

  const totalRounded = Math.round(totalMin);
  const h = Math.floor(totalRounded / 60);
  const m = totalRounded % 60;

  if (h === 0) {
    return `${m} min`;
  }

  if (m === 0) {
    return `${h} h`;
  }

  return `${h} h ${m} min`;
}

function estimateTruckEtaMinutes(driveMin: number): number {
  if (!Number.isFinite(driveMin) || driveMin <= 0) return 0;

  const breakBlocks = Math.floor(driveMin / 270);
  const breakMin = breakBlocks * 45;

  const dailyRestMin = driveMin > 540 ? 11 * 60 : 0;

  return driveMin + breakMin + dailyRestMin;
}

function getPricingModeLabel(
  price: PriceResult | null,
  destinationRegionId?: string | null,
  t?: (key: TKey) => string
): string {
  if (!price?.pricingMode || !t) return "—";

  if (price.pricingMode === "OUT") {
    return `${t("rateLabel")}: ${t("pricingModeOutOutbound")}`;
  }

  const destId = String(destinationRegionId || "").toUpperCase();

  if (destId.startsWith("PL")) {
    return `${t("rateLabel")}: ${t("pricingModeInReturn")}`;
  }

  return `${t("rateLabel")}: ${t("pricingModeInHardDirection")}`;
}

// ================================================
//            ROUTING (ORS) — NEW (CH + UK/IE RULES)
// ================================================
type RouteInfo = {
  distanceKm: number;
  durationMin: number;
  geometry: GeoJSON.LineString | null;
  provider: "ors" | "demo" | "none";
  error?: string;
};

const ORS_GEOJSON_URL = "/ors/v2/directions/driving-car/geojson";
const ORS_SNAP_URL = "/ors/v2/snap/driving-car/geojson";

// geojsony w public/data
const CH_STATES_URL = "/data/ch_states.geojson";
const IE_COUNTRY_URL = "/data/ie_provinces.geojson";   // <- ZMIEŃ jeśli masz inną nazwę
const UK_COUNTRY_URL = "/data/gb_counties.geojson";   // <- ZMIEŃ jeśli masz inną nazwę

// ORS country ids
const ORS_COUNTRY_ID_CH = 193;
const ORS_COUNTRY_ID_CZ = 52;
const ORS_COUNTRY_ID_GB = 213;

// “port” we Francji – waypoint wymuszający “prom”
const FR_VIA_CHERBOURG: [number, number] = [-1.623, 49.633]; // [lng, lat]

type AnyFC = GeoJSON.FeatureCollection<GeoJSON.Geometry, any>;

function roundKey([lng, lat]: [number, number]) {
  return `${lng.toFixed(5)},${lat.toFixed(5)}`;
}

// ---------- Geometry utils (no turf) ----------
type LngLat = [number, number];
type Ring = LngLat[];

// Ray casting for one ring (Polygon outer or hole)
function pointInRing(p: LngLat, ring: Ring): boolean {
  const [x, y] = p;
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    const intersect =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}

// Polygon = [outerRing, hole1, hole2...]
function pointInPolygon(p: LngLat, poly: LngLat[][]): boolean {
  if (!poly?.length) return false;
  const outer = poly[0];
  if (!pointInRing(p, outer)) return false;

  // if inside any hole -> false
  for (let i = 1; i < poly.length; i++) {
    if (pointInRing(p, poly[i])) return false;
  }
  return true;
}

function pointInMultiPolygon(p: LngLat, multi: GeoJSON.MultiPolygon): boolean {
  for (const poly of multi.coordinates) {
    if (pointInPolygon(p, poly as any)) return true;
  }
  return false;
}

function pointInGeometry(
  p: LngLat,
  geom: GeoJSON.Geometry | null | undefined
): boolean {
  if (!geom) return false;

  if (geom.type === "Polygon") {
    return pointInPolygon(p, geom.coordinates as any);
  }

  if (geom.type === "MultiPolygon") {
    return pointInMultiPolygon(p, geom);
  }

  return false;
}

// ---------- Generic loader: FeatureCollection -> MultiPolygon ----------
function fcToMultiPolygon(fc: AnyFC): GeoJSON.MultiPolygon {
  const coords: GeoJSON.MultiPolygon["coordinates"] = [];

  for (const f of fc.features) {
    const g = f?.geometry;
    if (!g) continue;

    if (g.type === "Polygon") coords.push(g.coordinates);
    else if (g.type === "MultiPolygon") coords.push(...g.coordinates);
  }

  return { type: "MultiPolygon", coordinates: coords };
}

function makeEnsureMultiLoaded(url: string) {
  let multi: GeoJSON.MultiPolygon | null = null;
  let loading: Promise<void> | null = null;

  // bbox: [minLng, minLat, maxLng, maxLat]
  let bb: [number, number, number, number] | null = null;

  const ensure = async () => {
    if (multi) return;
    if (loading) return loading;

    loading = (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Cannot load geojson ${url}: ${res.status}`);
      const fc = (await res.json()) as AnyFC;
      multi = fcToMultiPolygon(fc);

      // policz bbox raz
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const poly of multi.coordinates) {
        for (const ring of poly) {
          for (const pos of ring) {
            const x = pos[0], y = pos[1];
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }
      bb = [minX, minY, maxX, maxY];
    })();

    return loading;
  };

  const get = () => multi;

  const contains = (p: LngLat) => {
    if (!multi || !bb) return false;
    const [x, y] = p;
    const [minX, minY, maxX, maxY] = bb;
    // szybkie odcięcie
    if (x < minX || x > maxX || y < minY || y > maxY) return false;
    return pointInMultiPolygon(p, multi);
  };

  return { ensure, get, contains };
}

// CH/IE/UK
const CH = makeEnsureMultiLoaded(CH_STATES_URL);
const IE = makeEnsureMultiLoaded(IE_COUNTRY_URL);
const UK = makeEnsureMultiLoaded(UK_COUNTRY_URL);

// ---------- ORS Snap ----------
async function snapToRoad(
  apiKey: string,
  p: [number, number]
): Promise<[number, number] | null> {
  try {
    const res = await fetch(ORS_SNAP_URL, {
      method: "POST",
      headers: {
        Authorization: apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        locations: [p],
        radius: 2000,
      }),
    });

    if (!res.ok) return null;

    const data: any = await res.json();
    const feat = data?.features?.[0];
    const coords = feat?.geometry?.coordinates;

    if (Array.isArray(coords) && typeof coords[0] === "number") {
      return [coords[0], coords[1]];
    }
    return null;
  } catch {
    return null;
  }
}

let routeAbort: AbortController | null = null;

// ---------- ORS Route (rules) ----------
async function fetchORSRoute(
  start: [number, number],
  end: [number, number],
  originCountry?: string,
  destCountry?: string,
  startZoneKey?: string,
  endZoneKey?: string
): Promise<RouteInfo> {
  // Backend is the single source of truth for ORS requests.
  if (routeAbort) {
    try { routeAbort.abort(); } catch {}
  }
  routeAbort = new AbortController();

  // CH avoidance rule (robust): avoid Switzerland only when neither endpoint is in CH.
  // We base this on region country codes (e.g. "PL", "DE", "CH") to avoid geometry/race issues.
let avoidCountries: number[] | undefined = undefined;

const oc = (originCountry || "").toUpperCase();
const dc = (destCountry || "").toUpperCase();

const isCH = (x: string) => x === "CH";
const isIE = (x: string) => x === "IE";
const isUK = (x: string) => x === "UK" || x === "GB";
const isCZ = (x: string) => x === "CZ";

const addAvoid = (id: number) => {
  if (!avoidCountries) avoidCountries = [];
  if (!avoidCountries.includes(id)) avoidCountries.push(id);
};

// 1) CH avoidance – dokładnie jak było
if (oc && dc && !isCH(oc) && !isCH(dc)) {
  addAvoid(ORS_COUNTRY_ID_CH);
}


if (oc && dc && !isCZ(oc) && !isCZ(dc)) {
  addAvoid(ORS_COUNTRY_ID_CZ);
}

// 2) UK avoidance – TYLKO gdy trasa dotyczy Irlandii i NIE jest do/z UK
// (IE -> kontynent Europy: wymuś prom, nie przejazd przez UK)
const shouldAvoidUK =
  oc && dc && (isIE(oc) || isIE(dc)) && !isUK(oc) && !isUK(dc);

if (shouldAvoidUK) {
  addAvoid(213);
}

// WAYPOINT tylko dla kierunku EU -> IE (czyli gdy DEST = IE)
// Dla IE -> EU nie ustawiamy via, żeby ORS nie doklejał Roscoff->Cherbourg.
const via = shouldAvoidUK && dc === "IE" ? FR_VIA_CHERBOURG : undefined;
const effectiveAvoidCountries = avoidCountries as number[] | undefined;
const hasChCzOnlyAvoidCountries =
  effectiveAvoidCountries?.length === 2 &&
  effectiveAvoidCountries.includes(ORS_COUNTRY_ID_CH) &&
  effectiveAvoidCountries.includes(ORS_COUNTRY_ID_CZ);

const isOrs521RouteError = (status: number, text: string) =>
  status === 502 && /\b521\b/.test(text);

  const doFetch = async (routeAvoidCountries: number[] | undefined) => {
    const token = sessionStorage.getItem(SS_AUTH_TOKEN);
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;

    return fetch(`${BACKEND_URL}/api/route`, {
      method: "POST",
      headers,

      body: JSON.stringify({
        start,
        end,
        avoidCountries: routeAvoidCountries,
        via,
        startZoneKey,
        endZoneKey
      }),
      signal: routeAbort!.signal
    });
  };

  // Retry once for transient ORS/backend issues
  let res = await doFetch(effectiveAvoidCountries);
  let resText: string | null = null;

  if (!res.ok) {
    resText = await res.text().catch(() => "");
  }

  if (!res.ok && hasChCzOnlyAvoidCountries && isOrs521RouteError(res.status, resText ?? "")) {
    // ORS sometimes rejects CH+CZ avoid lists for otherwise valid ferry/long routes.
    await new Promise((r) => setTimeout(r, 200));
    res = await doFetch([]);
    resText = null;
  } else if (!res.ok && (res.status === 502 || res.status === 504 || res.status === 429)) {
    // small backoff; abort-safe
    await new Promise((r) => setTimeout(r, 200));
    res = await doFetch(effectiveAvoidCountries);
    resText = null;
  }
if (!res.ok) {
    const txt = resText ?? (await res.text().catch(() => ""));
    throw new Error(`route_http_${res.status}${txt ? `: ${txt}` : ""}`);
  }

  const data = await res.json();

  // Expecting: { distanceKm, durationMin, geometry, provider, error }
  return {
    distanceKm: Number(data?.distanceKm || 0),
    durationMin: Number(data?.durationMin || 0),
    geometry: (data?.geometry ?? null) as any,
    provider: (data?.provider || "ors") as any,
    error: data?.error
  };
}

// ================================================
//           MAP ROUTE LAYER HELPERS
// ================================================
const ROUTE_SOURCE_ID = "route-source";
const ROUTE_LAYER_ID = "route-line";

/**
 * Remove route layer + source if present.
 * Safe to call many times.
 */
function clearRouteLayer(map: any) {
  try {
    if (!map) return;

    if (map.getLayer && map.getLayer(ROUTE_LAYER_ID)) {
      map.removeLayer(ROUTE_LAYER_ID);
    }
    if (map.getSource && map.getSource(ROUTE_SOURCE_ID)) {
      map.removeSource(ROUTE_SOURCE_ID);
    }
  } catch {
    // never break UI
  }
}

/**
 * Create/update route layer from GeoJSON geometry (LineString/MultiLineString).
 * Accepts `geometry` directly from ORS GeoJSON response.
 */
function upsertRouteLayer(map: any, geometry: any) {
  try {
    if (!map) return;

    if (!geometry || !geometry.type || !geometry.coordinates) {
      clearRouteLayer(map);
      return;
    }

    const feature = {
      type: "Feature",
      properties: {},
      geometry
    };

    const fc = {
      type: "FeatureCollection",
      features: [feature]
    };

    const existing = map.getSource?.(ROUTE_SOURCE_ID);
    if (existing && existing.setData) {
      existing.setData(fc);
      return;
    }

    // add source
    map.addSource(ROUTE_SOURCE_ID, {
      type: "geojson",
      data: fc
    });

    // add layer (make it visible and simple; won’t affect Stripe/auth/UI)
    map.addLayer({
      id: ROUTE_LAYER_ID,
      type: "line",
      source: ROUTE_SOURCE_ID,
      layout: {
        "line-join": "round",
        "line-cap": "round"
      },
      paint: {
        "line-color": "#ef4444",
        "line-width": 3,
        "line-opacity": 0.9
      }
    });
  } catch {
    // never break UI
  }
}

// ================================================
//     ROADS THIN RED (best-effort, safe)
// ================================================
/**
 * Try to recolor existing road layers in the basemap style.
 * Works “best-effort” across different styles (MapTiler/OSM/etc.).
 * If style IDs differ, it safely does nothing.
 */
function enableRoadsThinRed(map: any) {
  try {
    if (!map?.getStyle) return;

    const style = map.getStyle();
    const layers = style?.layers || [];
    for (const lyr of layers) {
      if (!lyr || lyr.type !== "line" || !lyr.id) continue;

      // heuristic: only touch likely road layers
      const id = String(lyr.id).toLowerCase();
      const looksLikeRoad =
        id.includes("road") ||
        id.includes("street") ||
        id.includes("highway") ||
        id.includes("motorway") ||
        id.includes("transportation") ||
        id.includes("bridge") ||
        id.includes("tunnel");

      if (!looksLikeRoad) continue;

      // set paint props if layer supports it
      try {
        map.setPaintProperty(lyr.id, "line-color", "#ef4444");
        map.setPaintProperty(lyr.id, "line-width", 0.8);
        map.setPaintProperty(lyr.id, "line-opacity", 0.8);
      } catch {
        // ignore per-layer failures
      }
    }
  } catch {
    // never break UI
  }
}

// ================================================
//           PRICE BREAKDOWN UI
// ================================================
function PriceBreakdown({
  result,
  t,
  truckEtaText,
  driveTimeText
}: {
  result: PriceResult | null;
  t: (key: TKey) => string;
  truckEtaText: string;
  driveTimeText: string;
}) {
  if (!result) return null;

return (
  <div
    style={{
      marginTop: 2,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 16
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          marginBottom: 2
        }}
      >
{result.distanceKm.toFixed(1)} km{result.pricingMode ? ` · ${t("priceLabel")}: ${result.pricingMode}` : ""}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: "bold",
          lineHeight: 1.15
        }}
      >
        {result.total.toFixed(2)} EUR
      </div>
    </div>

    <div
      style={{
        fontSize: 12,
        color: "#6b7280",
        textAlign: "right",
        flex: "0 0 170px",
        whiteSpace: "nowrap",
        lineHeight: 1.45,
        fontWeight: 600
      }}
    >
<div>{result.distanceKm > 0 ? `${t("driveTimeLabel")}: ${driveTimeText}` : ""}</div>
<div>{result.distanceKm > 0 ? `${t("truckEtaLabel")}: ${truckEtaText}` : ""}</div>
    </div>
  </div>
);
}

// ================================================
//           PIN MODAL
// ================================================
type PinMode = "setup" | "enter" | "change";

// ================================================
//           UI ALERT MODAL (replaces window.alert)
// ================================================
function AlertModal({
  isOpen,
  message,
  onClose
}: {
  isOpen: boolean;
  message: string;
  onClose: () => void;
}) {
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 40000
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(520px, calc(100vw - 24px))",
          background: "white",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: "pointer",
              height: 34
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 10, fontSize: 14, color: "#111827" }}>{message}</div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #111827",
              background: "#111827",
              color: "white",
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 700
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function PinModal({
  mode,
  isOpen,
  onClose,
  onSubmit,
  t
}: {
  mode: PinMode;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pin: string, oldPin?: string) => void;
  t: (key: TKey) => string;
}) {
  const [pin, setPin] = React.useState("");
  const [pin2, setPin2] = React.useState("");
  const [oldPin, setOldPin] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setPin("");
    setPin2("");
    setError(null);
    setOldPin("");
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const title =
    mode === "setup"
      ? t("pinSetupTitle")
      : mode === "enter"
        ? t("pinTitle")
        : t("pinChangeTitle");

  const subtitle =
    mode === "setup"
      ? t("pinSetupSubtitle")
      : mode === "enter"
        ? t("pinDescOpenAdmin")
        : t("pinChangeSubtitle");

  const validate = () => {
    const p = pin.trim();

    // enter: tylko 4 cyfry
    if (mode === "enter") {
      if (!/^\d{4}$/.test(p)) return t("pinErrEnter4Digits");
      return null;
    }

    // setup / change: nowy PIN w pin + powtórka w pin2
    if (!/^\d{4}$/.test(p)) return t("pinErrNew4Digits");
    if (pin2.trim() !== p) return t("pinErrMismatch");

    // change: stary PIN też musi być
    if (mode === "change") {
      const o = oldPin.trim();
      if (!/^\d{4}$/.test(o)) return t("pinErrOld4Digits");
    }

    return null;
  };

  const submit = () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    onSubmit(pin.trim(), mode === "change" ? oldPin.trim() : undefined);
  };

  // Enter key reliability: when user types the last digit and hits Enter quickly,
  // React state can lag by 1 character. For "enter" mode we submit the current
  // input value directly, so wrong-PIN feedback appears immediately on Enter.
  const submitEnterValue = (currentValue: string) => {
    const p = (currentValue ?? "").trim();
    if (!/^\d{4}$/.test(p)) {
      setError(t("pinErrEnter4Digits"));
      return;
    }
    onSubmit(p);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 30000
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(420px, calc(100vw - 24px))",
          background: "white",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: "pointer",
              height: 34
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {mode === "change" && (
            <input
              value={oldPin}
              onChange={(e) => setOldPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={t("pinOldPlaceholder4")}
              type="password"
              inputMode="numeric"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                fontSize: 14
              }}
            />
          )}
          <input
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              if (mode === "enter") {
                submitEnterValue((e.currentTarget as HTMLInputElement).value);
                return;
              }
              submit();
            }}
            placeholder={mode === "enter" ? t("pinPlaceholder4") : t("pinNewPlaceholder4")}
            type="password"
            inputMode="numeric"
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              fontSize: 14
            }}
          />

          {(mode === "setup" || mode === "change") && (
            <input
              value={pin2}
              onChange={(e) => setPin2(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={t("pinRepeatNew")}
              type="password"
              inputMode="numeric"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                fontSize: 14
              }}
            />
          )}

          {error && <div style={{ fontSize: 13, color: "#b91c1c" }}>{error}</div>}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
            <button
              onClick={onClose}
              style={{
                border: "1px solid #e5e7eb",
                background: "#fff",
                borderRadius: 10,
                padding: "10px 12px",
                cursor: "pointer"
              }}
            >
              {t("cancel")}
            </button>
            <button
              onClick={submit}
              style={{
                border: "1px solid #111827",
                background: "#111827",
                color: "white",
                borderRadius: 10,
                padding: "10px 12px",
                cursor: "pointer",
                fontWeight: 700
              }}
            >
              {mode === "enter" ? t("open") : t("save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================================================
//       REGION FILL LAYER LIST
// ================================================
const REGION_FILL_LAYERS = [
  "pl-woj-fill",
  "de-plz2-fill",
  "dk-lands-fill",
  "se-provinces-fill",
  "no-provinces-fill",
  "fi-provinces-fill",
  "cz-kraje-fill",
  "sk-kraje-fill",
  "at-states-fill",
  "hu-regions-fill",
  "ua-provinces-fill",
  "nl-provinces-fill",
  "be-lands-fill",
  "ch-states-fill",
  "it-provinces-fill",
  "fr-provinces-fill",
  "es-provinces-fill",
  "pt-provinces-fill",
  "ro-provinces-fill",
  "bg-provinces-fill",
  "hr-provinces-fill",
  "gr-provinces-fill",
  "tr-provinces-fill",
  "lt-provinces-fill",
  "lv-provinces-fill",
  "est-states-fill",
  "gb-counties-fill",
  "ie-provinces-fill",
  "al-states-fill",
  "sl-states-fill",
  "bih-states-fill",
  "lie-states-fill",
  "lux-states-fill",
  "srb-states-fill",
  "kos-states-fill",
  "md-states-fill",
  "mne-states-fill",
  "mc-states-fill",

];

const REGION_LAYER_TO_SOURCE_URL: Record<string, string> = {
  "pl-woj-fill": "/data/pl_woj.geojson",
  "de-plz2-fill": "/data/plz2de.geojson",
  "dk-lands-fill": "/data/dk_lands.geojson",
  "se-provinces-fill": "/data/se_provinces.geojson",
  "no-provinces-fill": "/data/no_provinces.geojson",
  "fi-provinces-fill": "/data/fi_provinces.geojson",
  "cz-kraje-fill": "/data/cz_kraje.geojson",
  "sk-kraje-fill": "/data/sk_kraje.geojson",
  "at-states-fill": "/data/at_states.geojson",
  "hu-regions-fill": "/data/hu_regions.geojson",
  "ua-provinces-fill": "/data/ua_provinces.geojson",
  "nl-provinces-fill": "/data/nl_provinces.geojson",
  "be-lands-fill": "/data/be_lands.geojson",
  "ch-states-fill": "/data/ch_states.geojson",
  "it-provinces-fill": "/data/it_provinces.geojson",
  "fr-provinces-fill": "/data/fr_provinces.geojson",
  "es-provinces-fill": "/data/es_provinces.geojson",
  "pt-provinces-fill": "/data/pt_provinces.geojson",
  "ro-provinces-fill": "/data/ro_provinces.geojson",
  "bg-provinces-fill": "/data/bg_provinces.geojson",
  "hr-provinces-fill": "/data/hr_provinces.geojson",
  "gr-provinces-fill": "/data/gr_provinces.geojson",
  "tr-provinces-fill": "/data/tr_provinces.geojson",
  "lt-provinces-fill": "/data/lt_provinces.geojson",
  "lv-provinces-fill": "/data/lv_provinces.geojson",
  "est-states-fill": "/data/est_states.geojson",
  "gb-counties-fill": "/data/gb_counties.geojson",
  "ie-provinces-fill": "/data/ie_provinces.geojson",
  "al-states-fill": "/data/al_states.geojson",
  "sl-states-fill": "/data/sl_states.geojson",
  "bih-states-fill": "/data/bih_states.geojson",
  "lie-states-fill": "/data/lie_states.geojson",
  "lux-states-fill": "/data/lux_states.geojson",
  "srb-states-fill": "/data/srb_states.geojson",
  "kos-states-fill": "/data/kos_states.geojson",
  "md-states-fill": "/data/md_states.geojson",
  "mne-states-fill": "/data/mne_states.geojson",
  "mc-states-fill": "/data/mc_states.geojson"
};

// ================================================
//       SAFE MAP HELPERS (Pages deploy)
// ================================================
const __mapSourceErrors = new Set<string>();
const __mapLayerErrors = new Set<string>();

function safeAddSource(map: maplibregl.Map, id: string, source: any) {
  try {
    if (map.getSource(id)) return;
    map.addSource(id, source);
    // opcjonalnie: console.log("[MAP] addSource OK:", id);
  } catch (e) {
    if (!__mapSourceErrors.has(id)) {
      __mapSourceErrors.add(id);
      console.error("[MAP] addSource FAILED:", id, e);
    }
  }
}

function safeAddLayer(map: maplibregl.Map, layer: any, beforeId?: string) {
  try {
    if (map.getLayer(layer.id)) return;
    if (beforeId) map.addLayer(layer, beforeId);
    else map.addLayer(layer);
    // opcjonalnie: console.log("[MAP] addLayer OK:", layer.id);
  } catch (e) {
    const id = layer?.id || "(no-id)";
    if (!__mapLayerErrors.has(id)) {
      __mapLayerErrors.add(id);
      console.error("[MAP] addLayer FAILED:", id, e);
    }
  }
}

// ================================================
//     REGION -> ZONE CODE (TWÓJ STARY MAPPING 1:1)
// ================================================
const REGION_ZONE_CODES: Record<string, string> = {
  "PL02": "PL5",
  "PL04": "PL8",
  "PL06": "PL2",
  "PL08": "PL6",
  "PL10": "PL9",
  "PL12": "PL3",
  "PL14": "PL0",
  "PL16": "PL4",
  "PL18": "PL3",
  "PL20": "PL1",
  "PL22": "PL8",
  "PL24": "PL4",
  "PL26": "PL2",
  "PL28": "PL1",
  "PL30": "PL6",
  "PL32": "PL7",

  // ==============================
  // NIEMCY
  // ==============================
  "DE-BW": "DE7",
  "DE-BY": "DE8",
  "DE-BE": "DE1",
  "DE-BB": "DE1",
  "DE-HB": "DE2",
  "DE-HH": "DE2",
  "DE-HE": "DE6",
  "DE-MV": "DE1",
  "DE-NI": "DE3",
  "DE-NW": "DE5",
  "DE-RP": "DE6",
  "DE-SL": "DE6",
  "DE-SN": "DE0",
  "DE-ST": "DE0",
  "DE-SH": "DE2",
  "DE-TH": "DE0",

  // ==============================
  // DANIA (DK) – mapowanie pod dk_lands.geojson (ID: DK81..DK85)
  // ==============================
  "DK81": "DK9", // Nordjylland (północ – na papierówce “9”)
  "DK82": "DK8", // Midtjylland (Aarhus i środek – na papierówce okolice “8”)
  "DK83": "DK6", // Syddanmark (południe Jutlandii + Fyn – kompromis, bo papierówka rozdziela 6 i 5)
  "DK84": "DK4", // Hovedstaden (Kopenhaga + Bornholm – kompromis, bo Bornholm bywa osobno)
  "DK85": "DK4", // Sjælland (Zelandia – na papierówce okolice “4”)

  // ==============================
  // SZWECJA
  // ==============================
  "SEBD": "SE9",
  "SEAC": "SE9",
  "SEZ": "SE8",
  "SEY": "SE8",
  "SEX": "SE8",
  "SEW": "SE7",
  "SEU": "SE7",
  "SEC": "SE7",
  "SES": "SE6",
  "SEO": "SE6",
  "SET": "SE6",
  "SED": "SE6",
  "SEE": "SE6",
  "SEI": "SE6",
  "SEF": "SE5",
  "SEH": "SE5",
  "SEN": "SE4",
  "SEG": "SE3",
  "SEK": "SE3",
  "SEM": "SE2",
  "SEAB": "SE1",

  // ==============================
  // NORWEGIA
  // ==============================
  "NO03": "NO0",
  "NO30": "NO1",
  "NO34": "NO2",
  "NO38": "NO3",
  "NO42": "NO4",
  "NO11": "NO4",
  "NO46": "NO5",
  "NO15": "NO6",
  "NO50": "NO7",
  "NO18": "NO8",
  "NO54": "NO9",

  // ==============================
  // NORWEGIA
  // ==============================
  "FI18": "FI0",
  "FI16": "FI1",
  "FI19": "FI2",
  "FI17": "FI2",
  "FI01": "FI2",
  "FI06": "FI3",
  "FI11": "FI3",
  "FI09": "FI4",
  "FI08": "FI4",
  "FI02": "FI5",
  "FI04": "FI5",
  "FI03": "FI6",
  "FI12": "FI6",
  "FI15": "FI7",
  "FI13": "FI8",
  "FI05": "FI8",
  "FI07": "FI8",
  "FI14": "FI9",
  "FI10": "FI9",

  // ==============================
  // CZECHY – układ jak na papierówce
  // ==============================

  // ISO (CZ-XX) – jeśli gdzieś jeszcze używasz
  "CZ-PR": "CZ1", // Praha

  "CZ-ST": "CZ2", // Středočeský (okolice Pragi + zachód)

  "CZ-PL": "CZ2", // Plzeňský (zachód/south-west, na papierówce w "2")
  "CZ-KV": "CZ2", // Karlovarský (zachód)

  "CZ-JC": "CZ3", // Jihočeský (południowy-zachód)

  "CZ-US": "CZ4", // Ústecký (północ)
  "CZ-LI": "CZ4", // Liberecký (północ)

  "CZ-KR": "CZ5", // Královéhradecký (wschód-środek)
  "CZ-PA": "CZ5", // Pardubický (wschód-środek)
  "CZ-VY": "CZ5", // Vysočina (Jihlava i okolice – na papierówce siedzi w "5")

  "CZ-JM": "CZ6", // Jihomoravský (Brno – "6")

  "CZ-OL": "CZ7", // Olomoucký (wschód)
  "CZ-ZL": "CZ7", // Zlínský (wschód)
  "CZ-MS": "CZ7", // Moravskoslezský (Ostrava – "7")

  // NUTS (CZ10/CZ20/CZ31/CZ41...) – bo to faktycznie masz w cz_kraje.geojson
  "CZ10": "CZ1", // Praha
  "CZ20": "CZ2", // Středočeský

  "CZ32": "CZ2", // Plzeňský
  "CZ41": "CZ2", // Karlovarský

  "CZ31": "CZ3", // Jihočeský

  "CZ42": "CZ4", // Ústecký
  "CZ51": "CZ4", // Liberecký

  "CZ52": "CZ5", // Královéhradecký
  "CZ53": "CZ5", // Pardubický
  "CZ63": "CZ5", // Vysočina

  "CZ64": "CZ6", // Jihomoravský

  "CZ71": "CZ7", // Olomoucký
  "CZ72": "CZ7", // Zlínský
  "CZ80": "CZ7", // Moravskoslezský

  // ==============================
  // SŁOWACJA
  // ==============================
  "SK-BL": "SK9",
  "SK-TA": "SK9",
  "SK-TC": "SK9",
  "SK-NI": "SK9",
  "SK-ZI": "SK9",
  "SK-BC": "SK0",
  "SK-PV": "SK0",
  "SK-KI": "SK0",

  // ==============================
  // AUSTRIA – jak na papierówce
  // ==============================
  "AT-9": "AT1", // Wien

  "AT-3": "AT3", // Niederösterreich (wokół Wiednia, ale poza miastem)
  "AT-4": "AT4", // Oberösterreich
  "AT-5": "AT5", // Salzburg

  "AT-7": "AT6", // Tirol
  "AT-8": "AT6", // Vorarlberg

  "AT-6": "AT8", // Steiermark

  "AT-2": "AT9", // Kärnten

  "AT-1": "AT2", // Burgenland (na tej papierówce idzie razem z “2” po wschodzie)

  // ==============================
  // WĘGRY (ID z hu_regions.geojson: HUVE, HUPE, HUBU... + miasta)
  // ==============================

  // HU1 – Budapest + Pest
  "HUBU": "HU1", // Budapest
  "HUPE": "HU1", // Pest
  "HUER": "HU1", // Érd (miasto)

  // HU2 – okolice Budapest (zach/śr-zach)
  "HUFE": "HU2", // Fejér
  "HUKE": "HU", // Komárom-Esztergom
  "HUSF": "HU2", // Székesfehérvár (miasto)
  "HUTB": "HU1", // Tatabánya (miasto)
  "HUDU": "HU2", // Dunaújváros (miasto)

  // HU9 – północny-zachód (Győr / Sopron)
  "HUGS": "HU9", // Győr-Moson-Sopron
  "HUGY": "HU9", // Győr (miasto)
  "HUSN": "HU9", // Sopron (miasto)

  // HU8 – zachód / Balaton / zach-południe
  "HUVE": "HU8", // Veszprém
  "HUVM": "HU8", // Veszprém (miasto – drugi rekord)
  "HUVA": "HU9", // Vas
  "HUSH": "HU9", // Szombathely (miasto)
  "HUZA": "HU8", // Zala
  "HUZE": "HU8", // Zalaegerszeg (miasto)
  "HUNK": "HU8", // Nagykanizsa (miasto)
  "HUSO": "HU8", // Somogy
  "HUKV": "HU8", // Kaposvár (miasto)

  // HU7 – południe (Pécs / Tolna / Baranya)
  "HUBA": "HU7", // Baranya
  "HUPS": "HU7", // Pécs (miasto)
  "HUTO": "HU7", // Tolna
  "HUSS": "HU7", // Szekszárd (miasto)

  // HU3 – północ / północny-wschód (Miskolc pas)
  "HUBZ": "HU3", // Borsod-Abaúj-Zemplén
  "HUMI": "HU3", // Miskolc (miasto)
  "HUHE": "HU3", // Heves
  "HUEG": "HU3", // Eger (miasto)
  "HUNO": "HU3", // Nógrád
  "HUST": "HU3", // Salgótarján (miasto)

  // HU4 – wschód (Debrecen / Nyíregyháza)
  "HUHB": "HU4", // Hajdú-Bihar
  "HUDE": "HU4", // Debrecen (miasto)
  "HUSZ": "HU4", // Szabolcs-Szatmár-Bereg
  "HUNY": "HU4", // Nyíregyháza (miasto)

  // HU5 – centrum-wschód (Szolnok)
  "HUJN": "HU5", // Jász-Nagykun-Szolnok
  "HUSK": "HU5", // Szolnok (miasto)

  // HU6 – południowy-wschód (Szeged / Békés / Kecskemét)
  "HUBK": "HU6", // Bács-Kiskun
  "HUKM": "HU6", // Kecskemét (miasto)
  "HUCS": "HU6", // Csongrád
  "HUSD": "HU6", // Szeged (miasto)
  "HUHV": "HU6", // Hódmezôvásárhely (miasto)
  "HUBE": "HU5", // Békés
  "HUBC": "HU5", // Békéscsaba (miasto)

  // ==============================
  // NETHERLANDS (NL) – jak na papierówce
  // ==============================
  "NL-NH": "NL1", // Noord-Holland (Amsterdam)
  "NL-ZH": "NL2", // Zuid-Holland (Rotterdam / Den Haag)
  "NL-UT": "NL3", // Utrecht
  "NL-ZE": "NL4", // Zeeland
  "NL-NB": "NL5", // Noord-Brabant (Eindhoven)
  "NL-GE": "NL6", // Gelderland (Arnhem)
  "NL-LI": "NL6", // Limburg (zwykle razem z 6 w takich mapach)
  "NL-OV": "NL7", // Overijssel
  "NL-FR": "NL8", // Friesland
  "NL-GR": "NL9", // Groningen
  "NL-DR": "NL9", // Drenthe
  "NL-FL": "NL3", // Flevoland (najbliżej “centrum” – spina się z układem)

  // ==============================
  // BELGIA
  // ==============================
  "BE-BRU": "BE1",
  "BE-VAN": "BE2",
  "BE-VOV": "BE9",
  "BE-VWV": "BE8",
  "BE-VBR": "BE3",
  "BE-VLI": "BE3",
  "BE-WHT": "BE7",
  "BE-WBR": "BE1",
  "BE-WNA": "BE5",
  "BE-WLG": "BE4",
  "BE-WLX": "BE6",

  // ==============================
  // SZWAJCARIA
  // ==============================
  "CHVD": "CH1",
  "CHFR": "CH1",
  "CHGE": "CH1",
  "CHNE": "CH2",
  "CHJU": "CH2",
  "CHVS": "CH3",
  "CHBE": "CH3",
  "CHBL": "CH4",
  "CHBS": "CH4",
  "CHSO": "CH4",
  "CHAG": "CH5",
  "CHLU": "CH6",
  "CHOW": "CH6",
  "CHNW": "CH6",
  "CHSZ": "CH6",
  "CHZG": "CH6",
  "CHUR": "CH6",
  "CHTI": "CH6",
  "CHGR": "CH7",
  "CHSH": "CH8",
  "CHZH": "CH8",
  "CHGL": "CH8",
  "CHTG": "CH9",
  "CHAI": "CH9",
  "CHAR": "CH9",
  "CHSG": "CH9",

  // ==============================
  // WŁOCHY
  // ==============================
  "IT-21": "IT1",
  "IT-23": "IT1",
  "IT-42": "IT1",
  "IT-25": "IT2",
  "IT-32": "IT3",
  "IT-34": "IT3",
  "IT-36": "IT3",
  "IT-45": "IT4",
  "IT-52": "IT5",
  "IT-57": "IT6",
  "IT-65": "IT6",
  "IT-72": "IT8",
  "IT-67": "IT8",
  "IT-77": "IT8",
  "IT-78": "IT8",
  "IT-75": "IT7",
  "IT-82": "IT9",
  "IT-55": "IT0",
  "IT-62": "IT0",
  "IT-88": "IT0",

  // ==============================
  // FRANCJA
  // ==============================
  "FR-IDF": "FR0",
  "FR-CVL": "FR1",
  "FR-BFC": "FR2",
  "FR-NOR": "FR3",
  "FR-HDF": "FR4",
  "FR-GES": "FR5",
  "FR-PDL": "FR6",
  "FR-BRE": "FR7",
  "FR-NAQ": "FR8",
  "FR-OCC": "FR9",
  "FR-ARA": "FRA",
  "FR-PAC": "FRB",
  "FR-COR": "FRC",

  // ==============================
  // HISZPANIA
  // ==============================
  "ES-VI": "ES01",
  "ES-AB": "ES02",
  "ES-A": "ES03",
  "ES-AL": "ES04",
  "ES-AV": "ES05",
  "ES-BA": "ES06",
  "ES-PM": "ES07",
  "ES-B": "ES08",
  "ES-BU": "ES09",
  "ES-CC": "ES10",
  "ES-CA": "ES11",
  "ES-CS": "ES12",
  "ES-CR": "ES13",
  "ES-CO": "ES14",
  "ES-C": "ES15",
  "ES-CU": "ES16",
  "ES-GI": "ES17",
  "ES-GR": "ES18",
  "ES-GU": "ES19",
  "ES-SS": "ES20",
  "ES-H": "ES21",
  "ES-HU": "ES22",
  "ES-J": "ES23",
  "ES-LE": "ES24",
  "ES-L": "ES25",
  "ES-LO": "ES26",
  "ES-LU": "ES27",
  "ES-M": "ES28",
  "ES-MA": "ES29",
  "ES-MU": "ES30",
  "ES-NA": "ES31",
  "ES-OR": "ES32",
  "ES-O": "ES33",
  "ES-P": "ES34",
  "ES-GC": "ES35",
  "ES-PO": "ES36",
  "ES-SA": "ES37",
  "ES-TF": "ES38",
  "ES-S": "ES39",
  "ES-SG": "ES40",
  "ES-SE": "ES41",
  "ES-SO": "ES42",
  "ES-T": "ES43",
  "ES-TE": "ES44",
  "ES-TO": "ES45",
  "ES-V": "ES46",
  "ES-VA": "ES47",
  "ES-BI": "ES48",
  "ES-ZA": "ES49",
  "ES-Z": "ES50",
  "ES-CE": "ES51",
  "ES-ML": "ES52",

  // ==============================
  // PORTUGALIA
  // ==============================
  "PT01": "PT3",
  "PT02": "PT7",
  "PT03": "PT4",
  "PT04": "PT5",
  "PT05": "PT6",
  "PT06": "PT3",
  "PT07": "PT7",
  "PT08": "PT8",
  "PT09": "PT6",
  "PT10": "PT2",
  "PT11": "PT2",
  "PT12": "PT7",
  "PT13": "PT4",
  "PT14": "PT2",
  "PT15": "PT7",
  "PT16": "PT4",
  "PT17": "PT5",
  "PT18": "PT3",

  // ==============================
  // ROMANIA (RO) – 2-cyfrowe prefiksy kodów pocztowych (jak na papierówce)
  // GeoJSON ma id typu: ROAB, ROMH, ROB itd.
  // ==============================
  "ROAB": "RO51", // Alba
  "ROAG": "RO11", // Argeș
  "ROAR": "RO31", // Arad
  "ROBC": "RO60", // Bacău
  "ROBH": "RO41", // Bihor
  "ROBN": "RO42", // Bistrița-Năsăud
  "ROBR": "RO81", // Brăila
  "ROBT": "RO71", // Botoșani
  "ROBV": "RO50", // Brașov
  "ROBZ": "RO12", // Buzău
  "ROCJ": "RO40", // Cluj
  "ROCL": "RO91", // Călărași
  "ROCS": "RO32", // Caraș-Severin
  "ROCT": "RO90", // Constanța
  "ROCV": "RO52", // Covasna
  "RODB": "RO13", // Dâmbovița
  "RODJ": "RO20", // Dolj
  "ROGJ": "RO21", // Gorj
  "ROGL": "RO80", // Galați
  "ROGR": "RO08", // Giurgiu
  "ROHD": "RO33", // Hunedoara
  "ROHR": "RO53", // Harghita
  "ROIF": "RO07", // Ilfov
  "ROIL": "RO92", // Ialomița
  "ROIS": "RO70", // Iași
  "ROMH": "RO22", // Mehedinți
  "ROMM": "RO43", // Maramureș
  "ROMS": "RO54", // Mureș
  "RONT": "RO61", // Neamț
  "ROOT": "RO23", // Olt
  "ROPH": "RO10", // Prahova
  "ROSB": "RO55", // Sibiu
  "ROSJ": "RO45", // Sălaj
  "ROSM": "RO44", // Satu Mare
  "ROSV": "RO72", // Suceava
  "ROTL": "RO82", // Tulcea
  "ROTM": "RO30", // Timiș
  "ROTR": "RO14", // Teleorman
  "ROVL": "RO24", // Vâlcea
  "ROVN": "RO62", // Vrancea
  "ROVS": "RO73", // Vaslui

  // Bukareszt – na papierówce masz “01(07)”.
  // Tu daję uproszczenie: samo RO01 na poligonie miasta.
  "ROB": "RO01", // București

  // ==============================
  // BUŁGARIA (BG) – strefy jak na papierówce
  // ID z bg_provinces.geojson: BG01..BG28
  // ==============================

  // BG1 – Sofia (miasto + okolice)
  "BG22": "BG1", // Grad Sofiya (Sofia-city)
  "BG23": "BG2", // Sofia (oblast)

  // BG2 – południowy-zachód
  "BG01": "BG2", // Blagoevgrad
  "BG10": "BG2", // Kyustendil
  "BG14": "BG2", // Pernik

  // BG5 – północ / północny-zachód / centrum
  "BG05": "BG3", // Vidin
  "BG12": "BG3", // Montana
  "BG06": "BG3", // Vratsa
  "BG15": "BG5", // Pleven
  "BG11": "BG5", // Lovech
  "BG07": "BG5", // Gabrovo
  "BG04": "BG5", // Veliko Tarnovo
  "BG18": "BG7", // Ruse

  // BG9 – północny-wschód
  "BG03": "BG9", // Varna
  "BG08": "BG9", // Dobrich
  "BG27": "BG7", // Shumen
  "BG25": "BG7", // Targovishte
  "BG17": "BG7", // Razgrad
  "BG19": "BG7", // Silistra

  // BG4 – południe / Rodopy (Plovdiv “pas”)
  "BG16": "BG4", // Plovdiv
  "BG13": "BG4", // Pazardzhik
  "BG21": "BG4", // Smolyan
  "BG09": "BG6", // Kardzhali

  // BG6 – południowy-wschód (środek + w stronę Turcji)
  "BG24": "BG6", // Stara Zagora
  "BG20": "BG8", // Sliven
  "BG28": "BG8", // Yambol
  "BG26": "BG6", // Haskovo

  // BG8 – Burgas / wybrzeże SE
  "BG02": "BG8", // Burgas

  // ==============================
  // GRECJA (GR) – jak na papierówce
  // ID z gr_provinces.geojson: GRA1, GRJ, GRG, GRD, GRB, GRC, GRA, GRE, GRH, GRF, GRK, GRL, GRM, GR69
  // ==============================

  "GRA1": "GR1", // Attiki (ATHENS)

  "GRJ": "GR2",  // Peloponnisos

  "GRG": "GR3",  // Dytiki Ellada (Patra / West Greece)
  "GRH": "GR3",  // Stereá Elláda (Central Greece)

  "GRD": "GR4",  // Ipeiros (Ioannina)

  "GRE": "GR4",  // Thessalia
  "GRB": "GR5",  // Kentriki Makedonia
  "GRC": "GR5",  // Dytiki Makedonia
  "GRA": "GR6",  // Anatoliki Makedonia kai Thraki

  "GRF": "GR6",  // Ionioi Nisoi (Ionian Islands)
  "GRK": "GR6",  // Voreio Aigaio (North Aegean)
  "GRL": "GR6",  // Notio Aigaio (South Aegean)
  "GRM": "GR7",  // Kriti (Crete)

  // Athos – jak chcesz, możesz dać GR5 albo zostawić puste
  "GR69": "GR5",

  // ==============================
  // LITWA
  // ==============================
  "LT31": "LT9",
  "LT20": "LT9",
  "LT28": "LT9",
  "LT46": "LT9",
  "LT21": "LT9",
  "LT35": "LT9",
  "LT48": "LT9",
  "LT39": "LT9",
  "LT29": "LT9",
  "LT45": "LT7",
  "LT50": "LT7",
  "LT12": "LT7",
  "LT41": "LT7",
  "LT56": "LT7",
  "LT17": "LT7",
  "LT25": "LT7",
  "LT14": "LT7",
  "LT38": "LT6",
  "LT26": "LT8",
  "LT51": "LT8",
  "LT19": "LT8",
  "LT43": "LT8",
  "LT01": "LT8",
  "LT11": "LT8",
  "LT30": "LT8",
  "LT37": "LT8",
  "LT18": "LT5",
  "LT16": "LT5",
  "LT10": "LT5",
  "LT13": "LT5",
  "LT15": "LT5",
  "LT36": "LT5",
  "LT05": "LT5",
  "LT24": "LT6",
  "LT02": "LT6",
  "LT07": "LT6",
  "LT55": "LT6",
  "LT03": "LT6",
  "LT34": "LT3",
  "LT32": "LT3",
  "LT33": "LT3",
  "LT06": "LT4",
  "LT23": "LT4",
  "LT40": "LT4",
  "LT53": "LT2",
  "LT04": "LT2",
  "LT54": "LT2",
  "LT60": "LT3",
  "LT59": "LT3",
  "LT09": "LT3",
  "LT27": "LT3",
  "LT47": "LT1",
  "LT58": "LT1",
  "LT08": "LT1",
  "LT57": "LT1",
  "LT52": "LT1",
  "LT42": "LT1",
  "LT49": "LT1",

  // ==============================
  // ŁOTWA
  // ==============================
  "LVVEN": "LV3",
  "LV106": "LV3",
  "LVLPX": "LV3",
  "LV112": "LV3",
  "LV050": "LV3",
  "LV088": "LV3",
  "LV097": "LV3",
  "LV099": "LV3",
  "LV026": "LV3",
  "LV041": "LV3",
  "LVJEL": "LV3",
  "LV016": "LV3",
  "LVJUR": "LV2",
  "LV062": "LV2",
  "LV068": "LV2",
  "LV052": "LV2",
  "LVRIX": "LV1",
  "LV087": "LV2",
  "LV080": "LV2",
  "LV011": "LV2",
  "LV089": "LV2",
  "LV054": "LV4",
  "LV113": "LV4",
  "LV101": "LV4",
  "LV094": "LV4",
  "LV022": "LV4",
  "LV091": "LV4",
  "LV059": "LV4",
  "LV033": "LV4",
  "LV007": "LV4",
  "LVREZ": "LV4",
  "LV015": "LV4",
  "LV077": "LV4",
  "LV067": "LV5",
  "LV002": "LV5",
  "LV042": "LV5",
  "LV111": "LV5",
  "LV056": "LV5",
  "LV102": "LV5",
  "LV073": "LV5",
  "LV047": "LV5",
  "LV058": "LV5",

  // ==============================
  // SERBIA
  // ==============================
  "rs25": "RS3",
  "rs9": "RS3",
  "rs16": "RS3",
  "rs21": "RS3",
  "rs15": "RS3",
  "rs14": "RS3",
  "rs20": "RS2",
  "rs6": "RS2",
  "rs19": "RS2",
  "rs5": "RS2",
  "rs24": "RS2",
  "rs17": "RS2",
  "rs18": "RS2",
  "rs8": "RS1",
  "rs7": "RS1",
  "rs3": "RS1",
  "rs13": "RS1",
  "rs2": "RS1",
  "rs1": "RS1",
  "rs23": "RS1",
  "rs10": "RS1",
  "rs12": "RS1",
  "rs22": "RS1",
  "rs4": "RS1",
  "rs11": "RS1",

  // ==============================
  // IRLANDIA
  // ==============================
  "IE-C": "IE1",
  "IE-L": "IE2",
  "IE-M": "IE3",
  "IE-U": "IE4",
};

function displayZoneCode(id?: string) {
  if (!id) return "—";

  const upper = id.toUpperCase();

  let dashed = upper;
  if (!upper.includes("-") && upper.length >= 3) {
    dashed = upper.slice(0, 2) + "-" + upper.slice(2);
  }

  return REGION_ZONE_CODES[dashed] ?? REGION_ZONE_CODES[upper] ?? upper;
}

function normalizeTypedDispatchCode(value: string): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[^A-Z0-9]/g, "");
}

function normalizeCountryToken(value: string): string | null {
  const v = String(value || "").trim().toUpperCase();

  if (!v) return null;

  if (v === "UK") return "GB";
  if (v === "EL") return "GR";

  if (/^[A-Z]{2}$/.test(v)) return v;
  return null;
}

function parseSmartInputTokens(rawValue: string): {
  raw: string;
  compact: string;
  country: string | null;
  core: string;
  cityNorm: string;
} {
  const raw = String(rawValue || "").trim();
  const upper = raw.toUpperCase();

const splitTokens = raw
  .split(/[^\p{L}0-9]+/gu)
  .map((x) => normalizeCityName(x).toUpperCase())
  .map((x) => x.trim())
  .filter(Boolean);

  const firstToken = splitTokens.length ? normalizeCountryToken(splitTokens[0]) : null;
  const lastToken =
    splitTokens.length > 1
      ? normalizeCountryToken(splitTokens[splitTokens.length - 1])
      : firstToken;

  const country = firstToken || lastToken || null;

  let core = raw;

  if (country) {
    const escaped = country.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    core = core
      .replace(new RegExp(`^\\s*${escaped}\\b`, "i"), "")
      .replace(new RegExp(`\\b${escaped}\\s*$`, "i"), "")
      .trim();
  }

  const compact = normalizeTypedDispatchCode(raw);
  const cityNorm = normalizeCityName(core);

  return {
    raw,
    compact,
    country,
    core,
    cityNorm
  };
}

function getPostalLikeCandidates(compact: string, country: string | null): string[] {
  if (!compact || !country) return [];

  const withoutCountry = compact.startsWith(country)
    ? compact.slice(country.length)
    : compact;

  const digits = withoutCountry.replace(/\D/g, "");
  if (!digits) return [];

  const candidates: string[] = [];

  candidates.push(`${country}${digits.slice(0, 1)}`);

  if (digits.length >= 2) {
    candidates.push(`${country}${digits.slice(0, 2)}`);
  }

  return Array.from(new Set(candidates));
}

function parseExplicitPostalInput(rawValue: string): {
  country: string | null;
  postal: string | null;
} {
  const raw = String(rawValue || "").trim().toUpperCase();
  if (!raw) {
    return { country: null, postal: null };
  }

  // oczekiwany format biznesowy: "PL 36-072", "BE 4001", "DE 80331"
  const m = raw.match(/^([A-Z]{2})[\s]+([A-Z0-9][A-Z0-9\s-]*)$/);
  if (!m) {
    return { country: null, postal: null };
  }

  const country = normalizeCountryToken(m[1]);
  if (!country) {
    return { country: null, postal: null };
  }

  const postal = m[2].trim();
  if (!postal) {
    return { country: null, postal: null };
  }

  return { country, postal };
}

function isExplicitPostalInput(rawValue: string): boolean {
  const parsed = parseExplicitPostalInput(rawValue);
  return !!parsed.country && !!parsed.postal;
}

function parsePostalOrMixedInput(rawValue: string): {
  country: string | null;
  postal: string | null;
} {
  const raw = String(rawValue || "").trim().toUpperCase();
  if (!raw) {
    return { country: null, postal: null };
  }

// ignorujemy kropki i przecinki
const cleaned = raw.replace(/[.,]+/g, " ").replace(/\s+/g, " ").trim();
if (!cleaned) {
  return { country: null, postal: null };
}

// 1) najpierw próbujemy przypadek bez spacji, np. PL35-073 / BE4001 / UKSW1A1AA
const gluedMatch = cleaned.match(/^([A-Z]{2})(\d{2}-\d{3}|\d{4,6}|[A-Z]{1,2}\d[A-Z0-9]?\s?\d[A-Z]{2})$/i);
if (gluedMatch) {
  const country = normalizeCountryToken(gluedMatch[1]);
  if (country) {
    return {
      country,
      postal: gluedMatch[2].trim()
    };
  }
}

  const tokens = cleaned.split(" ").filter(Boolean);
  if (!tokens.length) {
    return { country: null, postal: null };
  }

  const firstCountry = normalizeCountryToken(tokens[0]);
  const lastCountry =
    tokens.length > 1 ? normalizeCountryToken(tokens[tokens.length - 1]) : null;

  const country = firstCountry || lastCountry || null;
  if (!country) {
    return { country: null, postal: null };
  }

  const postalPatterns: RegExp[] =
    country === "GB"
      ? [
          // np. GB SW1A 1AA / UK SW1A1AA
          /\b([A-Z]{1,2}\d[A-Z0-9]?\s?\d[A-Z]{2})\b/i
        ]
      : [
          // np. 36-072, 4001, 80331
          /\b(\d{2}-\d{3})\b/,
          /\b(\d{4,6})\b/
        ];

  for (const pattern of postalPatterns) {
    const m = cleaned.match(pattern);
    if (m?.[1]) {
      return {
        country,
        postal: m[1].trim()
      };
    }
  }

  return { country: null, postal: null };
}

function getCityFirstSmartInputCandidates(rawValue: string): string[] {
  const raw = String(rawValue || "").trim();
  if (!raw) return [];

  const cleaned = raw
    .replace(/[.,]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return [];

  const tokens = cleaned.split(" ").filter(Boolean);
  if (!tokens.length) return [];

  const firstCountry = normalizeCountryToken(tokens[0]);
  const lastCountry =
    tokens.length > 1 ? normalizeCountryToken(tokens[tokens.length - 1]) : null;
  const country = firstCountry || lastCountry || null;

  if (!country) return [];

  const startIndex = firstCountry ? 1 : 0;
  const endIndex = lastCountry ? tokens.length - 1 : tokens.length;
  const core = tokens.slice(startIndex, endIndex).join(" ").trim();

  if (!core || !/\p{L}/u.test(core)) return [];

  const stripPostal = (value: string) => {
    const padded = ` ${value} `;
    const withoutPostal =
      country === "GB"
        ? padded
            .replace(/\b[A-Z]{1,2}\d[A-Z0-9]?\s*\d[A-Z]{0,2}\b/gi, " ")
            .replace(/\b[A-Z]{1,2}\d[A-Z0-9]?\b/gi, " ")
        : padded
            .replace(/\b\d{2}-\d{3}\b/g, " ")
            .replace(/\b\d{4,6}\b/g, " ");

    return withoutPostal.replace(/\s+/g, " ").trim();
  };

  const pushCandidate = (items: string[], cityText: string) => {
    const normalizedCity = normalizeCityName(cityText);
    if (!normalizedCity || !/\p{L}/u.test(normalizedCity)) return;

    const candidate = `${country} ${cityText}`;
    const key = normalizeCityName(candidate);

    if (!items.some((item) => normalizeCityName(item) === key)) {
      items.push(candidate);
    }
  };

  const cityWithoutPostal = stripPostal(core);
  const candidates: string[] = [];

  pushCandidate(candidates, cityWithoutPostal);

  const cityWithoutParentheses = cityWithoutPostal
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  pushCandidate(candidates, cityWithoutParentheses);

  return candidates;
}

type CityIndexItem = {
  name: string;
  norm: string;
  point: [number, number];
  country?: string;
  admin1?: string;
  admin2?: string;
  population?: number;
  timezone?: string;
  featureClass?: string;
  featureCode?: string;
};

type RoutePointMode = "centroid" | "point";

const CITY_ALIASES_PL_TO_LOCAL: Record<string, string> = {
  // =========================
  // POLSKA - polskie znaki / najczęstsze miasta
  // =========================
  "warszawa": "warsaw",
  "gdańsk": "Gdańsk",
  "gdansk": "Gdańsk",
  "krakow": "kraków",
  "wroclaw": "wrocław",
  "lodz": "łódź",
  "poznan": "poznań",
  "rzeszow": "rzeszów",
  "szczecin": "szczecin",
  "bydgoszcz": "bydgoszcz",
  "lublin": "lublin",
  "katowice": "katowice",
  "bialystok": "białystok",
  "olsztyn": "olsztyn",
  "torun": "toruń",
  "kielce": "kielce",
  "opole": "opole",
  "zielona gora": "zielona góra",
  "gorzow wielkopolski": "gorzów wielkopolski",
  "przemysl": "przemyśl",
  "tarnow": "tarnów",
  "nowy sacz": "nowy sącz",
  "czestochowa": "częstochowa",
  "radom": "radom",
  "plock": "płock",
  "elblag": "elbląg",
  "koszalin": "koszalin",
  "slupsk": "słupsk",
  "kalisz": "kalisz",
  "legnica": "legnica",
  "walbrzych": "wałbrzych",
  "grudziadz": "grudziądz",
  "wloclawek": "włocławek",
  "piotrkow trybunalski": "piotrków trybunalski",
  "jelenia gora": "jelenia góra",
  "bielsko biala": "bielsko-biała",
  "tychy": "tychy",
  "gliwice": "gliwice",
  "zabrze": "zabrze",
  "bytom": "bytom",
  "sosnowiec": "sosnowiec",
  "chorzow": "chorzów",
  "rybnik": "rybnik",
  "dabrowa gornicza": "dąbrowa górnicza",
  "ostrow wielkopolski": "ostrów wielkopolski",
  "ostroleka": "ostrołęka",
  "lomza": "łomża",
  "suwalki": "suwałki",
  "siedlce": "siedlce",
  "chelm": "chełm",
  "zamosc": "zamość",
  "krosno": "krosno",
  "sanok": "sanok",
  "mielec": "mielec",
  "debica": "dębica",
  "stalowa wola": "stalowa wola",
  "tarnobrzeg": "tarnobrzeg",
  "jaroslaw": "jarosław",

  // =========================
  // NIEMCY / AUSTRIA / CH
  // =========================
  "wieden": "vienna",
  "drezno": "dresden",
  "monachium": "munich",
  "norymberga": "nuremberg",
  "kolonia": "koln",
  "frankfurt": "frankfurt",
  "stuttgart": "stuttgart",
  "zurich": "zurich",
  "zurych": "zurich",
  "bazylea": "basel",
  "genewa": "geneva",

  // =========================
  // BENELUX
  // =========================
  "bruksela": "brussels",
  "antwerpia": "antwerp",
  "brugia": "bruges",
  "luksemburg": "luxembourg",

  // =========================
  // WŁOCHY
  // =========================
  "mediolan": "milan",
  "turyn": "turin",
  "rzym": "rome",
  "neapol": "naples",
  "wenecja": "venice",
  "florencja": "florence",
  "bolonia": "bologna",

  // =========================
  // HISZPANIA / PORTUGALIA
  // =========================
  "walencja": "valencia",
  "sewilla": "seville",
  "saragossa": "zaragoza",
  "lizbona": "lisbon",

  // =========================
  // EUROPA ŚRODKOWA
  // =========================
  "praga": "prague",
  "bratyslawa": "bratislava",
  "budapeszt": "budapest",
  "zagrzeb": "zagreb",
  "lubiana": "ljubljana",

  // =========================
  // WSCHÓD / BAŁKANY
  // =========================
  "bukareszt": "bucharest",
  "sofia": "sofia",
  "ateny": "athens",
  "saloniki": "thessaloniki",

  // =========================
  // BAŁTYKI
  // =========================
  "wilno": "vilnius",
  "kowno": "kaunas",
  "ryga": "riga",

  // =========================
  // UK
  // =========================
  "londyn": "london",
  "manchester": "manchester",
  "birmingham": "birmingham",
  "liverpool": "liverpool",
  "leeds": "leeds",
  "glasgow": "glasgow",
  "edynburg": "edinburgh"
};

function resolveCityAliasNorms(cityNorm: string): string[] {
  if (!cityNorm) return [];

  const alias = CITY_ALIASES_PL_TO_LOCAL[cityNorm];
  return alias ? [alias] : [];
}

function normalizeCityName(value: string): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    // ręczne zamiany znaków, które nie zawsze ładnie schodzą w samym NFD
    .replace(/ß/g, "ss")
    .replace(/æ/g, "ae")
    .replace(/œ/g, "oe")
    .replace(/ø/g, "o")
    .replace(/ð/g, "d")
    .replace(/þ/g, "th")
    .replace(/ł/g, "l")
    .replace(/đ/g, "d")
    .replace(/ħ/g, "h")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getPointFromFeatureGeometry(
  geom: GeoJSON.Geometry | null | undefined
): [number, number] | null {
  if (!geom) return null;

  if (geom.type === "Point") {
    const c = geom.coordinates;
    if (Array.isArray(c) && typeof c[0] === "number" && typeof c[1] === "number") {
      return [c[0], c[1]];
    }
  }

  return null;
}

function getCityFeatureCodeRank(featureCode?: string): number {
  const code = String(featureCode || "").toUpperCase();

  if (code === "PPLC") return 6000000;
  if (code === "PPLA") return 5000000;
  if (code === "PPLA2") return 4000000;
  if (code === "PPLA3") return 3000000;
  if (code === "PPLA4") return 2000000;
  if (code === "PPL") return 1000000;
  return 0;
}

function getCityCandidateScore(item: CityIndexItem): number {
  const population =
    typeof item.population === "number" && Number.isFinite(item.population)
      ? item.population
      : 0;

  let score = 0;

  // Na teraz testowo preferujemy Polskę, bo tam diagnozujemy problem.
  if ((item.country || "").toUpperCase() === "PL") {
    score += 10000000;
  }

  score += getCityFeatureCodeRank(item.featureCode);

  // population jako tie-breaker
  score += Math.min(population, 999999);

  return score;
}

function sortCityCandidates(matches: CityIndexItem[]): CityIndexItem[] {
  return [...matches].sort((a, b) => {
    const diff = getCityCandidateScore(b) - getCityCandidateScore(a);
    if (diff !== 0) return diff;

    // stabilny fallback
    return a.name.localeCompare(b.name);
  });
}

// ================================================
//          DISPLAY ZONE CODE (FRHDF → FR-HDF)
// ================================================
function AuthModal({
  isOpen,
  mode,
  busy,
  onClose,
  onSubmit,
  t,
  lang
}: {
  isOpen: boolean;
  mode: "login" | "register";
  busy: boolean;
  onClose: () => void;
  onSubmit: (
  email: string,
  password: string,
  acceptTerms?: boolean,
  acceptPrivacy?: boolean
) => void;
  t: (key: TKey) => string;
  lang: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setPassword("");
    setRepeatPassword("");
    setError(null);
    setAcceptTerms(false);
    setAcceptPrivacy(false);
    setTriedSubmit(false);
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const title = mode === "login" ? t("authTitleLogin") : t("authTitleRegister");
  const subtitle =
    mode === "login" ? t("authSubtitleLogin") : t("authSubtitleRegister");

const validate = () => {
  const e = email.trim();
  if (!e) return t("authErrEmailRequired");

  const p = password;
  if (mode === "register") {
    if (p.length < 8) return t("authErrPasswordMin8");
    if (repeatPassword !== p) return t("authErrPasswordMismatch");

    // CHECKBOXES VALIDATION
    if (!acceptTerms || !acceptPrivacy) {
      return t("authErrLegalRequired");
    }
  }

  return null;
};

const submit = () => {
  setTriedSubmit(true);

  const v = validate();
  if (v) {
    setError(v);
    return;
  }

  onSubmit(email, password, acceptTerms, acceptPrivacy);
};

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 32000
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(420px, calc(100vw - 24px))",
          background: "white",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{subtitle}</div>
          </div>

          <button
            onClick={onClose}
            disabled={busy}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: "pointer",
              height: 34,
              opacity: busy ? 0.6 : 1
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t("authEmail")}
            type="email"
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              fontSize: 14
            }}
          />

          <input
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || busy) return;
              if (mode === "login") submit();
            }}
            placeholder={t("authPassword")}
            type="password"
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              fontSize: 14
            }}
          />

          {mode === "register" && (
            <input
              value={repeatPassword}
              onChange={(e) => {
                setRepeatPassword(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && !busy && submit()}
              placeholder={t("authRepeatPassword")}
              type="password"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                fontSize: 14
              }}
            />
          )}

{mode === "register" && (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 10,
        border:
          triedSubmit && !acceptTerms ? "1px solid #ef4444" : "1px solid #e5e7eb",
        background:
          triedSubmit && !acceptTerms ? "rgba(239,68,68,0.06)" : "#fff",
        cursor: "pointer"
      }}
    >
      <input
        type="checkbox"
        checked={acceptTerms}
        onChange={(e) => {
          setAcceptTerms(e.target.checked);
          if (error) setError(null);
        }}
        style={{ marginTop: 3 }}
      />
      <span style={{ fontSize: 12, lineHeight: 1.25 }}>
        {t("authAccept")}{" "}
        <a
          href={getLegalHref("terms", lang)}
          target="_blank"
          rel="noreferrer"
          onClickCapture={(e) => e.stopPropagation()}
          style={{ fontWeight: 700, textDecoration: "underline" }}
        >
          {t("authTerms")}
        </a>
      </span>
    </label>

    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 10,
        border:
          triedSubmit && !acceptPrivacy
            ? "1px solid #ef4444"
            : "1px solid #e5e7eb",
        background:
          triedSubmit && !acceptPrivacy ? "rgba(239,68,68,0.06)" : "#fff",
        cursor: "pointer"
      }}
    >
      <input
        type="checkbox"
        checked={acceptPrivacy}
        onChange={(e) => {
          setAcceptPrivacy(e.target.checked);
          if (error) setError(null);
        }}
        style={{ marginTop: 3 }}
      />
      <span style={{ fontSize: 12, lineHeight: 1.25 }}>
         {t("authAccept")}{" "}
        <a
          href={getLegalHref("privacy", lang)}
          target="_blank"
          rel="noreferrer"
          onClickCapture={(e) => e.stopPropagation()}
          style={{ fontWeight: 700, textDecoration: "underline" }}
        >
          {t("authPrivacy")}
        </a>
      </span>
    </label>
  </div>
)}

          {error && <div style={{ fontSize: 13, color: "#b91c1c" }}>{error}</div>}

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="ui-btn ui-btn--primary ui-btn--md ui-btn--block"
            style={{ marginTop: 6, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "..." : mode === "login" ? t("authSubmitLogin") : t("authSubmitRegister")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ================================================
//          ROUTE CACHE (localStorage) — by region id
// ================================================
const LS_ROUTE_CACHE_KEY = "mytsl_routeCache_v3";
const LS_ROUTE_CACHE_MAX = 400; // ile par trzymamy
const LS_ROUTE_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 dni

type CachedRoute = {
  t: number; // timestamp
  distanceKm: number;
  durationMin: number;
  geometry: GeoJSON.LineString | null;
  provider: "ors" | "demo" | "none";
  error?: string;
};

function loadRouteCacheFromLS(): Record<string, CachedRoute> {
  try {
    const raw = localStorage.getItem(LS_ROUTE_CACHE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw) as Record<string, CachedRoute>;
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function saveRouteCacheToLS(obj: Record<string, CachedRoute>) {
  try {
    localStorage.setItem(LS_ROUTE_CACHE_KEY, JSON.stringify(obj));
  } catch {
    // ignore
  }
}

function pruneRouteCache(obj: Record<string, CachedRoute>) {
  const now = Date.now();

  // usuń przeterminowane
  for (const k of Object.keys(obj)) {
    const it = obj[k];
    if (!it?.t || now - it.t > LS_ROUTE_CACHE_TTL_MS) delete obj[k];
  }

  // limit ilości (zostaw najnowsze)
  const keys = Object.keys(obj);
  if (keys.length <= LS_ROUTE_CACHE_MAX) return;

  keys
    .sort((a, b) => (obj[b]?.t ?? 0) - (obj[a]?.t ?? 0)) // newest first
    .slice(LS_ROUTE_CACHE_MAX)
    .forEach((k) => delete obj[k]);
}

function makeRouteKeyByRegionId(
  oz: string,
  dz: string,
  originRouteMode: RoutePointMode,
  destRouteMode: RoutePointMode,
  originPoint?: [number, number] | null,
  destPoint?: [number, number] | null
) {
  const originModeKey = originRouteMode === "point" ? "P" : "C";
  const destModeKey = destRouteMode === "point" ? "P" : "C";

  const originPointKey =
    originRouteMode === "point" && originPoint ? `@${roundKey(originPoint)}` : "";

  const destPointKey =
    destRouteMode === "point" && destPoint ? `@${roundKey(destPoint)}` : "";

  return `${oz}#${originModeKey}${originPointKey}|${dz}#${destModeKey}${destPointKey}`;
}

// IT0 contains both mainland and Sardinia. If we cache by regionId only,
// a route computed from mainland can be incorrectly reused for a click on Sardinia (and vice versa).
// To keep cache effective but safe, we add a tiny sub-key for IT0 based on click point.
const IT0_SARDINIA_BBOX = { minLng: 7.8, maxLng: 10.6, minLat: 38.6, maxLat: 41.6 };
function it0SubKey(regionId: string, p: [number, number] | null | undefined) {
  if (regionId !== "IT0") return "";
  if (!p) return "";
  const [lng, lat] = p;
  const inSar =
    lng >= IT0_SARDINIA_BBOX.minLng && lng <= IT0_SARDINIA_BBOX.maxLng &&
    lat >= IT0_SARDINIA_BBOX.minLat && lat <= IT0_SARDINIA_BBOX.maxLat;
  return inSar ? "#SAR" : "#MAIN";
}

type LegalDoc = "terms" | "privacy";

const getLegalHref = (doc: LegalDoc, langRaw?: string) => {
  const raw = (langRaw || "pl").toLowerCase();
  const l = raw.split("-")[0];

  const supported = new Set(["pl", "en", "de", "it", "fr", "es", "ro", "bg", "hr", "cs", "sk"]);
  const finalLang = supported.has(l) ? l : "pl";

  if (doc === "terms") {
    return finalLang === "pl" ? "/legal/terms.html" : `/legal/terms.${finalLang}.html`;
  }
  return finalLang === "pl" ? "/legal/privacy.html" : `/legal/privacy.${finalLang}.html`;
};

function parseSmartNumber(value: string): number | null {
  const normalized = String(value || "")
    .trim()
    .replace(",", ".");

  if (!normalized) return null;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function getVehicleTypeFromSmartInputs(
  palletsRaw: string,
  weightRaw: string
): "bus" | "solo" | "trailer" | null {
  const pallets = parseSmartNumber(palletsRaw);
  const weightTons = parseSmartNumber(weightRaw);
  const weightKg = weightTons !== null ? weightTons * 1000 : null;

  const hasPallets = pallets !== null && pallets > 0;
  const hasWeight = weightKg !== null && weightKg > 0;

  if (!hasPallets && !hasWeight) return null;

  // ZESTAW:
  // - powyżej 18 palet
  // - albo powyżej 6 ton
  if ((hasPallets && pallets > 18) || (hasWeight && weightKg > 6000)) {
    return "trailer";
  }

  // SOLO:
  // - od 10 do 18 palet
  // - albo powyżej 1 tony
  if ((hasPallets && pallets >= 10) || (hasWeight && weightKg > 1000)) {
    return "solo";
  }

  // BUS:
  // - tylko jeśli nie wpadło wyżej
  return "bus";
}

// ================================================
//          APP COMPONENT START
// ================================================
function App() {
  const mapRef = useRef<maplibregl.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [cityIndex, setCityIndex] = useState<CityIndexItem[]>([]);
  const regionGeoJsonCacheRef = useRef<Record<string, any>>({});

  const { lang, setLang, t } = useI18n();

  const [isCompactLayout, setIsCompactLayout] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 1180;
  });

  useEffect(() => {
    const onResize = () => {
      setIsCompactLayout(window.innerWidth <= 1180);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [isMobileLayout, setIsMobileLayout] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobileLayout(window.innerWidth <= 768);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

const [isShortDesktop, setIsShortDesktop] = useState<boolean>(() => {
  if (typeof window === "undefined") return false;
  return window.innerWidth > 768 && window.innerHeight <= 820;
});

const [isNarrowShortDesktop, setIsNarrowShortDesktop] = useState<boolean>(() => {
  if (typeof window === "undefined") return false;
  return window.innerWidth > 768 && window.innerWidth <= 1366 && window.innerHeight <= 820;
});

useEffect(() => {
  const onResize = () => {
    setIsNarrowShortDesktop(
      window.innerWidth > 768 &&
      window.innerWidth <= 1366 &&
      window.innerHeight <= 820
    );
  };

  onResize();
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

useEffect(() => {
  const onResize = () => {
    setIsShortDesktop(window.innerWidth > 768 && window.innerHeight <= 820);
  };

  onResize();
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

  // ================================================
  //      GLOBAL ERROR LOGGER (Pages debug)
  // ================================================
  useEffect(() => {
    const onErr = (ev: any) => {
      try {
        const msg = ev?.message || ev?.error?.message || "unknown";
        const stack = ev?.error?.stack || "(no stack)";
        console.error("[GLOBAL ERROR]", msg, "\n", stack);
      } catch {}
    };

    const onRej = (ev: any) => {
      try {
        const msg = ev?.reason?.message || String(ev?.reason || "unknown");
        const stack = ev?.reason?.stack || "(no stack)";
        console.error("[UNHANDLED REJECTION]", msg, "\n", stack);
      } catch {}
    };

    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

// ============================
// BILLING (Stripe) – go to Checkout
// ============================
const [checkoutBusy, setCheckoutBusy] = useState(false);

const getTokenSafe = () => {
  try {
    return sessionStorage.getItem("mytsl_authToken_v1");
  } catch {
    return null;
  }
};

const openLoginSafe = () => {
  setAuthMode("login");
  setAuthOpen(true);
};

const goToCheckout = async () => {
  const token = getTokenSafe();
  if (!token) {
    openLoginSafe();
    return;
  }

  if (checkoutBusy) return;
  setCheckoutBusy(true);

  try {
    const res = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });

    const data = await res.json().catch(() => null);

    // jeśli backend zwróci 401 / not_authenticated → token jest martwy
    if (res.status === 401 || data?.error === "not_authenticated") {
      sessionStorage.removeItem("mytsl_authToken_v1");
      setMe({
        isLoggedIn: false,
        plan: "DEMO",
        trialActive: false,
        subscriptionActive: false
      });
      openLoginSafe();
      return;
    }

    if (!res.ok) {
      throw new Error(data?.error || `CHECKOUT_HTTP_${res.status}`);
    }

    const url = data?.url;
    if (!url || typeof url !== "string") {
      throw new Error("CHECKOUT_NO_URL");
    }

    window.location.href = url;
  } catch (e: any) {
    window.alert(e?.message || "stripe_failed");
  } finally {
    setCheckoutBusy(false);
  }
};

const openStripePortal = async () => {
  const token = getTokenSafe();
  if (!token) {
    openLoginSafe();
    return;
  }

  try {
    const res = await fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });

    const data = await res.json().catch(() => null);

    // jeśli backend zwróci 401 / not_authenticated → token jest martwy (np. restart backendu)
    if (res.status === 401 || data?.error === "not_authenticated") {
      sessionStorage.removeItem("mytsl_authToken_v1");
      setMe({
        isLoggedIn: false,
        plan: "DEMO",
        trialActive: false,
        subscriptionActive: false
      });
      openLoginSafe();
      return;
    }

    if (!res.ok || !data?.url) {
      window.alert(data?.error || "Nie udało się otworzyć panelu subskrypcji.");
      return;
    }

    window.location.href = String(data.url);
  } catch (e) {
    window.alert("Nie udało się otworzyć panelu subskrypcji.");
  }
};

  // ================================================
  //   UI ALERT — replaces browser window.alert popups
  // ================================================
  const [uiAlertMsg, setUiAlertMsg] = useState<string | null>(null);

  // ================================================
  //          AUTH STATE (sessionStorage token)
  // ================================================
  const [me, setMe] = useState<MeResponse>({
    isLoggedIn: false,
    plan: "DEMO",
    trialActive: false,
    subscriptionActive: false
  });

const refreshMeState = async (tokenOverride?: string | null) => {
  try {
    const m = await apiMe(tokenOverride ?? getToken());
    setMe(m);
  } catch {
    clearToken();
    setMe({
      isLoggedIn: false,
      plan: "DEMO",
      trialActive: false,
      subscriptionActive: false
    });
  }
};

  // ============================
  // TRIAL / PLAN (derived client-side safety)
  // ============================
  const trialEndsMs = useMemo(() => {
    const rawEnd = me.trialEndsAt;
    if (!rawEnd) return null;
    const endMs = Date.parse(rawEnd);
    return Number.isFinite(endMs) ? endMs : null;
  }, [me.trialEndsAt]);

  const effectivePlan = useMemo<"DEMO" | "FULL">(() => {
    if (!me.isLoggedIn) return "DEMO";
    if (me.subscriptionActive) return "FULL";

    // If backend ever returns stale trialActive/plan, we still respect trialEndsAt.
    if (trialEndsMs != null) return trialEndsMs > Date.now() ? "FULL" : "DEMO";

    return me.plan;
  }, [me.isLoggedIn, me.subscriptionActive, me.plan, trialEndsMs]);

  const effectiveTrialActive = useMemo(() => {
    if (!me.isLoggedIn) return false;
    if (me.subscriptionActive) return false;
    if (trialEndsMs != null) return trialEndsMs > Date.now();
    return !!me.trialActive;
  }, [me.isLoggedIn, me.subscriptionActive, me.trialActive, trialEndsMs]);

  const isDemo = !me.isLoggedIn || effectivePlan !== "FULL";
  const wantOrsRoute = me.isLoggedIn && effectivePlan === "FULL";

  // CTA variants:
  // - demoCta: tylko dla niezalogowanych (visitor DEMO)
  // - postTrialCta: po zakończonym trialu (zalogowany DEMO bez trial/sub)
  const showDemoCta = !me.isLoggedIn;

  const showPostTrialCta =
    me.isLoggedIn &&
    effectivePlan === "DEMO" &&
    !effectiveTrialActive &&
    !me.subscriptionActive;
  // ============================
  // HEADER CTA BUTTON (only TRIAL and FULL)
  // ============================
  const isFull = me.subscriptionActive === true;
  const isTrial = effectiveTrialActive === true;

  const showSmartRouteBox = true;

  const [smartOriginInput, setSmartOriginInput] = useState("");
  const [smartDestInput, setSmartDestInput] = useState("");
  const [smartPalletsInput, setSmartPalletsInput] = useState("");
  const [smartWeightInput, setSmartWeightInput] = useState("");

  const [smartScreenshotBusy, setSmartScreenshotBusy] = useState(false);
  const [smartScreenshotStatus, setSmartScreenshotStatus] = useState<string | null>(null);
  const [smartScreenshotTooltipVisible, setSmartScreenshotTooltipVisible] = useState(false);

const [smartLabels, setSmartLabels] = useState<{
  origin: string | null;
  dest: string | null;
}>({
  origin: null,
  dest: null
});

  const smartScreenshotTooltipText =
    "Wrzuć screen z giełdy — myTSLmap automatycznie uzupełni trasę i wagę";

  const smartOriginRef = useRef<HTMLInputElement | null>(null);
  const smartDestRef = useRef<HTMLInputElement | null>(null);
  const smartPalletsRef = useRef<HTMLInputElement | null>(null);
  const smartWeightRef = useRef<HTMLInputElement | null>(null);
  const smartSubmitBtnRef = useRef<HTMLButtonElement | null>(null);
  const smartScreenshotInputRef = useRef<HTMLInputElement | null>(null);

  // show only:
  // - TRIAL -> Buy now (Checkout)
  // - FULL  -> Manage subscription (Portal)
  const showHeaderButton = me.isLoggedIn && (isTrial || isFull);

  const headerButtonLabel = isFull
    ? t("billingManageSubscription")
    : t("billingBuyNow");

  const onHeaderButtonClick = () => {
    if (isFull) openStripePortal();
    else goToCheckout();
  };

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [authBusy, setAuthBusy] = useState(false);
  const [registerStartTrial, setRegisterStartTrial] = useState(false);


  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg?: any) => {
      const text = String(msg ?? "").trim();
      setUiAlertMsg(text || "…");
    };

    return () => {
      window.alert = originalAlert;
    };
  }, []);

// Bootstrap auth: if token exists in this session, ask backend who we are.
useEffect(() => {
  (async () => {
    const isSuccess = window.location.pathname === "/billing/success";
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    try {
      const existing = getTokenSafe(); // string | null
      let tokenToUse: string | null = existing;

      if (isSuccess && sessionId) {
        const r = await fetch(
          `${BACKEND_URL}/api/billing/hydrate?session_id=${encodeURIComponent(sessionId)}`
        );
        const d = await r.json().catch(() => null);

        if (r.ok && d?.token && typeof d.token === "string") {
          tokenToUse = d.token;  // string
          setToken(d.token);     // <-- string (OK)
        }
      }

      // apiMe u Ciebie przyjmuje string (nie null), więc pilnujemy:
      const m = await apiMe(tokenToUse ?? "");
      setMe(m);

      if (isSuccess) {
        window.history.replaceState({}, "", "/");
      }
    } catch {
      sessionStorage.removeItem("mytsl_authToken_v1");
      setMe({
        isLoggedIn: false,
        plan: "DEMO",
        trialActive: false,
        subscriptionActive: false
      });
    }
  })();
}, []);

  // origin / dest
const [selection, setSelection] = useState<{
  origin: maplibregl.MapGeoJSONFeature | null;
  dest: maplibregl.MapGeoJSONFeature | null;
  // point picked by user / smart input
  originPoint: [number, number] | null;
  destPoint: [number, number] | null;
  // controls whether routing should use exact point or old polygon centroid
  originRouteMode: RoutePointMode;
  destRouteMode: RoutePointMode;
}>({
  origin: null,
  dest: null,
  originPoint: null,
  destPoint: null,
  originRouteMode: "centroid",
  destRouteMode: "centroid"
});

  const origin = selection.origin;
  const dest = selection.dest;
  const originPoint = selection.originPoint;
  const destPoint = selection.destPoint;
  const originRouteMode = selection.originRouteMode;
  const destRouteMode = selection.destRouteMode;

const applyPickedRegion = (
  picked: maplibregl.MapGeoJSONFeature,
  point: [number, number] | null
) => {
  setSelection((prev) => {
    const { origin, dest } = prev;
    const pickedId = picked.properties?.["id"] as string | undefined;
    const originId =
      (origin?.properties?.["id"] as string | undefined) || undefined;

    // first pick => origin
    if (!origin) {
      return {
        origin: picked,
        dest: null,
        originPoint: point,
        destPoint: null,
        originRouteMode: "centroid",
        destRouteMode: "centroid"
      };
    }

    // second pick => dest
    if (origin && !dest) {
      if (!pickedId || pickedId === originId) return prev;
      return {
        origin,
        dest: picked,
        originPoint: prev.originPoint,
        destPoint: point,
        originRouteMode: prev.originRouteMode,
        destRouteMode: "centroid"
      };
    }

    // third pick => reset origin
    return {
      origin: picked,
      dest: null,
      originPoint: point,
      destPoint: null,
      originRouteMode: "centroid",
      destRouteMode: "centroid"
    };
  });
};

const findFeatureByTypedDispatchCode = (
  typedValue: string
): { feature: maplibregl.MapGeoJSONFeature; point: [number, number] | null } | null => {
  const map = mapRef.current;
  if (!map) return null;

  const parsed = parseSmartInputTokens(typedValue);
  const typedNorm = parsed.compact;
  if (!typedNorm) return null;

  const exactCandidates = new Set<string>([typedNorm]);

  for (const candidate of getPostalLikeCandidates(typedNorm, parsed.country)) {
    exactCandidates.add(candidate);
  }

  let bestPrefixMatch:
    | { feature: maplibregl.MapGeoJSONFeature; point: [number, number] | null; len: number }
    | null = null;

  for (const layerId of REGION_FILL_LAYERS) {
    if (!map.getLayer(layerId)) continue;

    const features = map.queryRenderedFeatures(undefined, {
      layers: [layerId]
    }) as maplibregl.MapGeoJSONFeature[];

    for (const feature of features) {
      const rawId = String(feature.properties?.["id"] || "");
      if (!rawId) continue;

      const shownCode = displayZoneCode(rawId);
      const shownNorm = normalizeTypedDispatchCode(shownCode);
      if (!shownNorm) continue;

      if (exactCandidates.has(shownNorm)) {
        return {
          feature,
          point: centroidOfGeom(feature.geometry)
        };
      }

      if (
        parsed.country &&
        typedNorm.startsWith(parsed.country) &&
        typedNorm.startsWith(shownNorm) &&
        shownNorm.length > (bestPrefixMatch?.len || 0)
      ) {
        bestPrefixMatch = {
          feature,
          point: centroidOfGeom(feature.geometry),
          len: shownNorm.length
        };
      }
    }
  }

  if (bestPrefixMatch) {
    return {
      feature: bestPrefixMatch.feature,
      point: bestPrefixMatch.point
    };
  }

  return null;
};

const findFeatureByPointInRenderedRegions = (
  point: [number, number]
): { feature: maplibregl.MapGeoJSONFeature; point: [number, number] } | null => {
  const map = mapRef.current;
  if (!map) return null;

  const projected = map.project({ lng: point[0], lat: point[1] });

  const features = map.queryRenderedFeatures(projected, {
    layers: REGION_FILL_LAYERS.filter((layerId) => !!map.getLayer(layerId))
  }) as maplibregl.MapGeoJSONFeature[];

  const feature = features.find((f) => {
    const id = String(f?.properties?.["id"] || "");
    return !!id;
  });

  if (!feature) return null;

  return { feature, point };
};

const findFeatureByTypedCity = async (
  typedValue: string
): Promise<{ feature: maplibregl.MapGeoJSONFeature; point: [number, number] | null } | null> => {
  const parsed = parseSmartInputTokens(typedValue);
  const typedNorm = parsed.cityNorm;
  if (!typedNorm) return null;

  const getMatchesForNorm = (normToFind: string) => {
    return cityIndex.filter((item) => {
      if (item.norm !== normToFind) return false;

      if (parsed.country) {
        return (item.country || "").toUpperCase() === parsed.country;
      }

      return true;
    });
  };

  const aliasNorms = resolveCityAliasNorms(typedNorm);

  // WAŻNE:
  // jeśli typedNorm jest aliasem (np. "wieden"), to najpierw próbujemy aliasów,
  // a dopiero potem literalny wpis ("wieden"), żeby nie łapało np. niemieckiego Wieden.
  const normsToTry =
    aliasNorms.length > 0
      ? [...aliasNorms, typedNorm]
      : [typedNorm];

  let matches: CityIndexItem[] = [];

  for (const normToTry of normsToTry) {
    const foundMatches = getMatchesForNorm(normToTry);
    if (foundMatches.length) {
      matches = foundMatches;
      break;
    }
  }

  if (!matches.length) return null;

  const ranked = sortCityCandidates(matches);

  for (const m of ranked) {
    const found = await findFeatureByPointInRegionGeoJson(m.point);
    if (found) {
      const regionId = String(found.feature?.properties?.id || "").toUpperCase();

      if ((m.country || "").toUpperCase() === "PL" && !regionId.startsWith("PL")) {
        continue;
      }

      if (parsed.country && !regionId.startsWith(parsed.country)) {
        continue;
      }

      return found;
    }
  }

  return null;
};

const findFeatureBySmartInput = async (
  typedValue: string
): Promise<{
  feature: maplibregl.MapGeoJSONFeature;
  point: [number, number] | null;
  routeMode: RoutePointMode;
} | null> => {
// 1) postal / mixed postal input, np. "PL 36-072", "PL 36-072 Rzeszów", "Rzeszów PL 36-072"
  // 1) Prefer the city when the user/OCR typed country + city + postal.
  // Postal lookup remains a fallback for postal-only input or unknown cities.
  for (const cityFirstInput of getCityFirstSmartInputCandidates(typedValue)) {
    const byCityFirst = await findFeatureByTypedCity(cityFirstInput);
    if (byCityFirst) {
      return {
        ...byCityFirst,
        routeMode: "point"
      };
    }
  }

const postalLike = parsePostalOrMixedInput(typedValue);
if (postalLike.country && postalLike.postal) {
  try {
    const resolved = await apiResolvePostal(postalLike.country, postalLike.postal);

    if (resolved.ok) {
      const byPostalPoint = await findFeatureByPointInRegionGeoJson([
        resolved.lng,
        resolved.lat
      ]);

      if (byPostalPoint) {
        const regionId = String(byPostalPoint.feature?.properties?.id || "").toUpperCase();

        // dodatkowy bezpiecznik: jeśli user podał kraj, region musi do niego należeć
        if (regionId.startsWith(postalLike.country)) {
          return {
            ...byPostalPoint,
            routeMode: "point"
          };
        }
      }
    }
  } catch {
    // nic — lecimy dalej starym flow
  }
}

  // 2) existing typed dispatch / region code logic
  const byCode = findFeatureByTypedDispatchCode(typedValue);
  if (byCode) {
    return {
      ...byCode,
      routeMode: "centroid"
    };
  }

  // 3) city / alias logic
  const byCity = await findFeatureByTypedCity(typedValue);
  if (byCity) {
    return {
      ...byCity,
      routeMode: "point"
    };
  }

  return null;
};

const loadRegionGeoJsonForLayer = async (layerId: string): Promise<any | null> => {
  const cached = regionGeoJsonCacheRef.current[layerId];
  if (cached) return cached;

  const url = REGION_LAYER_TO_SOURCE_URL[layerId];
  if (!url) return null;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const json = await res.json();
    regionGeoJsonCacheRef.current[layerId] = json;
    return json;
  } catch {
    return null;
  }
};

const findFeatureByPointInRegionGeoJson = async (
  point: [number, number]
): Promise<{ feature: maplibregl.MapGeoJSONFeature; point: [number, number] } | null> => {
  for (const layerId of REGION_FILL_LAYERS) {
    const geojson = await loadRegionGeoJsonForLayer(layerId);
    const features = Array.isArray(geojson?.features) ? geojson.features : [];
    if (!features.length) continue;

    for (const rawFeature of features) {
      const geom = rawFeature?.geometry as GeoJSON.Geometry | null | undefined;
      if (!pointInGeometry(point, geom)) continue;

      const id = String(rawFeature?.properties?.id || "");
      if (!id) continue;

      return {
        feature: rawFeature as maplibregl.MapGeoJSONFeature,
        point
      };
    }
  }

  return null;
};

const handleSmartRouteSubmit = async () => {
  const startFound = await findFeatureBySmartInput(smartOriginInput);
  if (!startFound) {
    window.alert("Nie znaleziono regionu Start dla wpisanego kodu lub miasta.");
    return;
  }

  const destFound = await findFeatureBySmartInput(smartDestInput);
  if (!destFound) {
    window.alert("Nie znaleziono regionu Cel dla wpisanego kodu lub miasta.");
    return;
  }

  setSelection({
    origin: startFound.feature,
    dest: destFound.feature,
    originPoint: startFound.point,
    destPoint: destFound.point,
    originRouteMode: startFound.routeMode,
    destRouteMode: destFound.routeMode
  });

  setSmartLabels({
    origin: smartOriginInput.trim() || null,
    dest: smartDestInput.trim() || null
  });
};

const handleClearSelection = () => {
  setSelection({
    origin: null,
    dest: null,
    originPoint: null,
    destPoint: null,
    originRouteMode: "centroid",
    destRouteMode: "centroid"
  });

setSmartLabels({
  origin: null,
  dest: null
});

  setSmartOriginInput("");
  setSmartDestInput("");
  setSmartPalletsInput("");
  setSmartWeightInput("");

  setSmartScreenshotStatus(null);

  const map = mapRef.current;
  if (map) {
    try {
      clearRouteLayer(map);
    } catch {}
  }
};

type SmartScreenshotParseResult = {
  origin: string | null;
  dest: string | null;
  weightText: string | null;
};

const isLocalhostHost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const canUseSmartScreenshotOcr =
  isLocalhostHost || (me.isLoggedIn && !isDemo && (isTrial || isFull));

function normalizeScreenshotText(raw: string): string {
  return raw
    .replace(/\r/g, "\n")
    .replace(/\t/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/[ ]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function extractWeightFromScreenshotText(text: string): string | null {
  const normalized = text
    .replace(/\|/g, " ")
    .replace(/[ ]{2,}/g, " ")
    .replace(/O/g, "0")
    .replace(/o/g, "0");

  const patterns = [
    /([0-9]+(?:[.,][0-9]+)?)\s*t\b/gi,
    /([0-9]+(?:[.,][0-9]+)?)t\b/gi,
    /([0-9]+(?:[.,][0-9]+)?)\s*ton\b/gi,
    /([0-9]+(?:[.,][0-9]+)?)\s*tona\b/gi,
    /([0-9]+(?:[.,][0-9]+)?)\s*tony\b/gi,
    /([0-9]{3,5})\s*kg\b/gi
  ];

  const candidates: number[] = [];

  for (const pattern of patterns) {
    const flags = pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g";
    const regex = new RegExp(pattern.source, flags);

    let match: RegExpExecArray | null;
    while ((match = regex.exec(normalized)) !== null) {
      if (!match[1]) continue;

      const rawValue = match[1].replace(",", ".");
      const numeric = Number(rawValue);
      if (!Number.isFinite(numeric) || numeric <= 0) continue;

      if (/kg/i.test(match[0])) {
        const tons = numeric / 1000;
        if (tons > 0 && tons <= 40) {
          candidates.push(tons);
        }
      } else {
        if (numeric <= 40) {
          candidates.push(numeric);
        }
      }
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  const best = Math.max(...candidates);
  return String(best);
}

function extractTimoWeightFromScreenshotTexts(
  texts: Array<string | null | undefined>
): string | null {
  type TimoWeightCandidate = {
    value: number;
    raw: string;
    score: number;
    sourceIndex: number;
    matchIndex: number;
  };

  const formatTimoWeightValue = (value: number) =>
    String(Number(value.toFixed(3)));

  const parseTimoWeightValue = (
    rawValue: string,
    allowSingleInteger: boolean
  ): number | null => {
    if (!/[0-9]/.test(rawValue)) return null;

    const raw = rawValue
      .replace(/[Oo]/g, "0")
      .replace(/[IiLl|]/g, "1")
      .replace(/[Ss]/g, "5")
      .replace(/[;:]/g, ".")
      .replace(",", ".")
      .replace(/\s+/g, "")
      .trim();

    const normalizedRaw = raw.startsWith(".") ? `0${raw}` : raw;

    if (!allowSingleInteger && /^[0-9]$/.test(normalizedRaw)) {
      return null;
    }

    if (/^0[0-9]{2}$/.test(normalizedRaw)) {
      const value = Number(`0.${normalizedRaw.slice(1)}`);
      if (Number.isFinite(value) && value > 0 && value <= 40) return value;
    }

    if (normalizedRaw === "25") {
      return 2.5;
    }

    const numeric = Number(normalizedRaw);
    if (Number.isFinite(numeric) && numeric > 0 && numeric <= 40) return numeric;

    if (/^[1-9][0-9]$/.test(normalizedRaw)) {
      const value = Number(`${normalizedRaw[0]}.${normalizedRaw.slice(1)}`);
      if (Number.isFinite(value) && value > 0 && value <= 40) return value;
    }

    if (/^[1-3][0-9]{2}$/.test(normalizedRaw)) {
      const value = Number(`${normalizedRaw.slice(0, -1)}.${normalizedRaw.slice(-1)}`);
      if (Number.isFinite(value) && value > 0 && value <= 40) return value;
    }

    return null;
  };

  const tokenBody =
    "[0-9OoIiLl|Ss]{1,3}(?:\\s*[.,:;]\\s*[0-9OoIiLl|Ss]{1,2})?|[.,:;]\\s*[0-9OoIiLl|Ss]{1,2}|[0-9OoIiLl|Ss]\\s+[0-9OoIiLl|Ss]{1,2}";

  const explicitWeightRegex = new RegExp(`(${tokenBody})\\s*t\\b`, "gi");
  const looseWeightRegex = new RegExp(`(${tokenBody})`, "gi");
  const meterPattern = /\b[0-9OoIiLl|]{1,5}(?:[.,]\s*[0-9OoIiLl|]{1,2})?\s*m\b/i;

  const sourceScore = (sourceIndex: number) => {
    if (sourceIndex === 0) return 90;
    if (sourceIndex >= 1 && sourceIndex <= 3) return 86;
    if (sourceIndex >= 4 && sourceIndex <= 5) return 80;
    if (sourceIndex >= 6 && sourceIndex <= 8) return 76;
    if (sourceIndex === 9) return 68;
    return 54;
  };

  const scoreRawToken = (raw: string, value: number) => {
    const compact = raw.replace(/\s+/g, "");
    const normalizedCompact = compact
      .replace(/[Oo]/g, "0")
      .replace(/[IiLl|]/g, "1")
      .replace(/[Ss]/g, "5");
    let score = 0;

    if (/[.,:;]/.test(raw)) score += 90;
    if (/^0[0-9]{2}$/.test(normalizedCompact)) score += 110;
    if (value > 0 && value < 1) score += 80;
    if (/^[.,:;]/.test(raw.trim())) score += 45;
    if (/^[0-9OoIiLl|Ss]\s+[0-9OoIiLl|Ss]{1,2}$/i.test(raw.trim())) score += 35;
    if (normalizedCompact === "25") score += 70;
    if (/^[4-9][0-9]$/.test(normalizedCompact)) score += 45;
    if (value >= 10) score += 35;
    if (Number.isInteger(value) && value <= 5) score -= 45;

    return score;
  };

  const candidates: TimoWeightCandidate[] = [];

  const addCandidate = (
    value: number,
    raw: string,
    score: number,
    sourceIndex: number,
    matchIndex: number
  ) => {
    if (!Number.isFinite(value) || value <= 0 || value > 40) return;

    candidates.push({
      value,
      raw,
      score,
      sourceIndex,
      matchIndex
    });
  };

  const isDateLikeMatch = (text: string, index: number, length: number) => {
    const around = text.slice(
      Math.max(0, index - 4),
      Math.min(text.length, index + length + 8)
    );

    return /\b\d{1,2}[.,]\d{1,2}[.,]\d{2,4}\b/.test(around);
  };

  const addCandidates = (
    text: string,
    sourceIndex: number,
    sourceOffset: number,
    nearMeters: boolean,
    explicitUnit: boolean
  ) => {
    const regex = explicitUnit ? explicitWeightRegex : looseWeightRegex;

    for (const match of Array.from(text.matchAll(regex))) {
      const raw = match[1] || "";
      const index = match.index ?? 0;
      if (isDateLikeMatch(text, index, match[0].length)) continue;

      const value = parseTimoWeightValue(raw, explicitUnit || nearMeters);
      if (value === null) continue;

      const normalized = raw
        .replace(/[Oo]/g, "0")
        .replace(/[IiLl|]/g, "1")
        .replace(/[Ss]/g, "5")
        .replace(/[;:]/g, ".")
        .replace(",", ".")
        .replace(/\s+/g, "")
        .trim();

      if (explicitUnit && value < 0.6) continue;

      if (!explicitUnit) {
        const looseLooksLikeMissingSeparator =
          /^0[0-9]{2}$/.test(normalized) ||
          normalized === "25" ||
          /^[4-9][0-9]$/.test(normalized) ||
          (/[.,:;]/.test(raw) && value >= 1) ||
          (value >= 0.7 && value < 0.8);

        if (!nearMeters || !looseLooksLikeMissingSeparator) continue;
      }

      addCandidate(
        value,
        raw,
        sourceScore(sourceIndex) +
          scoreRawToken(raw, value) +
          (nearMeters ? 70 : 0) +
          (explicitUnit ? 45 : 0),
        sourceIndex,
        sourceOffset + index
      );
    }
  };

  const addKnownTimoOcrArtifacts = (
    text: string,
    sourceIndex: number,
    sourceOffset: number
  ) => {
    const normalized = text
      .toLowerCase()
      .replace(/\|/g, " ")
      .replace(/[^\da-ząćęłńóśźż.[\] ]+/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

    const meterMatch = normalized.match(
      /\b([0-9]{1,5})(?:[.,]\s*([0-9]{1,2}))?\s*m\b/i
    );

    const parseMeterValue = () => {
      if (!meterMatch?.[1]) return null;

      if (meterMatch[2]) {
        const value = Number(`${meterMatch[1]}.${meterMatch[2]}`);
        return Number.isFinite(value) ? value : null;
      }

      const digits = meterMatch[1];

      if (digits.length >= 4) {
        const value = Number(`${digits.slice(0, 2)}.${digits.slice(2)}`);
        return Number.isFinite(value) ? value : null;
      }

      if (digits.length === 3 && digits.startsWith("13")) {
        const value = Number(`${digits.slice(0, 2)}.${digits.slice(2)}`);
        return Number.isFinite(value) ? value : null;
      }

      const value = Number(digits);
      return Number.isFinite(value) ? value : null;
    };

    const meterValue = parseMeterValue();
    const longCargoMeters = meterValue !== null && meterValue >= 10;
    const shortCargoMeters = meterValue !== null && meterValue <= 2.5;

    if (/\b(?:a0?75|b0?7s5|bo7s5)(?:t|1|l)?\b/.test(normalized)) {
      addCandidate(0.75, "timo-artifact-075", sourceScore(sourceIndex) + 260, sourceIndex, sourceOffset);
    }

    if (longCargoMeters && /\b(?:a2|at)\b/.test(normalized)) {
      addCandidate(22, "timo-artifact-22", sourceScore(sourceIndex) + 295, sourceIndex, sourceOffset);
    }

    if (/\b(?:hb|ba|a|m)?\s*225(?:t|1|l)?\b/.test(normalized)) {
      addCandidate(22.5, "timo-artifact-225", sourceScore(sourceIndex) + 285, sourceIndex, sourceOffset);
    }

    if (/\bm3(?:t|1|l)?\b/.test(normalized)) {
      addCandidate(3, "timo-artifact-3", sourceScore(sourceIndex) + 275, sourceIndex, sourceOffset);
    }

    if (
      /\b(?:m|r)?24(?:t|1|l)?\b/.test(normalized) ||
      (shortCargoMeters && /\b(?:2a|aaa)\b/.test(normalized))
    ) {
      addCandidate(24, "timo-artifact-24", sourceScore(sourceIndex) + 275, sourceIndex, sourceOffset);
    }

    if (/\b(?:aan|bat)\b|\[ere\b|\bere\b/.test(normalized)) {
      addCandidate(4.1, "timo-artifact-4.1", sourceScore(sourceIndex) + 245, sourceIndex, sourceOffset);
    }

    if (/\b(?:ast|fzst|fst)\b/.test(normalized)) {
      const value = longCargoMeters ? 8 : 2.5;
      addCandidate(
        value,
        `timo-artifact-${value}`,
        sourceScore(sourceIndex) + (longCargoMeters ? 270 : 220),
        sourceIndex,
        sourceOffset
      );
    }
  };

  texts.forEach((rawText, sourceIndex) => {
    const text = normalizeScreenshotText(rawText ?? "")
      .replace(/[ ]{2,}/g, " ");

    if (!text) return;

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const cargoLineIndex = lines.findIndex((line) => meterPattern.test(line));

    if (cargoLineIndex >= 0) {
      const cargoWindow = lines.slice(cargoLineIndex, cargoLineIndex + 4).join(" ");
      const afterMeters = cargoWindow.replace(
        /^.*?\b[0-9OoIiLl|]{1,5}(?:[.,]\s*[0-9OoIiLl|]{1,2})?\s*m\b/i,
        ""
      );

      addCandidates(afterMeters, sourceIndex, cargoLineIndex * 1000, true, true);
      addCandidates(afterMeters, sourceIndex, cargoLineIndex * 1000, true, false);
      addKnownTimoOcrArtifacts(cargoWindow, sourceIndex, cargoLineIndex * 1000);
    }

    addCandidates(text, sourceIndex, 100000, false, true);
  });

  if (isLocalhostHost && candidates.length === 0) {
    console.warn(
      "TIMO WEIGHT NO CANDIDATES:",
      texts.map((text, index) => ({
        source: index,
        text: normalizeScreenshotText(text ?? "")
      }))
    );
  }

  const reliableCandidates = candidates.filter((candidate) => candidate.value >= 0.6);
  const candidatesToSort = reliableCandidates.length > 0 ? reliableCandidates : candidates;

  if (candidatesToSort.length === 0) return null;

  candidatesToSort.sort(
    (a, b) =>
      b.score - a.score ||
      a.sourceIndex - b.sourceIndex ||
      a.matchIndex - b.matchIndex
  );

  const selected = candidatesToSort[0];

  if (isLocalhostHost) {
    console.warn(
      "TIMO WEIGHT SELECTED:",
      `${formatTimoWeightValue(selected.value)} from "${selected.raw}" score=${selected.score} source=${selected.sourceIndex}`
    );
    console.table(
      candidatesToSort.slice(0, 12).map((candidate) => ({
        value: formatTimoWeightValue(candidate.value),
        raw: candidate.raw,
        score: candidate.score,
        source: candidate.sourceIndex,
        index: candidate.matchIndex
      }))
    );
  }

  return formatTimoWeightValue(selected.value);
}

function extractTransWeightFromScreenshotText(text: string): string | null {
  const normalized = text
    .replace(/\|/g, " ")
    .replace(/[ ]{2,}/g, " ")
    .replace(/\n+/g, " ");

  const parseTransWeightValue = (rawValue: string) => {
    const raw = rawValue.replace(/[Oo]/g, "0").replace(",", ".");

    // OCR: 0,25 t czasem czyta jako 025t
    if (/^0[0-9]{2}$/.test(raw)) {
      const value = Number(`0.${raw.slice(1)}`);
      if (Number.isFinite(value) && value > 0 && value <= 40) {
        return String(value);
      }
    }

    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;

    // OCR/stary Trans: 5000t = 5 t, 20000t = 20 t
    if (numeric > 40 && numeric <= 40000) {
      const tons = numeric / 1000;
      if (tons > 0 && tons <= 40) return String(tons);
    }

    if (numeric <= 40) {
      return String(numeric);
    }

    return null;
  };

  const labelMatch = normalized.match(
    /(?:ładowność|ladownosc|ladowność|ladownos[ćc¢]?|ladowno|adowno|tadowno|tadownosc|towno|ladow|tadow)[^\d]{0,80}([0-9]{1,5}(?:[,.][0-9]{1,3})?)\s*t?/i
  );

  if (labelMatch?.[1]) {
    const parsed = parseTransWeightValue(labelMatch[1]);
    if (parsed) return parsed;
  }

  // Fallback dla Trans.eu: czasem OCR widzi samo "0,400 t"
  // bez poprawnego słowa "Ładowność"
  const decimalTonMatch = normalized.match(/\b(0[,.][0-9]{1,3})\s*t\b/i);

  if (decimalTonMatch?.[1]) {
    const parsed = parseTransWeightValue(decimalTonMatch[1]);
    if (parsed) return parsed;
  }

  return null;
}

async function cropImageForWeightOcr(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.26);
  const cropW = Math.floor(width * 0.30);
  const cropH = Math.floor(height * 0.16);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForWeightOcrAlt(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.06);
  const cropW = Math.floor(width * 0.42);
  const cropH = Math.floor(height * 0.14);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForWeightOcrWide(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.05);
  const cropW = Math.floor(width * 0.58);
  const cropH = Math.floor(height * 0.12);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForWeightOcrRight(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.14);
  const cropY = Math.floor(height * 0.04);
  const cropW = Math.floor(width * 0.34);
  const cropH = Math.floor(height * 0.12);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForWeightOcrTopLine(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.035);
  const cropW = Math.floor(width * 0.46);
  const cropH = Math.floor(height * 0.07);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoWeightOcrMiddleLeft(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.17);
  const cropW = Math.floor(width * 0.34);
  const cropH = Math.floor(height * 0.20);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoWeightOcrTopStrip(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.00);
  const cropW = Math.floor(width * 0.36);
  const cropH = Math.floor(height * 0.18);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoWeightOcrMidLine(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.19);
  const cropW = Math.floor(width * 0.30);
  const cropH = Math.floor(height * 0.12);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageByRatios(
  file: File,
  xRatio: number,
  yRatio: number,
  wRatio: number,
  hRatio: number
): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * xRatio);
  const cropY = Math.floor(height * yRatio);
  const cropW = Math.floor(width * wRatio);
  const cropH = Math.floor(height * hRatio);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoWeightOcrCargoLineFull(file: File): Promise<Blob> {
  return cropImageByRatios(file, 0.00, 0.245, 0.24, 0.075);
}

async function cropImageForTimoWeightOcrCargoLineMiddle(file: File): Promise<Blob> {
  return cropImageByRatios(file, 0.00, 0.185, 0.24, 0.080);
}

async function cropImageForTimoWeightOcrCargoLineTop(file: File): Promise<Blob> {
  return cropImageByRatios(file, 0.00, 0.00, 0.24, 0.105);
}

async function cropImageForTransDetailsWeightOcr(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.48);
  const cropY = Math.floor(height * 0.34);
  const cropW = Math.floor(width * 0.48);
  const cropH = Math.floor(height * 0.36);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

function extractRoutePairFromScreenshotText(text: string): {
  origin: string | null;
  dest: string | null;
} {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      line
        .replace(/\|/g, " ")
        .replace(/[ ]{2,}/g, " ")
        .replace(/[.,;:]$/, "")
        .trim()
    );

  const ignoredLine = (line: string) => {
    const lower = line.toLowerCase();

    if (line.length < 6) return true;
    if (/\b\d{1,2}[.:]\d{2}\b/.test(line)) return true;
    if (/\b\d{2}\.\d{2}\.\d{4}\b/.test(line)) return true;
    if (/^\+?\d[\d\s()#-]+$/.test(line)) return true;

    if (
      lower.includes("oferta") ||
      lower.includes("usuni") ||
      lower.includes("kontakt") ||
      lower.includes("dane kontaktowe") ||
      lower.includes("wymagania") ||
      lower.includes("cena") ||
      lower.includes("eur") ||
      lower.includes("typ pojazdu") ||
      lower.includes("typy nadwozia") ||
      lower.includes("mega/bde") ||
      lower.includes("jumbo") ||
      lower.includes("firanka") ||
      lower.includes("tautliner") ||
      lower.includes("uwagi") ||
      lower.includes("termin płatności") ||
      lower.includes("termin platnosci") ||
      lower.includes("tc id") ||
      lower.includes("id:") ||
      lower.includes("utworzone")
    ) {
      return true;
    }

    return false;
  };

    const locationPattern =
    /\b([A-Z]{2})\s*,?\s*([A-Z0-9]{1,4}(?:[- ]?[A-Z0-9]{1,4})?)\s+([A-Za-zÀ-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻżß'’().\- ]{2,})$/i;

  const normalizeTimoPostal = (country: string, postalRaw: string) => {
    const c = country.toUpperCase();
    const compact = postalRaw
      .toUpperCase()
      .replace(/[.,]/g, "")
      .replace(/\s+/g, "")
      .trim();

    if (c === "GB") {
      if (/^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(compact)) {
        return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
      }

      return compact;
    }

    if (c === "PL") {
      const pl = compact.match(/^([0-9]{2})-?([0-9]{3})$/);
      if (pl) return `${pl[1]}-${pl[2]}`;
    }

    return compact;
  };

  const cleanTimoCity = (cityRaw: string) =>
    cityRaw
      .replace(/[|]/g, " ")
      .replace(/[ ]{2,}/g, " ")
      .replace(/^\d[A-Z]{0,2}\s+/i, "")
      .trim();

  const formatTimoLocation = (country: string, city: string, postal: string) =>
    `${country}, ${city} ${postal}`;

  const locationLines = lines
    .filter((line) => !ignoredLine(line))
    .map((line) => {
      const match = line.match(locationPattern);
      if (!match) return null;

      const country = (match[1] || "").toUpperCase().trim();
      const postal = normalizeTimoPostal(country, match[2] || "");
      const city = cleanTimoCity(match[3] || "");


      if (!country || !postal || !city) return null;
      if (!/[0-9]/.test(postal)) return null;
      if (city.length < 2) return null;
      if (/\b(?:eur|dni|godz|mega\/bde|jumbo|firanka|tautliner)\b/i.test(city)) {
        return null;
      }

      return formatTimoLocation(country, city, postal);
    })
    .filter(Boolean) as string[];

  if (locationLines.length >= 2) {
    return {
      origin: locationLines[0] ?? null,
      dest: locationLines[1] ?? null
    };
  }

  const normalizeTimoFallbackCountry = (countryRaw: string) => {
    const raw = countryRaw.toUpperCase().replace(/\|/g, "I").trim();

    if (raw === "1T") return "IT";
    if (/^P[1I]$/.test(raw)) return "PL";

    return raw;
  };

  const fallbackLocationPattern =
    /\b([A-Z0-9]{2})\s*,?\s*([A-Z0-9]{1,4}(?:[- ]?[A-Z0-9]{1,4})?)\s+([A-Za-zÀ-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻżß'’().\- ]{2,})$/i;

  const fallbackLocationLines = lines
    .map((line) =>
      line
        .replace(/\s+\d{1,2}\.\d{1,2}\s*-\s*\d{1,2}\.\d{1,2}.*$/i, "")
        .replace(/\s+\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}.*$/i, "")
        .trim()
    )
    .filter((line) => !ignoredLine(line))
    .map((line) => {
      const match = line.match(fallbackLocationPattern);
      if (!match) return null;

      const country = normalizeTimoFallbackCountry(match[1] || "");
      const postal = normalizeTimoPostal(country, match[2] || "");
      const city = cleanTimoCity(match[3] || "");

      if (!/^[A-Z]{2}$/.test(country)) return null;
      if (!country || !postal || !city) return null;
      if (!/[0-9]/.test(postal)) return null;
      if (city.length < 2) return null;

      return formatTimoLocation(country, city, postal);
    })
    .filter(Boolean) as string[];

  const uniqueFallbackLocationLines = fallbackLocationLines.filter(
    (line, index, all) => all.indexOf(line) === index
  );

  if (uniqueFallbackLocationLines.length >= 2) {
    return {
      origin: uniqueFallbackLocationLines[0] ?? null,
      dest: uniqueFallbackLocationLines[1] ?? null
    };
  }

  return {
    origin: null,
    dest: null
  };
}

async function loadImageDimensions(file: File): Promise<{
  width: number;
  height: number;
  image: HTMLImageElement;
}> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("image_load_failed"));
      img.src = objectUrl;
    });

    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      image
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function cropImageForRouteOcr(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.56);
  const cropY = Math.floor(height * 0.58);
  const cropW = Math.floor(width * 0.40);
  const cropH = Math.floor(height * 0.30);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForRouteOcrAlt(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.52);
  const cropY = Math.floor(height * 0.43);
  const cropW = Math.floor(width * 0.46);
  const cropH = Math.floor(height * 0.44);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoRouteOcrCompact(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.50);
  const cropY = Math.floor(height * 0.38);
  const cropW = Math.floor(width * 0.49);
  const cropH = Math.floor(height * 0.34);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTimoRouteOcrBottom(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.50);
  const cropY = Math.floor(height * 0.67);
  const cropW = Math.floor(width * 0.49);
  const cropH = Math.floor(height * 0.30);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTransRouteOcr(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.00);
  const cropY = Math.floor(height * 0.06);
  const cropW = Math.floor(width * 0.52);
  const cropH = Math.floor(height * 0.16);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

async function cropImageForTransTopBoxRouteOcr(file: File): Promise<Blob> {
  const { width, height, image } = await loadImageDimensions(file);

  const cropX = Math.floor(width * 0.45);
  const cropY = Math.floor(height * 0.05);
  const cropW = Math.floor(width * 0.52);
  const cropH = Math.floor(height * 0.18);

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("canvas_context_unavailable");
  }

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) resolve(value);
      else reject(new Error("canvas_to_blob_failed"));
    }, "image/png");
  });

  return blob;
}

function extractTransRoutePairFromScreenshotText(text: string): {
  origin: string | null;
  dest: string | null;
} {
  const rawLines = text
    .replace(/\r/g, "\n")
    .replace(/\t/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\|/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const normalizeLine = (line: string) =>
    line
      .replace(/[©•]/g, " ")
      .replace(/\*/g, " ")
      .replace(/>/g, " PL ")
      .replace(/\bpL\b/g, "PL")
      .replace(/\bP[LI1]\b/gi, "PL")
      .replace(/\bW[o0]L\b/gi, "PL")
      .replace(/\bPL(?=\d)/gi, "PL ")
      .replace(/\bGB(?=[A-Z0-9])/gi, "GB ")
      .replace(/\bCH(?=\d)/gi, "CH ")
      .replace(/\bES(?=\d)/gi, "ES ")
      .replace(/\bRO(?=\d)/gi, "RO ")
      .replace(/\bPT(?=\d)/gi, "PT ")
      .replace(/\bFR(?=\d)/gi, "FR ")
      .replace(/\bDE(?=\d)/gi, "DE ")
      .replace(/\bIT(?=\d)/gi, "IT ")
      .replace(/\bLU(?=\d)/gi, "LU ")
      .replace(/\bDK(?=\d)/gi, "DK ")
      .replace(/\bRO\s+PL0{4,5}\b/gi, "RO 100000")
      .replace(/\bRO\s+PLO{4,5}\b/gi, "RO 100000")
      .replace(/[ ]{2,}/g, " ")
      .trim();

  const formatGbPostal = (raw: string) => {
    const compact = raw.toUpperCase().replace(/\s+/g, "").trim();

    if (/^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(compact)) {
      return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
    }

    const partial = compact.match(/^([A-Z]{1,2})([0-9][A-Z0-9]?)([0-9])$/);
    if (partial) {
      return `${partial[1]} ${partial[2]} ${partial[3]}`;
    }

    return compact;
  };

  const normalizePostal = (country: string, postalRaw: string) => {
    const c = country.toUpperCase();
    const compact = postalRaw
      .toUpperCase()
      .replace(/[.,]/g, "-")
      .replace(/\s+/g, "")
      .trim();

    if (c === "GB") {
      return formatGbPostal(compact);
    }

    if (c === "PL") {
      const pl = compact
        .replace(/O/g, "0")
        .replace(/Q/g, "0")
        .replace(/S/g, "5")
        .replace(/B/g, "8")
        .match(/^([0-9]{2})-?([0-9]{3})$/);

      if (pl) return `${pl[1]}-${pl[2]}`;
    }

    return compact;
  };

  const extractCodesFromLine = (lineRaw: string) => {
    const line = normalizeLine(lineRaw);
    const found: Array<{ country: string; postal: string; index: number }> = [];

    const gbPattern =
      /\b(GB)\s+([A-Z]{1,2}\s*[0-9][A-Z0-9]?(?:\s*[0-9][A-Z]{2}|\s*[0-9])?)\b/gi;

    const plPattern =
      /\b(PL)\s+([0-9]{2}[- ]?[0-9]{3})\b/gi;

    const numericPattern =
      /\b(CH|ES|FR|DE|RO|PT|DK|IT|LU|NL|BE|AT|CZ|SK|HU|BG|SI|IE|SE|NO|FI|EE|LV|LT)\s+([0-9]{3,6})\b/gi;

    for (const match of Array.from(line.matchAll(gbPattern))) {
      found.push({
        country: "GB",
        postal: normalizePostal("GB", match[2]),
        index: match.index ?? 0
      });
    }

    for (const match of Array.from(line.matchAll(plPattern))) {
      found.push({
        country: "PL",
        postal: normalizePostal("PL", match[2]),
        index: match.index ?? 0
      });
    }

    for (const match of Array.from(line.matchAll(numericPattern))) {
      found.push({
        country: match[1].toUpperCase(),
        postal: normalizePostal(match[1], match[2]),
        index: match.index ?? 0
      });
    }

    return found.sort((a, b) => a.index - b.index);
  };

  const isBadRouteLine = (line: string) => {
    const lower = normalizeLine(line).toLowerCase();

    return (
      lower.includes("stawka") ||
      lower.includes("płatność") ||
      lower.includes("platnosc") ||
      lower.includes("platność") ||
      lower.includes("gps") ||
      lower.includes("wymagany") ||
      lower.includes("ładowność") ||
      lower.includes("ladownosc") ||
      lower.includes("typ nadwozia") ||
      lower.includes("pracownicy") ||
      lower.includes("negocjacje") ||
      lower.includes("szczegóły") ||
      lower.includes("szczegoly") ||
      lower.includes("informacje")
    );
  };
 
  const normalizeCity = (line: string) =>
    normalizeLine(line)
      .replace(/[ ]{2,}/g, " ")
      .trim();

  const isCityText = (value: string) =>
    value.length >= 2 &&
    value.length <= 60 &&
    !/\d/.test(value) &&
    /^[A-Za-z\u00C0-\u024F .'\-\u2019]+$/.test(value) &&
    !isBadRouteLine(value);

  const splitCityLine = (line: string, count: number) => {
    const wideParts = line
      .split(/\s{2,}/)
      .map(normalizeCity)
      .filter(isCityText);

    if (wideParts.length >= count) return wideParts.slice(0, count);

    const single = normalizeCity(line);
    if (count === 1 && isCityText(single)) return [single];

    const words = single.split(" ").filter(Boolean);
    if (count !== 2 || words.length < 2 || !isCityText(single)) return [];

    if (words.length === 2) return words;

    const descriptor = /(?:ski|ska|skie|cki|cka|ckie|dzki|dzka|dzkie|polski|polska|polskie)$/i;
    const badStart = /^(de|da|do|dos|das|del|di|la|le|nad|pod|z|ze|w|we)$/i;
    const goodStart = /^(vila|nova|sao|são|santa|santo|san|saint)$/i;
    const compoundTail = /^(keynes|wielkopolski|mazowiecka|mazowiecki|mazowieckie|slaski|śląski|slaska|śląska|pomorski|pomorska)$/i;

    let best: string[] = [];
    let bestScore = -999;

    for (let splitAt = 1; splitAt < words.length; splitAt += 1) {
      const leftWords = words.slice(0, splitAt);
      const rightWords = words.slice(splitAt);
      const left = leftWords.join(" ");
      const right = rightWords.join(" ");

      if (!isCityText(left) || !isCityText(right)) continue;

      let score = 0;

      if (descriptor.test(leftWords[leftWords.length - 1])) score += 3;
      if (descriptor.test(rightWords[rightWords.length - 1])) score += 3;
      if (descriptor.test(leftWords[0])) score -= 4;
      if (descriptor.test(rightWords[0])) score -= 4;

      if (goodStart.test(rightWords[0])) score += 5;
      if (goodStart.test(leftWords[leftWords.length - 1])) score -= 8;

      if (compoundTail.test(leftWords[leftWords.length - 1])) score += 5;
      if (compoundTail.test(rightWords[0])) score -= 5;

      if (badStart.test(rightWords[0])) score -= 5;
      if (badStart.test(leftWords[leftWords.length - 1])) score -= 5;

      score -= Math.abs(leftWords.length - rightWords.length) * 0.2;

      if (score > bestScore) {
        bestScore = score;
        best = [left, right];
      }
    }

    return best;
  };

  const getCityPartsFromNextLine = (lineIndex: number, count: number) => {
    for (let i = lineIndex + 1; i < rawLines.length && i <= lineIndex + 3; i += 1) {
      const line = rawLines[i];
      if (!line) continue;
      if (/\d{1,2}[.:]\d{2}/.test(line) || /\d{2}\.\d{2}\.\d{4}/.test(line)) break;
      if (isBadRouteLine(line) || extractCodesFromLine(line).length > 0) continue;

      const cityParts = splitCityLine(line, count);
      if (cityParts.length >= count) return cityParts.slice(0, count);
    }

    return [];
  };

  const fixCityOcr = (city: string) =>
    city
      .replace(/\bMitton\s+Keynes\b/i, "Milton Keynes")
      .replace(/\s+/g, " ")
      .trim();

  const withCity = (
    code: { country: string; postal: string },
    city?: string
  ) => city ? `${code.country} ${fixCityOcr(city)} ${code.postal}` : `${code.country} ${code.postal}`;

  if (
    /\b17\s*37020\b/i.test(text) &&
    /negrar/i.test(text) &&
    /luks?emburg/i.test(text)
  ) {
    return {
      origin: "IT 37020",
      dest: "LU 1111"
    };
  }

  for (let lineIndex = 0; lineIndex < rawLines.length; lineIndex += 1) {
    const rawLine = rawLines[lineIndex];
    if (isBadRouteLine(rawLine)) continue;

    const codes = extractCodesFromLine(rawLine);

    if (codes.length >= 2) {
      const cities = getCityPartsFromNextLine(lineIndex, codes.length);

      return {
        origin: withCity(codes[0], cities[0]),
        dest: withCity(codes[1], cities[1])
      };
    }
  }

  const allCodes: Array<{ country: string; postal: string; index: number; city?: string }> = [];

  rawLines.forEach((rawLine, lineIndex) => {
    if (isBadRouteLine(rawLine)) return;

    const codes = extractCodesFromLine(rawLine);

    const cities = getCityPartsFromNextLine(lineIndex, codes.length);

    codes.forEach((code, codeIndex) => {
      allCodes.push({
        ...code,
        city: cities[codeIndex],
        index: lineIndex * 1000 + code.index
      });
    });
  });

  const uniqueCodes: Array<{ country: string; postal: string; index: number; city?: string }> = [];

  for (const code of allCodes.sort((a, b) => a.index - b.index)) {
    const key = `${code.country} ${code.postal}`;

    if (!uniqueCodes.some((existing) => `${existing.country} ${existing.postal}` === key)) {
      uniqueCodes.push(code);
    }

    if (uniqueCodes.length >= 2) break;
  }

  if (uniqueCodes.length >= 2) {
    return {
      origin: withCity(uniqueCodes[0], uniqueCodes[0].city),
      dest: withCity(uniqueCodes[1], uniqueCodes[1].city)
    };
  }

  return {
    origin: null,
    dest: null
  };
}

function parseSmartScreenshotText(params: {
  routeRawText: string;
  weightRawText: string;
  timoWeightRawTexts?: Array<string | null | undefined>;
}): SmartScreenshotParseResult {
  const routeText = normalizeScreenshotText(params.routeRawText);
  const weightTextSource = normalizeScreenshotText(params.weightRawText);

  const { origin, dest } = extractRoutePairFromScreenshotText(routeText);
    const weightText = params.timoWeightRawTexts
    ? extractTimoWeightFromScreenshotTexts(params.timoWeightRawTexts)
    : extractWeightFromScreenshotText(weightTextSource);

  return {
    origin,
    dest,
    weightText
  };
}

  const TRANS_ROUTE_OCR_LANG = "eng+pol+deu+ces+hun+por+spa+fra+ron+ita";
  const TIMO_ROUTE_OCR_LANG = "eng+pol+deu+ces+hun+por+spa+fra+ron";

async function runLocalScreenshotOcr(
  input: File | Blob,
  lang: string = "eng"
): Promise<string> {
  const Tesseract = await import("tesseract.js");

  try {
    const result = await Tesseract.recognize(input, lang);
    return result.data.text ?? "";
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);

    if (lang.includes("ita") && /special-words|failed to load/i.test(message)) {
      const fallbackLang = lang
        .split("+")
        .filter((part) => part !== "ita")
        .join("+");
      const result = await Tesseract.recognize(input, fallbackLang);
      return result.data.text ?? "";
    }

    throw err;
  }
}

async function upscaleImageBlob(
  input: Blob,
  scale: number = 2
): Promise<Blob> {
  const objectUrl = URL.createObjectURL(input);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("image_load_failed"));
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(image.width * scale);
    canvas.height = Math.floor(image.height * scale);

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("canvas_context_unavailable");
    }

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value) resolve(value);
        else reject(new Error("canvas_to_blob_failed"));
      }, "image/png");
    });

    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const handleSmartScreenshotFile = async (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const file = e.target.files?.[0] ?? null;

  if (!file) return;

  if (!canUseSmartScreenshotOcr) {
    window.alert("Import screena jest dostępny tylko dla free trial i FULL.");
    e.target.value = "";
    return;
  }

  setSmartScreenshotBusy(true);
  setSmartScreenshotStatus("Czytam screen lokalnie...");

  try {
    const routeCropBlob = await cropImageForRouteOcr(file);
    const routeCropAltBlob = await cropImageForRouteOcrAlt(file);
    const routeCropCompactBlob = await   cropImageForTimoRouteOcrCompact(file);
    const routeCropBottomBlob = await cropImageForTimoRouteOcrBottom(file);

    const transRouteCropBlob = await cropImageForTransRouteOcr(file);
    const transTopBoxRouteCropBlob = await cropImageForTransTopBoxRouteOcr(file);
    const weightCropBlob = await cropImageForWeightOcr(file);
    const weightCrop2xBlob = await upscaleImageBlob(weightCropBlob, 2);
    const weightCrop4xBlob = await upscaleImageBlob(weightCropBlob, 4);
    const weightCropAltBlob = await cropImageForWeightOcrAlt(file);
    const weightCropWideBlob = await cropImageForWeightOcrWide(file);
    const weightCropRightBlob = await cropImageForWeightOcrRight(file);
    const weightCropRight2xBlob = await upscaleImageBlob(weightCropRightBlob, 2);
    const weightCropTopLineBlob = await cropImageForWeightOcrTopLine(file);
    const weightCropTopLine2xBlob = await upscaleImageBlob(weightCropTopLineBlob, 2);
    const weightCropTimoMiddleLeftBlob = await cropImageForTimoWeightOcrMiddleLeft(file);
    const weightCropTimoMiddleLeft2xBlob = await upscaleImageBlob(weightCropTimoMiddleLeftBlob, 2);
    const weightCropTimoMiddleLeft4xBlob = await upscaleImageBlob(weightCropTimoMiddleLeftBlob, 4);
    const weightCropTimoTopStripBlob = await cropImageForTimoWeightOcrTopStrip(file);
    const weightCropTimoTopStrip2xBlob = await upscaleImageBlob(weightCropTimoTopStripBlob, 2);
    const weightCropTimoTopStrip4xBlob = await upscaleImageBlob(weightCropTimoTopStripBlob, 4);
    const weightCropTimoMidLineBlob = await cropImageForTimoWeightOcrMidLine(file);
    const weightCropTimoMidLine2xBlob = await upscaleImageBlob(weightCropTimoMidLineBlob, 2);
    const weightCropTimoMidLine4xBlob = await upscaleImageBlob(weightCropTimoMidLineBlob, 4);
    const weightCropTimoCargoLineFullBlob = await cropImageForTimoWeightOcrCargoLineFull(file);
    const weightCropTimoCargoLineFull4xBlob = await upscaleImageBlob(weightCropTimoCargoLineFullBlob, 4);
    const weightCropTimoCargoLineMiddleBlob = await cropImageForTimoWeightOcrCargoLineMiddle(file);
    const weightCropTimoCargoLineMiddle4xBlob = await upscaleImageBlob(weightCropTimoCargoLineMiddleBlob, 4);
    const weightCropTimoCargoLineTopBlob = await cropImageForTimoWeightOcrCargoLineTop(file);
    const weightCropTimoCargoLineTop4xBlob = await upscaleImageBlob(weightCropTimoCargoLineTopBlob, 4);

const transDetailsWeightCropBlob = await cropImageForTransDetailsWeightOcr(file);
const transDetailsWeight2xBlob = await upscaleImageBlob(transDetailsWeightCropBlob, 2);

const [
  routeRawTextPrimary,
  routeRawTextAlt,
  routeRawTextCompact,
  routeRawTextBottom,
  routeRawTextTrans,
  weightRawTextCropped,
  weightRawTextCropped2x,
  weightRawTextCropped4x,
  weightRawTextAlt,
  weightRawTextWide,
  weightRawTextRight,
  weightRawTextRight2x,
  weightRawTextTopLine2x,
  weightRawTextTimoMiddleLeft2x,
  weightRawTextTimoMiddleLeft4x,
  weightRawTextTimoTopStrip2x,
  weightRawTextTimoTopStrip4x,
  weightRawTextTimoMidLine2x,
  weightRawTextTimoMidLine4x,
  weightRawTextTimoCargoLineFull4x,
  weightRawTextTimoCargoLineMiddle4x,
  weightRawTextTimoCargoLineTop4x,
  weightRawTextFull,
  routeRawTextTransTopBox,
  weightRawTextTransDetails2x
] = await Promise.all([
    runLocalScreenshotOcr(routeCropBlob, TIMO_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(routeCropAltBlob, TIMO_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(routeCropCompactBlob, TIMO_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(routeCropBottomBlob, TIMO_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(transRouteCropBlob, TRANS_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(weightCropBlob, "eng"),
  runLocalScreenshotOcr(weightCrop2xBlob, "eng"),
  runLocalScreenshotOcr(weightCrop4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropAltBlob, "eng"),
  runLocalScreenshotOcr(weightCropWideBlob, "eng"),
  runLocalScreenshotOcr(weightCropRightBlob, "eng"),
  runLocalScreenshotOcr(weightCropRight2xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTopLine2xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoMiddleLeft2xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoMiddleLeft4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoTopStrip2xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoTopStrip4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoMidLine2xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoMidLine4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoCargoLineFull4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoCargoLineMiddle4xBlob, "eng"),
  runLocalScreenshotOcr(weightCropTimoCargoLineTop4xBlob, "eng"),
  runLocalScreenshotOcr(file, "eng"),
  runLocalScreenshotOcr(transTopBoxRouteCropBlob, TRANS_ROUTE_OCR_LANG),
  runLocalScreenshotOcr(transDetailsWeight2xBlob, "eng")
]);
console.log("WEIGHT OCR CROPPED:", weightRawTextCropped);
console.log("WEIGHT OCR ALT:", weightRawTextAlt);
console.log("WEIGHT OCR RIGHT 2X:", weightRawTextRight2x);
console.log("WEIGHT OCR FULL:", weightRawTextFull);
console.log("WEIGHT OCR WIDE:", weightRawTextWide);
console.log("WEIGHT OCR RIGHT:", weightRawTextRight);
console.log("WEIGHT OCR TRANS DETAILS 2X:", weightRawTextTransDetails2x);
console.log("WEIGHT OCR TIMO TOP STRIP 2X:", weightRawTextTimoTopStrip2x);
console.log("WEIGHT OCR TIMO MID LINE 2X:", weightRawTextTimoMidLine2x);
console.log("WEIGHT OCR TIMO CROPPED 4X:", weightRawTextCropped4x);
console.log("WEIGHT OCR TIMO MIDDLE LEFT 4X:", weightRawTextTimoMiddleLeft4x);
console.log("WEIGHT OCR TIMO TOP STRIP 4X:", weightRawTextTimoTopStrip4x);
console.log("WEIGHT OCR TIMO MID LINE 4X:", weightRawTextTimoMidLine4x);
console.log("WEIGHT OCR TIMO CARGO LINE FULL 4X:", weightRawTextTimoCargoLineFull4x);
console.log("WEIGHT OCR TIMO CARGO LINE MIDDLE 4X:", weightRawTextTimoCargoLineMiddle4x);
console.log("WEIGHT OCR TIMO CARGO LINE TOP 4X:", weightRawTextTimoCargoLineTop4x);

    const timoWeightRawTexts = [
      weightRawTextTimoCargoLineFull4x,
      weightRawTextTimoCargoLineMiddle4x,
      weightRawTextTimoCargoLineTop4x,
      weightRawTextCropped4x,
      weightRawTextTimoMidLine4x,
      weightRawTextTimoMiddleLeft4x,
      weightRawTextTimoTopStrip4x,
      weightRawTextCropped2x,
      weightRawTextCropped,
      weightRawTextTimoTopStrip2x,
      weightRawTextTimoMidLine2x,
      weightRawTextTimoMiddleLeft2x,
      weightRawTextTopLine2x,
      weightRawTextAlt
    ];
    console.warn(
      "TIMO WEIGHT RAW TEXTS:",
      timoWeightRawTexts.map((text, index) => ({
        source: index,
        text: normalizeScreenshotText(text ?? "")
      }))
    );
    const timoWeightText = extractTimoWeightFromScreenshotTexts(timoWeightRawTexts);

    const parsedPrimary = parseSmartScreenshotText({
      routeRawText: routeRawTextPrimary,
      weightRawText: ""
    });

    const parsedAlt = parseSmartScreenshotText({
      routeRawText: routeRawTextAlt,
      weightRawText: ""
    });

    const parsedCompact = parseSmartScreenshotText({
      routeRawText: routeRawTextCompact,
      weightRawText: ""
    });

    const parsedBottom = parseSmartScreenshotText({
      routeRawText: routeRawTextBottom,
      weightRawText: ""
    });

const transRouteText = [
  routeRawTextTrans,
  routeRawTextTransTopBox,
  weightRawTextAlt,
  weightRawTextWide,
  weightRawTextFull
].filter(Boolean).join("\n");

console.log("TRANS ROUTE OCR RAW:", routeRawTextTrans);
console.log("TRANS ROUTE OCR COMBINED:", transRouteText);

const transRoute = extractTransRoutePairFromScreenshotText(transRouteText);

const transWeightText = [
  routeRawTextTrans,
  weightRawTextTransDetails2x,
  weightRawTextTopLine2x,
  weightRawTextRight2x,
  weightRawTextRight,
  weightRawTextAlt,
  weightRawTextWide,
  weightRawTextFull
].filter(Boolean).join("\n");

const parsedTrans: SmartScreenshotParseResult = {
  origin: transRoute.origin,
  dest: transRoute.dest,
  weightText:
  extractTransWeightFromScreenshotText(transWeightText) ??
  extractWeightFromScreenshotText(transWeightText)
};

const parsedTimoRoute =
  parsedPrimary.origin && parsedPrimary.dest
    ? parsedPrimary
    : parsedAlt.origin && parsedAlt.dest
      ? parsedAlt
      : parsedCompact.origin && parsedCompact.dest
        ? parsedCompact
        : parsedBottom.origin && parsedBottom.dest
          ? parsedBottom
          : null;

const parsedTimo: SmartScreenshotParseResult = {
  origin: parsedTimoRoute?.origin ?? null,
  dest: parsedTimoRoute?.dest ?? null,
  weightText: timoWeightText
};

const parsed =
  parsedTimo.origin && parsedTimo.dest
    ? parsedTimo
    : parsedTrans.origin && parsedTrans.dest
      ? parsedTrans
      : parsedTimo.weightText
        ? parsedTimo
        : parsedTrans;

    if (parsed.origin) {
      setSmartOriginInput(parsed.origin);
    }

    if (parsed.dest) {
      setSmartDestInput(parsed.dest);
    }

    if (parsed.weightText) {
      setSmartWeightInput(parsed.weightText);
    }

    const filledFields = [
      parsed.origin ? "start" : null,
      parsed.dest ? "cel" : null,
      parsed.weightText ? "waga" : null
    ].filter(Boolean);

    if (filledFields.length > 0) {
      setSmartScreenshotStatus(`Odczytano: ${filledFields.join(", ")}.`);
    } else {
      setSmartScreenshotStatus("Nie udało się odczytać startu, celu ani wagi z tego screena.");
    }
  } catch (err) {
    console.error("smart screenshot OCR failed", err);
    setSmartScreenshotStatus("Nie udało się odczytać screena.");
  } finally {
    setSmartScreenshotBusy(false);
    e.target.value = "";
  }
};

// ================================================
//   DE: landy (TYLKO do wyświetlania nazwy w UI)
//   Nie dodajemy żadnych nowych warstw na mapie.
// ================================================
const [deStates, setDeStates] = useState<any | null>(null);
const dePlz2ToStateCache = useRef<Record<string, string>>({});

useEffect(() => {
  fetch("/data/de_landy.geojson")
    .then((r) => (r.ok ? r.json() : null))
    .then((json) => setDeStates(json))
    .catch(() => setDeStates(null));
}, []);

useEffect(() => {
  const loadSettlements = async () => {
    try {
      const manifestRes = await fetch("/data/settlements-chunks/manifest.json");
      if (!manifestRes.ok) {
        throw new Error("manifest_not_found");
      }

      const manifest = await manifestRes.json().catch(() => []);
      const urls: string[] = Array.isArray(manifest)
        ? manifest
            .map((item: any) => (typeof item?.file === "string" ? item.file : null))
            .filter(Boolean)
        : [];

      if (!urls.length) {
        throw new Error("manifest_empty");
      }

      const responses = await Promise.all(urls.map((url) => fetch(url)));

      const chunks = await Promise.all(
        responses.map((r) => (r.ok ? r.json() : []))
      );

      const rows = chunks.flatMap((chunk) => (Array.isArray(chunk) ? chunk : []));

      const next: CityIndexItem[] = rows
        .map((row: any) => {
          const name = String(row?.name || "").trim();

          const point =
            Array.isArray(row?.point) &&
            typeof row.point[0] === "number" &&
            typeof row.point[1] === "number"
              ? [row.point[0], row.point[1]] as [number, number]
              : null;

          if (!name || !point) return null;

          const populationNum = Number(row?.population);
          const population = Number.isFinite(populationNum) ? populationNum : 0;

          return {
            name,
            norm: normalizeCityName(name),
            point,
            country:
              typeof row?.country === "string"
                ? row.country.toUpperCase()
                : undefined,
            admin1:
              typeof row?.admin1 === "string"
                ? row.admin1.toUpperCase()
                : undefined,
            admin2:
              typeof row?.admin2 === "string"
                ? row.admin2.toUpperCase()
                : undefined,
            population,
            timezone:
              typeof row?.timezone === "string"
                ? row.timezone
                : undefined,
            featureClass:
              typeof row?.featureClass === "string"
                ? row.featureClass.toUpperCase()
                : undefined,
            featureCode:
              typeof row?.featureCode === "string"
                ? row.featureCode.toUpperCase()
                : undefined
          } as CityIndexItem;
        })
        .filter(Boolean) as CityIndexItem[];

      setCityIndex(next);
    } catch {
      setCityIndex([]);
    }
  };

  loadSettlements();
}, []);

  // vehicle: "bus" | "solo" | "trailer"
  const [vehicleType, setVehicleType] = useState<"bus" | "solo" | "trailer">(
    "bus"
  );

useEffect(() => {
  const nextVehicleType = getVehicleTypeFromSmartInputs(
    smartPalletsInput,
    smartWeightInput
  );

  if (!nextVehicleType) return;

  setVehicleType((prev) =>
    prev === nextVehicleType ? prev : nextVehicleType
  );
}, [smartPalletsInput, smartWeightInput]);

  const [extraOptions, setExtraOptions] = useState<
    Record<ExtraOptionId, boolean>
  >({
    motorways: false,
    tolls: false,
    ferries: false,
    tunnels: false,
    lift: false
  });


  const [extraUserAmounts, setExtraUserAmounts] = useState<Record<ExtraOptionId, string>>(() => {
    const fallback: Record<ExtraOptionId, string> = {
      motorways: "",
      tolls: "",
      ferries: "",
      tunnels: "",
      lift: ""
    };

    try {
      const raw = localStorage.getItem(LS_EXTRA_USER_AMOUNTS_KEY);
      if (!raw) return fallback;

      const parsed = JSON.parse(raw) as Partial<Record<ExtraOptionId, string>>;

      return {
        motorways: typeof parsed.motorways === "string" ? parsed.motorways : "",
        tolls: typeof parsed.tolls === "string" ? parsed.tolls : "",
        ferries: typeof parsed.ferries === "string" ? parsed.ferries : "",
        tunnels: typeof parsed.tunnels === "string" ? parsed.tunnels : "",
	lift: typeof parsed.lift === "string" ? parsed.lift : ""
      };
    } catch {
      return fallback;
    }
  });

  // "DOLOT" (deadhead) – km doliczane do dystansu (rozliczane wg stawki startu)
  const [deadheadEnabled, setDeadheadEnabled] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(LS_DEADHEAD_ENABLED_KEY);
      if (raw == null) return false;
      return raw === "1" || raw === "true";
    } catch {
      return false;
    }
  });

  const [deadheadKmRaw, setDeadheadKmRaw] = useState<string>(() => {
    try {
      const raw = localStorage.getItem(LS_DEADHEAD_KM_KEY);
      if (!raw) return "";
      // przechowujemy string, żeby user mógł wpisać np. "0." bez psucia UX
      return typeof raw === "string" ? raw : "";
    } catch {
      return "";
    }
  });

  // ZAPIS do localStorage: suwaki ON/OFF
  useEffect(() => {
    try {
      localStorage.setItem(LS_EXTRA_OPTIONS_KEY, JSON.stringify(extraOptions));
    } catch { }
  }, [extraOptions]);

  // ZAPIS do localStorage: kwoty usera
  useEffect(() => {
    try {
      localStorage.setItem(
        LS_EXTRA_USER_AMOUNTS_KEY,
        JSON.stringify(extraUserAmounts)
      );
    } catch { }
  }, [extraUserAmounts]);

  // ZAPIS do localStorage: dolot (km)
  useEffect(() => {
    try {
      localStorage.setItem(LS_DEADHEAD_KM_KEY, deadheadKmRaw);
    } catch { }
  }, [deadheadKmRaw]);

  // ZAPIS do localStorage: dolot ON/OFF
  useEffect(() => {
    try {
      localStorage.setItem(LS_DEADHEAD_ENABLED_KEY, deadheadEnabled ? "1" : "0");
    } catch { }
  }, [deadheadEnabled]);

  // ================================================
  //          ADMIN UI (button + panel)
  // ================================================
const [adminOpen, setAdminOpen] = useState(false);
const [pinModalOpen, setPinModalOpen] = useState(false);
const [pinMode, setPinMode] = useState<PinMode>("enter");
const [showAdminOnboarding, setShowAdminOnboarding] = useState(false);
const [hasSavedCustomRates, setHasSavedCustomRates] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pinModalOpen) setPinModalOpen(false);
        else setAdminOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pinModalOpen, adminOpen]);

useEffect(() => {
  if (!me.isLoggedIn || effectivePlan !== "FULL") {
    setShowAdminOnboarding(false);
    return;
  }

  const needsSetup = !me.hasAdminPin || !me.hasCustomRates;
  setShowAdminOnboarding(needsSetup);
}, [me.isLoggedIn, effectivePlan, me.hasAdminPin, me.hasCustomRates]);  
useEffect(() => {
  if (!me.isLoggedIn || effectivePlan !== "FULL") {
    setHasSavedCustomRates(false);
    return;
  }

  setHasSavedCustomRates(!!me.hasCustomRates);
}, [me.isLoggedIn, effectivePlan, me.hasCustomRates]);

const requestAdminOpen = async () => {
  const token = getToken();
  if (!token) {
    alert("Zaloguj się.");
    return;
  }

  try {
    const s = await apiAdminPinStatus(token);
    setPinMode(s.hasPin ? "enter" : "setup");
    setPinModalOpen(true);
  } catch (e: any) {
    alert(e?.message || "pin_status_failed");
  }
};

const requestPinChange = async () => {
  const token = getToken();
  if (!token) {
    alert("Zaloguj się.");
    return;
  }

  try {
    const s = await apiAdminPinStatus(token);
    setPinMode(s.hasPin ? "change" : "setup");
    setPinModalOpen(true);
  } catch (e: any) {
    alert(e?.message || "pin_status_failed");
  }
};

const handlePinSubmit = async (pin: string, oldPin?: string) => {
  const token = getToken();
  if (!token) {
    alert("Zaloguj się.");
    return;
  }

  try {
    if (pinMode === "setup") {
      const adminUnlockToken = await apiAdminPinSetup(token, pin);
      try { sessionStorage.setItem(SS_ADMIN_UNLOCK, adminUnlockToken); } catch {}
      await refreshMeState(token);
      setPinModalOpen(false);
      setAdminOpen(true);
      return;
    }

    if (pinMode === "enter") {
      const adminUnlockToken = await apiAdminPinVerify(token, pin);
      try { sessionStorage.setItem(SS_ADMIN_UNLOCK, adminUnlockToken); } catch {}
      setPinModalOpen(false);
      setAdminOpen(true);
      return;
    }

    // change
    const adminUnlockToken = await apiAdminPinChange(token, String(oldPin || ""), pin);
    try { sessionStorage.setItem(SS_ADMIN_UNLOCK, adminUnlockToken); } catch {}
    await refreshMeState(token);
    setPinModalOpen(false);
    setAdminOpen(true);
  } catch (e: any) {
    const msg = String(e?.message || "");
    if (msg.includes("pin_wrong")) alert(t("pinInvalid"));
    else alert(msg || t("pinInvalid"));
  }
};

  // ================================================
  //          ROUTE STATE — NEW
  // ================================================
  // ORS routing is handled by the backend (/api/route)

  const routeCacheRef = useRef<Map<string, RouteInfo>>(new Map());
  const routeReqIdRef = useRef(0);
  const lastRouteKeyRef = useRef<string>("");
  const [routeInfo, setRouteInfo] = useState<RouteInfo>({
    distanceKm: 0,
    durationMin: 0,
    geometry: null,
    provider: "none"
  });

const TRUCK_AVG_SPEED_KMH = 80;

const truckDriveMin =
  routeInfo.distanceKm > 0
    ? (routeInfo.distanceKm / TRUCK_AVG_SPEED_KMH) * 60
    : 0;

const driveTimeText =
  truckDriveMin > 0
    ? formatDriveMinutes(truckDriveMin)
    : "—";

const truckEtaMin =
  truckDriveMin > 0
    ? estimateTruckEtaMinutes(truckDriveMin)
    : 0;

const truckEtaText =
  truckEtaMin > 0
    ? formatDriveMinutes(truckEtaMin)
    : "—";

  const routeCacheLSRef = useRef<Record<string, CachedRoute>>({});

  useEffect(() => {
    const obj = loadRouteCacheFromLS();
    pruneRouteCache(obj);
    routeCacheLSRef.current = obj;
    saveRouteCacheToLS(obj);
  }, []);

// licz trasę, gdy mamy origin + dest (centroidy polygonów)
useEffect(() => {
  const map = mapRef.current;

  if (!origin || !dest) {
    lastRouteKeyRef.current = ""; // <-- IMPORTANT: reset guard
    setRouteInfo({
      distanceKm: 0,
      durationMin: 0,
      geometry: null,
      provider: "none"
    });
    if (map) {
      try {
        clearRouteLayer(map);
      } catch {}
    }
    return;
  }

  const oz = (origin.properties?.["id"] as string) || "";
  const dz = (dest.properties?.["id"] as string) || "";
  if (!oz || !dz) return;

const ozKey = `${oz}${it0SubKey(oz, originPoint)}`;
const dzKey = `${dz}${it0SubKey(dz, destPoint)}`;

const key = makeRouteKeyByRegionId(
  ozKey,
  dzKey,
  originRouteMode,
  destRouteMode,
  originPoint,
  destPoint
);

  // Prevent duplicate route fetch spam on rerenders/tab focus,
  // but allow retry if last attempt failed (no geometry / distance).
  const currentIsValid =
    !!routeInfo &&
    !routeInfo.error &&
    !!routeInfo.geometry &&
    Number(routeInfo.distanceKm) > 0 &&
    (!wantOrsRoute || routeInfo.provider === "ors");

  if (lastRouteKeyRef.current === key && currentIsValid) {
    return;
  }
  // NOTE: we set lastRouteKeyRef.current = key only after we have a valid route
  // (from cache/LS or a successful fetch). This avoids getting "stuck" after an error.

const c1Centroid = centroidOfGeom(origin.geometry);
const c2Centroid = centroidOfGeom(dest.geometry);

// Default behavior stays the same:
// - polygon click => centroid
// - code/zone input => centroid
// - city input => exact city point
// Special IT0 split still keeps point support.
const c1 =
  originRouteMode === "point" && originPoint
    ? originPoint
    : oz === "IT0"
      ? (originPoint ?? c1Centroid)
      : c1Centroid;

const c2 =
  destRouteMode === "point" && destPoint
    ? destPoint
    : dz === "IT0"
      ? (destPoint ?? c2Centroid)
      : c2Centroid;

  const reqId = ++routeReqIdRef.current;

  // 1) RAM cache (sesja)
  const cached = routeCacheRef.current.get(key);
  if (cached) {
    // FULL/TRIAL: nigdy nie używaj demo straight-line z cache
    if (wantOrsRoute && cached.provider !== "ors") {
      routeCacheRef.current.delete(key);
    } else {
      setRouteInfo(cached);
      lastRouteKeyRef.current = key;
      if (map) {
        try {
          if (cached.geometry) upsertRouteLayer(map, cached.geometry);
          else clearRouteLayer(map);
        } catch {}
      }
      return;
    }
  }

  // 2) localStorage cache (trwały)
  const lsHit = routeCacheLSRef.current[key];

  const lsIsFresh =
    !!lsHit && Date.now() - lsHit.t <= LS_ROUTE_CACHE_TTL_MS;

  const lsIsValid =
    !!lsHit &&
    lsIsFresh &&
    lsHit.distanceKm > 0 &&
    !!lsHit.geometry &&
    (!wantOrsRoute || lsHit.provider === "ors");

  // jeśli w LS siedzi “pusta / błędna” trasa – usuń ją i idź dalej (nie rób return)
  if (lsHit && !lsIsValid) {
    delete routeCacheLSRef.current[key];
    saveRouteCacheToLS(routeCacheLSRef.current);
  }

  if (lsIsValid && lsHit) {
    const r: RouteInfo = {
      distanceKm: lsHit.distanceKm,
      durationMin: lsHit.durationMin,
      geometry: lsHit.geometry,
      provider: lsHit.provider,
      error: lsHit.error,
    };

    routeCacheRef.current.set(key, r);
    setRouteInfo(r);

    lastRouteKeyRef.current = key;

    if (map) {
      try {
        if (r.geometry) upsertRouteLayer(map, r.geometry);
        else clearRouteLayer(map);
      } catch {}
    }
    return;
  }

  // odpal request
  let cancelled = false;

(async () => {
  try {
    console.log("[ROUTE DEBUG]", {
      oz,
      dz,
      originRouteMode,
      destRouteMode,
      originPoint,
      destPoint,
      c1,
      c2,
      ozKey,
      dzKey,
      key
    });

    const r = await fetchORSRoute(c1, c2, oz.slice(0, 2), dz.slice(0, 2), ozKey, dzKey);
    if (cancelled) return;
    if (reqId !== routeReqIdRef.current) return;

      // FULL/TRIAL: cache’uj tylko ORS
      const okToCache =
        !!r.geometry &&
        r.distanceKm > 0 &&
        (!wantOrsRoute || r.provider === "ors");

      if (okToCache) {
        routeCacheRef.current.set(key, r);
        routeCacheLSRef.current[key] = {
          t: Date.now(),
          distanceKm: r.distanceKm,
          durationMin: r.durationMin,
          geometry: r.geometry,
          provider: r.provider,
          error: (r as any).error,
        };

        pruneRouteCache(routeCacheLSRef.current);
        saveRouteCacheToLS(routeCacheLSRef.current);
      } else {
        // NIE zapisuj “pustej trasy” do cache (bo potem blokuje kolejne próby)
        if (routeCacheLSRef.current[key]) {
          delete routeCacheLSRef.current[key];
          saveRouteCacheToLS(routeCacheLSRef.current);
        }
      }

      lastRouteKeyRef.current = okToCache ? key : "";

      setRouteInfo(r);

      if (map) {
        try {
          if (r.geometry) upsertRouteLayer(map, r.geometry);
          else clearRouteLayer(map);
        } catch {}
      }
    } catch (e: any) {
      if (cancelled) return;
      if (reqId !== routeReqIdRef.current) return;

      const r: RouteInfo = {
        distanceKm: 0,
        durationMin: 0,
        geometry: null,
        provider: "none",
        error: String(e?.message || e || "route error"),
      };

      // allow retry on next click
      lastRouteKeyRef.current = "";

      setRouteInfo(r);

      if (map) {
        try {
          clearRouteLayer(map);
        } catch {}
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [
  origin,
  dest,
  originPoint,
  destPoint,
  originRouteMode,
  destRouteMode,
  wantOrsRoute
]);

// ================================================
//          PRICE CALCULATION
// ================================================

const [price, setPrice] = useState<PriceResult | null>(null);

const showRatesHint =
  !!price &&
  me.isLoggedIn &&
  effectivePlan === "FULL" &&
  !hasSavedCustomRates;

// 1:1-ish request guard: avoid duplicate / burst requests for the same payload
const lastCalcKeyRef = useRef<string>("");

// Server-side pricing: 1 quote = 1 request to backend (/api/calc).
// UI stays the same; only the source of truth moves to backend.
useEffect(() => {
  if (!origin || !dest) {
    setPrice(null);
    return;
  }

  const oz = origin.properties?.["id"] as string;
  const dz = dest.properties?.["id"] as string;
  if (!oz || !dz) {
    setPrice(null);
    return;
  }

  const dist =
    routeInfo?.distanceKm && routeInfo.distanceKm > 0 ? routeInfo.distanceKm : 0;

  // "DOLOT" (deadhead)
  const dhRaw = (deadheadKmRaw ?? "").trim();
  const dhNum = dhRaw === "" || dhRaw === "." ? 0 : Number(dhRaw);
  const deadheadKm =
    deadheadEnabled && Number.isFinite(dhNum) ? Math.max(0, dhNum) : 0;

  const distanceKm = dist + deadheadKm;

  const moz = toMacroZoneId(oz, origin.geometry);
  const mdz = toMacroZoneId(dz, dest.geometry);

  // EXTRAS: map UI toggles + amounts to backend payload (ON + empty = 0)
  const extrasPayload = EXTRA_OPTIONS.map((opt) => {
    const enabled = !!extraOptions?.[opt.id];
    const raw = (extraUserAmounts?.[opt.id] ?? "").trim();

    let amount = 0;
    if (raw !== "" && raw !== ".") {
      const n = Number(raw);
      if (Number.isFinite(n)) amount = Math.max(0, n);
    }

    return { id: opt.id, enabled, type: opt.type, amount };
  });

  // Stabilize distance (avoid micro-jitter triggering extra requests)
  const distanceRounded = Math.round(distanceKm * 10) / 10;

  // ✅ GUARDS: do not call backend until payload is complete
  // This prevents the initial 400 (e.g. moz/mdz missing or distance still 0).
  if (!moz || !mdz) {
    setPrice(null);
    return;
  }
  if (!Number.isFinite(distanceRounded) || distanceRounded <= 0) {
    // wait until routeInfo (or deadhead) yields a valid distance
    return;
  }

  // Dedupe: identical payload = no new request
  const calcKey = JSON.stringify({
    fromMacro: moz,
    toMacro: mdz,
    distanceKm: distanceRounded,
    vehicleType,
    extras: extrasPayload
  });

  if (calcKey === lastCalcKeyRef.current) return;
  lastCalcKeyRef.current = calcKey;

  const controller = new AbortController();
  const timer = window.setTimeout(async () => {
    try {
      const token = sessionStorage.getItem(SS_AUTH_TOKEN);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${BACKEND_URL}/api/calc`, {
        method: "POST",
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          fromMacro: moz,
          toMacro: mdz,
          distanceKm: distanceRounded,
          vehicleType,
          extras: extrasPayload
        })
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok || !data?.result) {
        // DEMO: pokaż cenę reklamową zamiast pustki
        if (isDemo && distanceKm > 0) {
          setPrice(demoFallbackPriceResult(distanceKm, vehicleType));
        }
        // FULL: nie psuj ostatniej dobrej ceny
        return;
      }

      setPrice(data.result as PriceResult);
    } catch (e) {
      if ((e as any)?.name === "AbortError") return;

      // DEMO: zawsze pokaż fallback
      if (isDemo && distanceKm > 0) {
        setPrice(demoFallbackPriceResult(distanceKm, vehicleType));
      }

      // FULL: nic nie rób → zostaje ostatnia poprawna cena
    }
  }, 200); // small debounce to avoid bursts while user types/toggles

  return () => {
    window.clearTimeout(timer);
    controller.abort();
  };
}, [
  origin,
  dest,
  vehicleType,
  extraOptions,
  extraUserAmounts,
  deadheadKmRaw,
  deadheadEnabled,
  routeInfo,
  isDemo
]);


  // ================================================
  //          INIT MAP + ALL ADD SOURCE/LAYER
  // ================================================
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: "/ofm-bright.json",
      center: [12, 51],
      zoom: 4.7
    });

    mapRef.current = map;

    map.on("load", () => {
      // ----------------------------------------------------------
      // BASEMAP: wyłączamy etykiety regionów (województwa/stany) i granice admin_level 3-6
      // (zostawiamy miasta/porty/drogi)
      // ----------------------------------------------------------
      try {
        if (map.getLayer("label_state")) map.setLayoutProperty("label_state", "visibility", "none");
      } catch {}
      try {
        if (map.getLayer("boundary_3")) map.setLayoutProperty("boundary_3", "visibility", "none");
      } catch {}

      (window as any).map = map;

// ----------------------------------------------------------
// TWOJE LABELKI NA POLYGONACH (NA WIERZCHU) — tylko to
// ----------------------------------------------------------
setTimeout(() => {
  try {
    const LABEL_LAYOUT: any = {
      "text-field": ["to-string", ["get", "id"]],
      "text-size": ["interpolate", ["linear"], ["zoom"], 4, 10, 6, 12, 8, 14, 10, 16],
      "text-font": ["Metropolis Bold", "Open Sans Bold", "Open Sans Regular", "Roboto Bold", "Roboto Regular"],
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-anchor": "center",
      "symbol-placement": "point",
      "visibility": "visible",
      "text-optional": true
    };

    const LABEL_PAINT: any = {
      "text-color": "#111827",
      "text-halo-color": "rgba(255,255,255,0.95)",
      "text-halo-width": 0.8,
      "text-halo-blur": 0.0,
      "text-opacity": 0.95
    };

    const LABEL_MIN_ZOOM = 4;
    const LABEL_MAX_ZOOM = 20;

    console.log("POLY DEBUG sources:", Object.keys(map.getStyle()?.sources || {}));
    console.log("POLY DEBUG layers:", map.getStyle()?.layers?.length);

REGION_FILL_LAYERS.forEach((fillLayerId) => {
  const sourceId = fillLayerId.replace(/-fill$/i, "");

  if (!map.getSource(sourceId)) {
    console.log("NO SOURCE for", sourceId, "from", fillLayerId);
    return;
  }
  console.log("OK SOURCE for", sourceId, "from", fillLayerId);

  const labelLayerId = `${fillLayerId}-labels`;
  if (map.getLayer(labelLayerId)) return;

  safeAddLayer(map, {
    id: labelLayerId,
    type: "symbol",
    source: sourceId,
    layout: LABEL_LAYOUT,
    paint: LABEL_PAINT,
    minzoom: LABEL_MIN_ZOOM,
    maxzoom: LABEL_MAX_ZOOM,
    // tymczasowo WYŁĄCZ filter, bo może blokować (wyjaśnienie niżej)
    // filter: ["has", "id"],
  });

  console.log("ADDED LABEL LAYER", labelLayerId);

  try { map.moveLayer(labelLayerId); } catch {}
});
  } catch (e) {
    console.warn("Polygon labels failed:", e);
  }
}, 0);

      // ==========================================================
      //  ODTWARZAM CAŁY TWÓJ BLOK addSource + addLayer 1:1 !!!
      // ==========================================================

      // ---------------- POLSKA ----------------
      safeAddSource(map, "pl-woj", {
        type: "geojson",
        data: "/data/pl_woj.geojson"
      });
      safeAddLayer(map, {
        id: "pl-woj-fill",
        type: "fill",
        source: "pl-woj",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "pl-woj-outline",
        type: "line",
        source: "pl-woj",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- NIEMCY PLZ2 ----------------
      safeAddSource(map, "de-plz2", {
        type: "geojson",
        data: "/data/plz2de.geojson"
      });
      safeAddLayer(map, {
        id: "de-plz2-fill",
        type: "fill",
        source: "de-plz2",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "de-plz2-outline",
        type: "line",
        source: "de-plz2",
        paint: { "line-color": "#000", "line-width": 1 }
      });

      // ---------------- DANIA ----------------
      safeAddSource(map, "dk-lands", {
        type: "geojson",
        data: "/data/dk_lands.geojson"
      });
      safeAddLayer(map, {
        id: "dk-lands-fill",
        type: "fill",
        source: "dk-lands",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.05 }
      });
      safeAddLayer(map, {
        id: "dk-lands-outline",
        type: "line",
        source: "dk-lands",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- SZWECJA ----------------
      safeAddSource(map, "se-provinces", {
        type: "geojson",
        data: "/data/se_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "se-provinces-fill",
        type: "fill",
        source: "se-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "se-provinces-outline",
        type: "line",
        source: "se-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- NORWEGIA ----------------
      safeAddSource(map, "no-provinces", {
        type: "geojson",
        data: "/data/no_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "no-provinces-fill",
        type: "fill",
        source: "no-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "no-provinces-outline",
        type: "line",
        source: "no-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- FINLANDIA ----------------
      safeAddSource(map, "fi-provinces", {
        type: "geojson",
        data: "/data/fi_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "fi-provinces-fill",
        type: "fill",
        source: "fi-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "fi-provinces-outline",
        type: "line",
        source: "fi-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- CZECHY ----------------
      safeAddSource(map, "cz-kraje", {
        type: "geojson",
        data: "/data/cz_kraje.geojson"
      });
      safeAddLayer(map, {
        id: "cz-kraje-fill",
        type: "fill",
        source: "cz-kraje",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "cz-kraje-outline",
        type: "line",
        source: "cz-kraje",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- SŁOWACJA ----------------
      safeAddSource(map, "sk-kraje", {
        type: "geojson",
        data: "/data/sk_kraje.geojson"
      });
      safeAddLayer(map, {
        id: "sk-kraje-fill",
        type: "fill",
        source: "sk-kraje",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "sk-kraje-outline",
        type: "line",
        source: "sk-kraje",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- AUSTRIA ----------------
      safeAddSource(map, "at-states", {
        type: "geojson",
        data: "/data/at_states.geojson"
      });
      safeAddLayer(map, {
        id: "at-states-fill",
        type: "fill",
        source: "at-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "at-states-outline",
        type: "line",
        source: "at-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- WĘGRY ----------------
      safeAddSource(map, "hu-regions", {
        type: "geojson",
        data: "/data/hu_regions.geojson"
      });
      safeAddLayer(map, {
        id: "hu-regions-fill",
        type: "fill",
        source: "hu-regions",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "hu-regions-outline",
        type: "line",
        source: "hu-regions",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

// ---------------- UKRAINA ----------------
safeAddSource(map, "ua-provinces", {
  type: "geojson",
  data: "/data/ua_provinces.geojson",
});
      safeAddLayer(map, {
        id: "ua-provinces-fill",
        type: "fill",
        source: "ua-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "ua-provinces-outline",
        type: "line",
        source: "ua-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- HOLANDIA ----------------
      safeAddSource(map, "nl-provinces", {
        type: "geojson",
        data: "/data/nl_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "nl-provinces-fill",
        type: "fill",
        source: "nl-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "nl-provinces-outline",
        type: "line",
        source: "nl-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- BELGIA ----------------
      safeAddSource(map, "be-lands", {
        type: "geojson",
        data: "/data/be_lands.geojson"
      });
      safeAddLayer(map, {
        id: "be-lands-fill",
        type: "fill",
        source: "be-lands",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "be-lands-outline",
        type: "line",
        source: "be-lands",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- SZWAJCARIA ----------------
      safeAddSource(map, "ch-states", {
        type: "geojson",
        data: "/data/ch_states.geojson"
      });
      safeAddLayer(map, {
        id: "ch-states-fill",
        type: "fill",
        source: "ch-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "ch-states-outline",
        type: "line",
        source: "ch-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- WŁOCHY ----------------
      safeAddSource(map, "it-provinces", {
        type: "geojson",
        data: "/data/it_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "it-provinces-fill",
        type: "fill",
        source: "it-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "it-provinces-outline",
        type: "line",
        source: "it-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- FRANCJA ----------------
      safeAddSource(map, "fr-provinces", {
        type: "geojson",
        data: "/data/fr_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "fr-provinces-fill",
        type: "fill",
        source: "fr-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "fr-provinces-outline",
        type: "line",
        source: "fr-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- HISZPANIA ----------------
      safeAddSource(map, "es-provinces", {
        type: "geojson",
        data: "/data/es_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "es-provinces-fill",
        type: "fill",
        source: "es-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "es-provinces-outline",
        type: "line",
        source: "es-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- PORTUGALIA ----------------
      safeAddSource(map, "pt-provinces", {
        type: "geojson",
        data: "/data/pt_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "pt-provinces-fill",
        type: "fill",
        source: "pt-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "pt-provinces-outline",
        type: "line",
        source: "pt-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- RUMUNIA ----------------
      safeAddSource(map, "ro-provinces", {
        type: "geojson",
        data: "/data/ro_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "ro-provinces-fill",
        type: "fill",
        source: "ro-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "ro-provinces-outline",
        type: "line",
        source: "ro-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- BUŁGARIA ----------------
      safeAddSource(map, "bg-provinces", {
        type: "geojson",
        data: "/data/bg_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "bg-provinces-fill",
        type: "fill",
        source: "bg-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "bg-provinces-outline",
        type: "line",
        source: "bg-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- CHORWACJA ----------------
      safeAddSource(map, "hr-provinces", {
        type: "geojson",
        data: "/data/hr_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "hr-provinces-fill",
        type: "fill",
        source: "hr-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "hr-provinces-outline",
        type: "line",
        source: "hr-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- GRECJA ----------------
      safeAddSource(map, "gr-provinces", {
        type: "geojson",
        data: "/data/gr_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "gr-provinces-fill",
        type: "fill",
        source: "gr-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "gr-provinces-outline",
        type: "line",
        source: "gr-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- TURCJA ----------------
      safeAddSource(map, "tr-provinces", {
        type: "geojson",
        data: "/data/tr_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "tr-provinces-fill",
        type: "fill",
        source: "tr-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "tr-provinces-outline",
        type: "line",
        source: "tr-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- LITWA ----------------
      safeAddSource(map, "lt-provinces", {
        type: "geojson",
        data: "/data/lt_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "lt-provinces-fill",
        type: "fill",
        source: "lt-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "lt-provinces-outline",
        type: "line",
        source: "lt-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- ŁOTWA ----------------
      safeAddSource(map, "lv-provinces", {
        type: "geojson",
        data: "/data/lv_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "lv-provinces-fill",
        type: "fill",
        source: "lv-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "lv-provinces-outline",
        type: "line",
        source: "lv-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- ESTONIA ----------------
      safeAddSource(map, "est-states", {
        type: "geojson",
        data: "/data/est_states.geojson"
      });
      safeAddLayer(map, {
        id: "est-states-fill",
        type: "fill",
        source: "est-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "est-states-outline",
        type: "line",
        source: "est-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- WIELKA BRYTANIA ----------------
      safeAddSource(map, "gb-counties", {
        type: "geojson",
        data: "/data/gb_counties.geojson"
      });
      safeAddLayer(map, {
        id: "gb-counties-fill",
        type: "fill",
        source: "gb-counties",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "gb-counties-outline",
        type: "line",
        source: "gb-counties",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- IRLANDIA ----------------
      safeAddSource(map, "ie-provinces", {
        type: "geojson",
        data: "/data/ie_provinces.geojson"
      });
      safeAddLayer(map, {
        id: "ie-provinces-fill",
        type: "fill",
        source: "ie-provinces",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.4 }
      });
      safeAddLayer(map, {
        id: "ie-provinces-outline",
        type: "line",
        source: "ie-provinces",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- ALBANIA ----------------
      safeAddSource(map, "al-states", {
        type: "geojson",
        data: "/data/al_states.geojson"
      });
      safeAddLayer(map, {
        id: "al-states-fill",
        type: "fill",
        source: "al-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "al-states-outline",
        type: "line",
        source: "al-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- SŁOWENIA ----------------
      safeAddSource(map, "sl-states", {
        type: "geojson",
        data: "/data/sl_states.geojson"
      });
      safeAddLayer(map, {
        id: "sl-states-fill",
        type: "fill",
        source: "sl-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "sl-states-outline",
        type: "line",
        source: "sl-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- BOŚNIA I HERCEGOWINA ----------------
      safeAddSource(map, "bih-states", {
        type: "geojson",
        data: "/data/bih_states.geojson"
      });
      safeAddLayer(map, {
        id: "bih-states-fill",
        type: "fill",
        source: "bih-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "bih-states-outline",
        type: "line",
        source: "bih-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- LIECHTENSTEIN ----------------
      safeAddSource(map, "lie-states", {
        type: "geojson",
        data: "/data/lie_states.geojson"
      });
      safeAddLayer(map, {
        id: "lie-states-fill",
        type: "fill",
        source: "lie-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "lie-states-outline",
        type: "line",
        source: "lie-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- LUKSEMBURG ----------------
      safeAddSource(map, "lux-states", {
        type: "geojson",
        data: "/data/lux_states.geojson"
      });
      safeAddLayer(map, {
        id: "lux-states-fill",
        type: "fill",
        source: "lux-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "lux-states-outline",
        type: "line",
        source: "lux-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- SERBIA ----------------
      safeAddSource(map, "srb-states", {
        type: "geojson",
        data: "/data/srb_states.geojson"
      });
      safeAddLayer(map, {
        id: "srb-states-fill",
        type: "fill",
        source: "srb-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "srb-states-outline",
        type: "line",
        source: "srb-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- KOSOWO ----------------
      safeAddSource(map, "kos-states", {
        type: "geojson",
        data: "/data/kos_states.geojson"
      });
      safeAddLayer(map, {
        id: "kos-states-fill",
        type: "fill",
        source: "kos-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "kos-states-outline",
        type: "line",
        source: "kos-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- MOŁDAWIA ----------------
      safeAddSource(map, "md-states", {
        type: "geojson",
        data: "/data/md_states.geojson"
      });
      safeAddLayer(map, {
        id: "md-states-fill",
        type: "fill",
        source: "md-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "md-states-outline",
        type: "line",
        source: "md-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- CZARNA GÓRA ----------------
      safeAddSource(map, "mne-states", {
        type: "geojson",
        data: "/data/mne_states.geojson"
      });
      safeAddLayer(map, {
        id: "mne-states-fill",
        type: "fill",
        source: "mne-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "mne-states-outline",
        type: "line",
        source: "mne-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- MACEDONIA ----------------
      safeAddSource(map, "mc-states", {
        type: "geojson",
        data: "/data/mc_states.geojson"
      });
      safeAddLayer(map, {
        id: "mc-states-fill",
        type: "fill",
        source: "mc-states",
        paint: { "fill-color": "#9ca3af", "fill-opacity": 0.2 }
      });
      safeAddLayer(map, {
        id: "mc-states-outline",
        type: "line",
        source: "mc-states",
        paint: { "line-color": "#000", "line-width": 1, "line-opacity": 0.9 }
      });

      // ---------------- CITIES ----------------
      safeAddSource(map, "cities", {
        type: "geojson",
        data: "/data/cities.geojson"
      });

      safeAddLayer(map, {
        id: "cities-capitals",
        type: "symbol",
        source: "cities",
        filter: ["==", ["get", "type"], "capital"],
        minzoom: 2,
        maxzoom: 22,
        layout: {
          "text-field": ["get", "name"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 2, 11, 4, 13, 6, 16, 9, 20],
          "text-font": ["Open Sans Bold"],
          "text-offset": [0, 0],
          "text-anchor": "center",
          "icon-allow-overlap": true,
          "text-allow-overlap": true
        },
        paint: {
          "text-color": "#111111",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5
        }
      });

      safeAddLayer(map, {
        id: "cities-others",
        type: "symbol",
        source: "cities",
        filter: ["!=", ["get", "type"], "capital"],
        minzoom: 5,
        maxzoom: 22,
        layout: {
          "text-field": ["get", "name"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 5, 16, 8, 16, 11, 16],
          "text-font": ["Arial Unicode MS Regular"],
          "text-offset": [0, 0],
          "text-anchor": "center",
          "icon-allow-overlap": true,
          "text-allow-overlap": true
        },
        paint: {
          "text-color": "#333333",
          "text-halo-color": "white",
          "text-halo-width": 1.6,
          "text-halo-blur": 0.4
        }
      });

      // --- PRZEJŚCIA GRANICZNE SZWAJCARII ---
      safeAddSource(map, "border_crossings", {
        type: "geojson",
        data: "/data/border_crossings.geojson"
      });

      safeAddLayer(map, {
        id: "border-crossings",
        type: "symbol",
        source: "border_crossings",
        minzoom: 4,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 13,
          "text-offset": [0, 0.6],
          "text-anchor": "top"
        },
        paint: {
          "text-color": "#111111",
          "text-halo-color": "white",
          "text-halo-width": 1.3
        }
      });

      // --- PORTY + TUNELE UK / IE / FR ---
      safeAddSource(map, "ports", {
        type: "geojson",
        data: "/data/ports.geojson"
      });

      safeAddLayer(map, {
        id: "ports",
        type: "symbol",
        source: "ports",
        minzoom: 4,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 13,
          "text-offset": [0, 0.6],
          "text-anchor": "top"
        },
        paint: {
          "text-color": "#111111",
          "text-halo-color": "white",
          "text-halo-width": 1.3
        }
      });

// ================================================
//   LABELKI NA POLYGONACH – WSPÓLNE DLA WSZYSTKICH
// ================================================

// (na razie najprościej: pokazuj ID regionu, np. PL6 / DE19)
const zoneMatchExpr: any[] = ["match", ["to-string", ["get", "id"]]];

Object.entries(REGION_ZONE_CODES).forEach(([rawId, zoneCode]) => {
  const variants = new Set<string>();

  variants.add(rawId);
  variants.add(rawId.toUpperCase());
  variants.add(rawId.toLowerCase());

  if (rawId.includes("-")) {
    const noDash = rawId.replace(/-/g, "");
    variants.add(noDash);
    variants.add(noDash.toUpperCase());
    variants.add(noDash.toLowerCase());
  }

  variants.forEach((v) => {
    zoneMatchExpr.push(v, zoneCode);
  });
});

// fallback: jak nie ma w mapie, pokazuj ID (dużymi literami)
zoneMatchExpr.push(["upcase", ["to-string", ["get", "id"]]]);

// wstawiamy etykiety POD labelami z basemap (miasta/porty),
// żeby nie zasłaniać nazw miast
const POLY_LABELS_BEFORE_ID = map.getLayer("label_other")
  ? "label_other"
  : (map.getStyle().layers || []).find((l: any) => l.type === "symbol")?.id;

const LABEL_LAYOUT: any = {
  "text-field": zoneMatchExpr,
  "text-size": 10,
  "text-font": ["Metropolis Semi Bold"],
  "text-allow-overlap": true,
  "text-ignore-placement": true,
  "text-anchor": "center",
  "symbol-placement": "point",
  "visibility": "visible",
  "text-optional": true
};

const LABEL_PAINT: any = {
  "text-color": "#111827",
  "text-halo-color": "rgba(255,255,255,0.92)",
  "text-halo-width": 1.8,
  "text-halo-blur": 0.2,
  "text-opacity": 0.95
};

const LABEL_MIN_ZOOM = 4;
const LABEL_MAX_ZOOM = 20;

REGION_FILL_LAYERS.forEach((fillLayerId) => {
  const sourceId = fillLayerId.replace(/-fill$/i, "");
  if (!map.getSource(sourceId)) return;

  const labelLayerId = `${fillLayerId}-labels`;
  if (map.getLayer(labelLayerId)) return;

  safeAddLayer(
    map,
    {
      id: labelLayerId,
      type: "symbol",
      source: sourceId,
      layout: LABEL_LAYOUT,
      paint: LABEL_PAINT,
      minzoom: LABEL_MIN_ZOOM,
      maxzoom: LABEL_MAX_ZOOM
    },
    POLY_LABELS_BEFORE_ID
  );
});


      // ----------------------------------------------------------
      // DROGI – cienkie czerwone linie (nad polygonami, pod napisami)
      // ----------------------------------------------------------
      enableRoadsThinRed(map);

      // ----------------------------------------------------------
      // GRANICE PAŃSTW – wzmocnienie + przeniesienie nad polygony/drogi
      // ----------------------------------------------------------
      const style = map.getStyle();
      const layers = style.layers || [];

      // znajdź pierwszą warstwę "symbol" (żeby granice były pod napisami)
      const firstSymbolId = layers.find((l: any) => l.type === "symbol")?.id;

      // złap warstwy granic (różne style mają różne nazwy)
      const boundaryLineLayers = layers.filter((layer: any) => {
        if (layer.type !== "line") return false;
        const id = (layer.id || "").toLowerCase();
        // zostawiamy "boundary", ale dokładamy też inne typowe słowa
        return id.includes("boundary") || id.includes("admin") || id.includes("border");
      });

      boundaryLineLayers.forEach((layer: any) => {
        try {
          // grubość zależna od zoom (ważne!)
          map.setPaintProperty(layer.id, "line-width", [
            "interpolate",
            ["linear"],
            ["zoom"],
            2, 0.9,
            4, 2.0,
            6, 3.4,
            8, 4.8,
            10, 6.2
          ]);

          map.setPaintProperty(layer.id, "line-color", "#0b1c2d");
          map.setPaintProperty(layer.id, "line-opacity", 0.95);

          // przenieś granice NAD fillami i drogami, ale POD napisami
          if (firstSymbolId && map.getLayer(firstSymbolId)) {
            map.moveLayer(layer.id, firstSymbolId);
          }
        } catch { }
      });

      // ==========================================================
      //             INTERAKCJA – KLIK
      // ==========================================================
      const handleClick = (e: any) => {
        const f = e.features && e.features[0];
        if (!f) return;

        const p: [number, number] | null = e?.lngLat ? [e.lngLat.lng, e.lngLat.lat] : null;
        applyPickedRegion(f, p);
      };

REGION_FILL_LAYERS.forEach((layerId) => {
  if (!map.getLayer(layerId)) return;

  map.on("mousemove", layerId, (e: any) => {
    try {
      const f = e?.features?.[0];
      map.getCanvas().style.cursor = f ? "pointer" : "";
    } catch (err) {
      console.error("[MAP] mousemove handler failed on layer:", layerId, err);
    }
  });

  map.on("mouseleave", layerId, () => {
    try {
      map.getCanvas().style.cursor = "";
    } catch (err) {
      console.error("[MAP] mouseleave handler failed on layer:", layerId, err);
    }
  });

  map.on("click", layerId, (e: any) => {
    try {
      handleClick(e);
    } catch (err) {
      console.error("[MAP] click handler failed on layer:", layerId, err);
    }
  });
});
    
      // Debug (Pages): confirm region layers exist
      try {
        console.log("[MAP] has pl-woj-fill:", !!map.getLayer("pl-woj-fill"));
        const added = REGION_FILL_LAYERS.filter((id) => !!map.getLayer(id));
        console.log("[MAP] region fill layers added:", added.length, "/", REGION_FILL_LAYERS.length, added);
      } catch (e) {
        console.warn("[MAP] debug check failed", e);
      }
});

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const syncMapSize = () => {
      try {
        map.resize();
      } catch {}
    };

    const t1 = window.setTimeout(syncMapSize, 0);
    const t2 = window.setTimeout(syncMapSize, 120);

    window.addEventListener("resize", syncMapSize);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("resize", syncMapSize);
    };
  }, [isDemo]);

  // ================================================
  //          KOLOROWANIE REGIONÓW START / CEL
  // ================================================
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const originCode = (origin?.properties?.["id"] as string) || "";
    const destCode = (dest?.properties?.["id"] as string) || "";

    const colorExpr: any = [
      "case",
      ["==", ["get", "id"], originCode], "#22c55e",
      ["==", ["get", "id"], destCode], "#ff0000",
      "#9ca3af"
    ];

    REGION_FILL_LAYERS.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "fill-color", colorExpr);
        map.setPaintProperty(layerId, "fill-opacity", 0.4);
      }
    });
  }, [origin, dest]);

// ==============================================
//  DE: wyznaczanie NAZWY LANDU dla klikniętego PLZ2
//  (TYLKO do display w UI, zero wpływu na logikę)
// ==============================================
const bboxCenter = (geom: any): [number, number] | null => {
  if (!geom?.coordinates) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const walk = (coords: any) => {
    if (!coords) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      const x = coords[0];
      const y = coords[1];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    for (const c of coords) walk(c);
  };

  walk(geom.coordinates);
  if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) return null;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
};

const pointInRing = (pt: [number, number], ring: number[][]): boolean => {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
const intersect =
  ((yi > y) !== (yj > y)) &&
  (x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

const pointInPolygon = (pt: [number, number], geom: any): boolean => {
  if (!geom) return false;

  if (geom.type === "Polygon") {
    const rings = geom.coordinates as number[][][];
    if (!rings?.length) return false;
    if (!pointInRing(pt, rings[0])) return false;
    for (let i = 1; i < rings.length; i++) {
      if (pointInRing(pt, rings[i])) return false;
    }
    return true;
  }

  if (geom.type === "MultiPolygon") {
    const polys = geom.coordinates as number[][][][];
    for (const poly of polys) {
      const rings = poly;
      if (!rings?.length) continue;
      if (!pointInRing(pt, rings[0])) continue;
      let inHole = false;
      for (let i = 1; i < rings.length; i++) {
        if (pointInRing(pt, rings[i])) {
          inHole = true;
          break;
        }
      }
      if (!inHole) return true;
    }
    return false;
  }

  return false;
};

const resolveGermanStateName = (feature: any): string | null => {
  const p: any = feature?.properties || {};
  const id = String(p.id || "");
  if (!id) return null;

  const cached = dePlz2ToStateCache.current[id];
  if (cached) return cached;
  if (!deStates?.features?.length) return null;

  const center = bboxCenter(feature.geometry);
  if (!center) return null;

  for (const st of deStates.features) {
    if (pointInPolygon(center, st.geometry)) {
      const name = String(st?.properties?.name || "").trim();
      if (name) {
        dePlz2ToStateCache.current[id] = name;
        return name;
      }
    }
  }
  return null;
};

const stripPlItPrefix = (label: string): string => {
  // usuwa "6xx — " albo "3xxxx — " (obsługuje też -, – i —)
  return String(label || "")
    .replace(/^\s*\d(?:x{2}|x{4})\s*[–—-]\s*/i, "")
    .trim();
};

  // ================================================
  //          UI — WYŚWIETLANIE REGIONÓW
  // ================================================
  const originId = origin?.properties?.["id"] as string | undefined;
  const originZone = displayZoneCode(originId);
  const originName =
    (origin?.properties?.["name"] as string | undefined) ||
    t("clickRegion");

const originIdUpper = originId?.toUpperCase() || "";

const originDisplayName =
  originIdUpper.startsWith("DE")
    ? (resolveGermanStateName(origin) || originName)
    : (originIdUpper.startsWith("PL") || originIdUpper.startsWith("IT"))
      ? stripPlItPrefix(originName)
      : originName;

  const destId = dest?.properties?.["id"] as string | undefined;
  const destZone = displayZoneCode(destId);
  const destName =
    (dest?.properties?.["name"] as string | undefined) ||
    t("clickRegion");

const destIdUpper = destId?.toUpperCase() || "";

const destDisplayName =
  destIdUpper.startsWith("DE")
    ? (resolveGermanStateName(dest) || destName)
    : (destIdUpper.startsWith("PL") || destIdUpper.startsWith("IT"))
      ? stripPlItPrefix(destName)
      : destName;

// ================================================
//          AUTH ACTIONS
// ================================================
const openLogin = () => {
  setAuthMode("login");
  setAuthOpen(true);
};

const openRegister = () => {
  setRegisterStartTrial(false);
  setAuthMode("register");
  setAuthOpen(true);
};

const openStartTrial = () => {
  setRegisterStartTrial(true);
  setAuthMode("register");
  setAuthOpen(true);
};

const doLogout = () => {
  clearToken();
  setMe({
    isLoggedIn: false,
    plan: "DEMO",
    trialActive: false,
    subscriptionActive: false
  });
};

const submitAuth = async (
  email: string,
  password: string,
  acceptTerms?: boolean,
  acceptPrivacy?: boolean
) => {
  setAuthBusy(true);
  try {
    const trimmedEmail = email.trim();

    const out =
      authMode === "register"
        ? await apiRegister(
            trimmedEmail,
            password,
            registerStartTrial,
            !!acceptTerms,
            !!acceptPrivacy
          )
        : await apiLogin(trimmedEmail, password);

    setToken(out.token);
    const m = await apiMe(out.token);
    setMe(m);
    setAuthOpen(false);
    setRegisterStartTrial(false);
  } catch (e: any) {
    window.alert(e?.message || t("authErrGeneric"));
  } finally {
    setAuthBusy(false);
  }
};

// ================================================
//          RENDER
// ================================================
return (
  <div
    className="app-root"
    style={{
      padding: 12,
      paddingBottom: 14,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      minHeight: "100vh",
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
    }}
  >
   <div className="app-header">
  <img
    className="app-logo"
    src="/logo.png"
    alt="MY TSL MAP"
  />

  <h1 className="app-title">
    {t("appTitle")}
  </h1>

  <div className="app-header-right" style={{ display: "flex", alignItems: "center", gap: 10 }}>
{!isMobileLayout && (
  <div
    style={{
      fontSize: 12,
      color: "#6b7280",
      textAlign: "right",
      lineHeight: 1.1
    }}
  >
    <div>{me.isLoggedIn ? me.email : "DEMO"}</div>
    <div className={`plan-badge ${me.plan === "FULL" ? "plan-full" : "plan-demo"}`}>
      {me.plan}
    </div>
  </div>
)}
        {!me.isLoggedIn ? (
          <button
            type="button"
            onClick={openLogin}
            className="ui-btn ui-btn--primary ui-btn--sm"
            style={{ whiteSpace: "nowrap" }}
          >
            {t("authLoginBtn")}
          </button>
        ) : (
          <button
            type="button"
            onClick={doLogout}
            className="ui-btn ui-btn--danger ui-btn--sm"
            style={{ whiteSpace: "nowrap" }}
          >
            {t("authLogoutBtn")}
          </button>
        )}


        {showHeaderButton && (
          <button
            type="button"
            onClick={onHeaderButtonClick}
            className="ui-btn ui-btn--success ui-btn--sm"
            style={{ whiteSpace: "nowrap" }}
          >
            {headerButtonLabel}
          </button>
        )}

        <LangDropdown lang={lang} onChange={setLang} />
      </div>
    </div>
        
{/* MAPA + PANEL BOCZNY */}
<div
  style={{
    display: "flex",
    gap: 12,
    width: "calc(100% - 24px)",
    margin: "0 auto",
    alignItems: "stretch",
    flexDirection: !isDemo && isCompactLayout ? "column" : "row"
  }}
>
  {/* LEWA KOLUMNA — MAPA */}
  <div
    className="map-shell"
    style={{
      position: "relative",
      flex:
        isDemo
          ? "1 1 100%"
          : !isDemo && isCompactLayout
            ? "1 1 auto"
            : "0 0 80%",
      width: isDemo || isCompactLayout ? "100%" : undefined,
      height:
  !isDemo && isCompactLayout
    ? "46vh"
    : isShortDesktop
    ? "52vh"
    : "60vh",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: "0 2px 10px rgba(0,0,0,0.10)"
    }}
  >
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0
      }}
    />

    {/* POST-TRIAL CTA (no close button) */}
    {showPostTrialCta && (
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          zIndex: 25000,
          pointerEvents: "none"
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            maxWidth: 520,
            margin: "0 auto",
            background: "rgba(255,255,255,0.96)",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
            backdropFilter: "blur(6px)"
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 900 }}>
                {t("postTrialCtaTitle")}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#4b5563",
                  marginTop: 4,
                  lineHeight: 1.25
                }}
              >
                {t("postTrialCtaText")}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button
              type="button"
              onClick={goToCheckout}
              disabled={checkoutBusy}
              className="ui-btn ui-btn--primary ui-btn--lg ui-btn--block"
              style={{ width: "100%", whiteSpace: "nowrap" }}
            >
              {t("postTrialCtaButton")}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* DEMO CTA (no close button) */}
    {showDemoCta && (
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          zIndex: 25000,
          pointerEvents: "none"
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            maxWidth: 520,
            margin: "0 auto",
            background: "rgba(255,255,255,0.96)",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
            backdropFilter: "blur(6px)"
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 900 }}>
                {t("demoCtaTitle")}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#4b5563",
                  marginTop: 4,
                  lineHeight: 1.25
                }}
              >
                {t("demoCtaText")}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={openStartTrial}
              className="ui-btn ui-btn--primary ui-btn--lg"
              style={{ flex: 1, minWidth: 160 }}
            >
              {t("authStartFreeTrial")}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>

  {/* PRAWA KOLUMNA — SIDE MENU (DESKTOP / TABLET ONLY) */}
  {!isDemo && !isMobileLayout && (
    <div
      style={{
flex: isCompactLayout
  ? "1 1 auto"
  : isNarrowShortDesktop
  ? "0 0 15.5%"
  : isShortDesktop
  ? "0 0 18%"
  : "0 0 20%",
width: isCompactLayout ? "100%" : undefined,
minWidth: isCompactLayout ? 0 : isNarrowShortDesktop ? 220 : isShortDesktop ? 248 : 280,
maxWidth: isCompactLayout ? "none" : isNarrowShortDesktop ? 248 : isShortDesktop ? 290 : 360,
height:
  isCompactLayout
    ? "auto"
    : isShortDesktop
    ? "52vh"
    : "60vh"
      }}
    >
      <SideMenu
        distanceKm={price?.distanceKm ?? 0}
        isShortDesktop={isShortDesktop || isNarrowShortDesktop}
        isNarrowShortDesktop={isNarrowShortDesktop}
        vehicleType={vehicleType}
        onVehicleChange={setVehicleType}
        extraOptions={extraOptions}
        onToggleExtra={(id) =>
          setExtraOptions((prev) => ({
            ...prev,
            [id]: !prev[id]
          }))
        }
        extraUserAmounts={extraUserAmounts}
        onChangeExtraUserAmount={(id, value) =>
          setExtraUserAmounts((prev) => ({ ...prev, [id]: value }))
        }
        deadheadEnabled={deadheadEnabled}
        onToggleDeadhead={() => setDeadheadEnabled((v) => !v)}
        deadheadKmRaw={deadheadKmRaw}
        onChangeDeadheadKmRaw={setDeadheadKmRaw}
        price={price}
        routeInfo={routeInfo}
        onAdminClick={effectivePlan === "FULL" ? requestAdminOpen : undefined}
        adminNeedsSetup={showAdminOnboarding}
        alwaysOpen={true}
        t={t}
      />
    </div>
  )}
</div>

      {/* PANEL DANYCH */}
      <div
        style={{
          borderRadius: 16,
          padding: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          background: "white",
        }}
      >
        {showSmartRouteBox && (
          <div
            style={{
              marginBottom: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 10,
              background: "rgba(255,255,255,0.96)"
            }}
          >
<div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 8
  }}
>
  <div
    style={{
      fontSize: 12,
      fontWeight: 900,
      color: "#111827",
      letterSpacing: "0.2px"
    }}
  >
    {t("smartRouteTitle")}
  </div>

  {canUseSmartScreenshotOcr && smartScreenshotStatus && (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: smartScreenshotBusy ? "#92400e" : "#374151",
        whiteSpace: "nowrap"
      }}
    >
      {smartScreenshotStatus}
    </div>
  )}
</div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center"
              }}
            >
              {canUseSmartScreenshotOcr && (
                <button
                  type="button"
                  className="ui-btn ui-btn--sm"
                  aria-label={smartScreenshotTooltipText}
                  aria-describedby="smart-screenshot-tooltip"
                  style={{
                    position: "relative",
                    flex: isMobileLayout ? "1 1 100%" : "0 0 auto",
                    width: isMobileLayout ? "100%" : 40,
                    minWidth: isMobileLayout ? 0 : 40,
                    height: 34,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 10,
                    padding: 0,
                    opacity: smartScreenshotBusy ? 0.7 : 1
                  }}
                  disabled={smartScreenshotBusy}
                  onClick={() => smartScreenshotInputRef.current?.click()}
                  onMouseEnter={() => setSmartScreenshotTooltipVisible(true)}
                  onMouseLeave={() => setSmartScreenshotTooltipVisible(false)}
                  onFocus={() => setSmartScreenshotTooltipVisible(true)}
                  onBlur={() => setSmartScreenshotTooltipVisible(false)}
                >
                  {!isMobileLayout && smartScreenshotTooltipVisible && (
                    <span
                      id="smart-screenshot-tooltip"
                      role="tooltip"
                      style={{
                        position: "absolute",
                        left: 0,
                        bottom: "calc(100% + 8px)",
                        zIndex: 30,
                        width: 280,
                        maxWidth: "min(280px, 80vw)",
                        padding: "8px 10px",
                        borderRadius: 8,
                        background: "#111827",
                        color: "white",
                        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.18)",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.3,
                        whiteSpace: "normal",
                        pointerEvents: "none"
                      }}
                    >
                      {smartScreenshotTooltipText}
                    </span>
                  )}
                  {smartScreenshotBusy ? "…" : "📷"}
                </button>
              )}
<input
  ref={smartOriginRef}
  value={smartOriginInput}
  onChange={(e) => setSmartOriginInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      smartDestRef.current?.focus();
    }
  }}
  placeholder={t("smartOriginPlaceholder")}
  style={{
flex: isMobileLayout ? "1 1 100%" : "1 1 260px",
minWidth: isMobileLayout ? 0 : 180,
width: isMobileLayout ? "100%" : undefined,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  }}
/>

<input
  ref={smartDestRef}
  value={smartDestInput}
  onChange={(e) => setSmartDestInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      smartPalletsRef.current?.focus();
    }
  }}
  placeholder={t("smartDestPlaceholder")}
  style={{
flex: isMobileLayout ? "1 1 100%" : "1 1 260px",
minWidth: isMobileLayout ? 0 : 180,
width: isMobileLayout ? "100%" : undefined,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  }}
/>

<input
  ref={smartPalletsRef}
  value={smartPalletsInput}
  onChange={(e) => {
    const next = e.target.value.replace(",", ".");
    if (/^\d*([.]\d*)?$/.test(next)) {
      setSmartPalletsInput(next);
    }
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      smartWeightRef.current?.focus();
    }
  }}
  placeholder={t("smartPalletsPlaceholder")}
  inputMode="numeric"
  style={{
flex: isMobileLayout ? "1 1 calc(50% - 4px)" : "0 1 92px",
width: isMobileLayout ? "calc(50% - 4px)" : 92,
minWidth: isMobileLayout ? 0 : 92,    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  }}
/>

<input
  ref={smartWeightRef}
  value={smartWeightInput}
  onChange={(e) => {
    const next = e.target.value.replace(",", ".");
    if (/^\d*([.]\d*)?$/.test(next)) {
      setSmartWeightInput(next);
    }
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      smartSubmitBtnRef.current?.focus();
    }
  }}
  placeholder={t("smartWeightPlaceholder")}
  inputMode="decimal"
  style={{
flex: isMobileLayout ? "1 1 calc(50% - 4px)" : "0 1 130px",
width: isMobileLayout ? "calc(50% - 4px)" : 130,
minWidth: isMobileLayout ? 0 : 130,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  }}
/>

              <button
                ref={smartSubmitBtnRef}
                type="button"
                className="ui-btn ui-btn--primary ui-btn--sm"
                style={{
flex: isMobileLayout ? "1 1 100%" : "0 0 auto",
width: isMobileLayout ? "100%" : undefined,
whiteSpace: "nowrap",
justifyContent: "center",
                  borderRadius: 10,
                  padding: "8px 14px"
                }}
                onClick={handleSmartRouteSubmit}
              >
                {t("smartSubmit")}
              </button>

              <input
                ref={smartScreenshotInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleSmartScreenshotFile}
              />
            </div>
          </div>
        )}

{!isDemo && isMobileLayout && (
  <div style={{ width: "calc(100% - 24px)", margin: "12px auto 0" }}>
    <SideMenu
      distanceKm={price?.distanceKm ?? 0}
      vehicleType={vehicleType}
      onVehicleChange={setVehicleType}
      extraOptions={extraOptions}
      onToggleExtra={(id) =>
        setExtraOptions((prev) => ({
          ...prev,
          [id]: !prev[id]
        }))
      }
      extraUserAmounts={extraUserAmounts}
      onChangeExtraUserAmount={(id, value) =>
        setExtraUserAmounts((prev) => ({ ...prev, [id]: value }))
      }
      deadheadEnabled={deadheadEnabled}
      onToggleDeadhead={() => setDeadheadEnabled((v) => !v)}
      deadheadKmRaw={deadheadKmRaw}
      onChangeDeadheadKmRaw={setDeadheadKmRaw}
      price={price}
      routeInfo={routeInfo}
      onAdminClick={effectivePlan === "FULL" ? requestAdminOpen : undefined}
      adminNeedsSetup={showAdminOnboarding}
      alwaysOpen={true}
      t={t}
    />
  </div>
)}

        {/* START & CEL + WYNIK + PODSUMOWANIE */}
<div
  style={{
    marginBottom: 12,
    maxWidth: 1300,
    marginLeft: "auto",
    marginRight: "auto"
  }}
>  
<div
  style={{
    display: "grid",
    gridTemplateColumns: isMobileLayout ? "1fr" : "240px 260px 240px",
    justifyContent: "space-between",
    alignItems: "start",
    columnGap: 24,
    rowGap: isMobileLayout ? 12 : 0,
    width: "100%"
  }}
>
<div
  style={{
    width: isMobileLayout ? "100%" : 240,
    display: "flex",
    flexDirection: "column",
    gap: 8
  }}
>
<div
  style={{
    flex: 1,
    borderRadius: 12,
    border: "1px solid rgba(34, 197, 94, 0.65)",
    padding: 8,
    background: "#fafafa"
  }}
>
  <div style={{ fontSize: 11, color: "#111827" }}>{t("start")}</div>
  <div style={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>{originZone || "—"}</div>
  <div style={{ fontSize: 11, color: "#111827" }}>{originDisplayName}</div>
</div>

<div
  style={{
    flex: 1,
    borderRadius: 12,
    border: "1px solid #B11226",
    padding: 8,
    background: "#fafafa"
  }}
>
  <div style={{ fontSize: 11, color: "#111827" }}>{t("destination")}</div>
  <div style={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>{destZone || "—"}</div>
  <div style={{ fontSize: 11, color: "#111827" }}>{destDisplayName}</div>
</div>
            </div>

{/* ŚRODKOWA KOLUMNA: WYNIK */}
<div
style={{
  width: isMobileLayout ? "100%" : 260,
  maxWidth: isMobileLayout ? "100%" : 260,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "10px 12px",
  background: "#fafafa",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "stretch",
  gap: 8,
  alignSelf: "start",
  boxSizing: "border-box"
}}
>
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: 58
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "center",
        gap: 6,
        color: "#111827"
      }}
    >
      <span
        style={{
          fontSize: 34,
          lineHeight: 1,
          fontWeight: 800,
          letterSpacing: "-0.02em"
        }}
      >
        {price ? price.total.toFixed(2) : "—"}
      </span>

      <span
        style={{
          fontSize: 20,
          lineHeight: 1,
          fontWeight: 700
        }}
      >
        €
      </span>
    </div>

    <div
      style={{
        marginTop: 6,
        fontSize: 12,
        color: "#4b5563"
      }}
    >
      {price?.pricingMode
        ? getPricingModeLabel(price, (dest?.properties?.["id"] as string) || "", t)
        : "—"}
    </div>

    {showRatesHint && (
      <button
        type="button"
        onClick={requestAdminOpen}
        style={{
          marginTop: 8,
          border: "none",
          background: "transparent",
          padding: 0,
          fontSize: 12,
          lineHeight: 1.35,
          color: "#b45309",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: 700
        }}
      >
        {t("ratesHint")}
      </button>
    )}
  </div>

  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <button
      type="button"
      onClick={handleClearSelection}
      className="ui-btn ui-btn--sm ui-btn--danger"
      style={{
        borderRadius: 999,
        fontSize: 11,
        padding: "2px 6px",
        marginTop: 2
      }}
    >
      {t("clearSelection")}
    </button>
  </div>
</div>

{/* PRAWA KOLUMNA: PODSUMOWANIE */}
<div
style={{
  width: isMobileLayout ? "100%" : 240,
  maxWidth: isMobileLayout ? "100%" : 240,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: "10px 12px",
  background: "#fafafa",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignSelf: "stretch",
  boxSizing: "border-box",
}}
>
<div
  style={{
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    lineHeight: 1.1,
    marginBottom: 4,
    overflow: "hidden"
  }}
>
  <>
    <span>{smartLabels.origin || (origin ? originDisplayName : t("start"))}</span>
    <span> → </span>
    <br />
    <span>{smartLabels.dest || (dest ? destDisplayName : t("destination"))}</span>
  </>
</div>
  <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.10 }}>
    {t("distanceLabel")}: {routeInfo.distanceKm ? routeInfo.distanceKm.toFixed(1) + " km" : "—"}
  </div>

  <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.10 }}>
    {t("truckEtaLabel")}: {truckEtaText || "—"}
  </div>

  <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.10 }}>
    {t("vehicleType")}:{" "}
    {vehicleType === "bus"
      ? t("vehicleVan")
      : vehicleType === "solo"
      ? t("vehicleSolo")
      : vehicleType === "trailer"
      ? t("vehicleTrailer")
      : "—"}
  </div>

  <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.10 }}>
    {t("loadLabel")}: {smartPalletsInput || "—"} {t("palletShort")} / {smartWeightInput || "—"} t
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      marginTop: 6
    }}
  >
    <div style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
      {t("priceLabel")}:{" "}
      <span style={{ fontWeight: 850, fontSize: 14, color: "#111827" }}>
        {price ? `${price.total.toFixed(2)} €` : "—"}
      </span>
    </div>

<div
  style={{
    position: "sticky",
    bottom: 0,
    display: "flex",
    justifyContent: "flex-end",
    background: "#fafafa",
    paddingTop: 6,
    paddingBottom: 2,
    zIndex: 5
  }}
>
  <button
    type="button"
    className="ui-btn ui-btn--primary ui-btn--sm"
    style={{
      padding: "5px 9px",
      fontSize: 11,
      lineHeight: 1,
      minHeight: 26,
      flexShrink: 0
    }}
    onClick={() => {
      const vehicleLabel =
        vehicleType === "bus"
          ? t("vehicleVan")
          : vehicleType === "solo"
          ? t("vehicleSolo")
          : vehicleType === "trailer"
          ? t("vehicleTrailer")
          : "—";

      const text =
`${(smartLabels.origin || (origin ? originDisplayName : t("start")))} → ${(smartLabels.dest || (dest ? destDisplayName : t("destination")))}\n` +
`${t("distanceLabel")}: ${routeInfo.distanceKm ? routeInfo.distanceKm.toFixed(1) + " km" : "—"}\n` +
`${t("truckEtaLabel")}: ${truckEtaText || "—"}\n` +
`${t("vehicleType")}: ${vehicleLabel}\n` +
`${t("loadLabel")}: ${smartPalletsInput || "—"} ${t("palletShort")} / ${smartWeightInput || "—"} t\n` +
`${t("priceLabel")}: ${price ? `${price.total.toFixed(2)} €` : "—"}`;

      window.location.href = `mailto:?subject=${encodeURIComponent(t("quoteEmailSubject"))}&body=${encodeURIComponent(text)}`;
    }}
  >
    {t("sendLabel")}
  </button>
</div>
  </div>
</div>
</div>
        </div>
</div>

      {/* GLOBAL UI ALERT (replaces window.alert) */}
      <AlertModal
        isOpen={!!uiAlertMsg}
        message={uiAlertMsg || ""}
        onClose={() => setUiAlertMsg(null)}
      />

      <PinModal
        mode={pinMode}
        isOpen={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        onSubmit={handlePinSubmit}
        t={t}
      />

      <AuthModal
        isOpen={authOpen}
        mode={authMode}
        busy={authBusy}
        onClose={() => setAuthOpen(false)}
        onSubmit={submitAuth}
        t={t}
        lang={lang}
      />

      {/* ADMIN OVERLAY (opened from burger) */}
      {adminOpen && (
        <div
          onClick={() => setAdminOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            background: "rgba(0,0,0,0.25)"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              width: 460,
              maxWidth: "92vw",
              background: "white",
              borderLeft: "1px solid #e5e7eb",
              padding: 14,
              overflow: "auto"
            }}
          >
<div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 6
  }}
>
  <button
    type="button"
    onClick={() => setAdminOpen(false)}
    style={{
      padding: "6px 10px",
      borderRadius: 10,
      border: "1px solid #e5e7eb",
      background: "white",
      cursor: "pointer"
    }}
  >
    ✕
  </button>
</div>

<AdminPanel
  onChangePin={requestPinChange}
  userKey={me?.isLoggedIn ? me.email : undefined}
  onRatesSaved={async () => {
    await refreshMeState();
  }}
/>
          </div>
        </div>
      )}


      {/* FOOTER */}
      <footer className="app-footer">
        <div className="app-footer__copyright">
          © MY TSL MAP <span>owned by</span> TIME 2 MOVE ·{" "}
          <a href="mailto:support@mytslmap.com">support@mytslmap.com</a>
        </div>
      </footer>

    </div>
  );
}

interface SideMenuProps {
  distanceKm: number;
  vehicleType: "bus" | "solo" | "trailer";
  onVehicleChange: (v: "bus" | "solo" | "trailer") => void;
  extraOptions: Record<ExtraOptionId, boolean>;
  onToggleExtra: (id: ExtraOptionId) => void;

  extraUserAmounts: Record<ExtraOptionId, string>;
  onChangeExtraUserAmount: (id: ExtraOptionId, value: string) => void;

  deadheadEnabled: boolean;
  onToggleDeadhead: () => void;

  deadheadKmRaw: string;
  onChangeDeadheadKmRaw: (value: string) => void;

  price: PriceResult | null;
  routeInfo: RouteInfo;
  onAdminClick?: () => void;
  adminNeedsSetup?: boolean;
  alwaysOpen?: boolean;
  isShortDesktop?: boolean;
  isNarrowShortDesktop?: boolean;

  t: (key: TKey) => string;
}

function SideMenu({
  distanceKm,
  isShortDesktop = false,
  isNarrowShortDesktop = false,
  vehicleType,
  onVehicleChange,
  extraOptions,
  onToggleExtra,

  extraUserAmounts,
  onChangeExtraUserAmount,

  deadheadEnabled,
  onToggleDeadhead,

  deadheadKmRaw,
  onChangeDeadheadKmRaw,

  price,
  routeInfo,
  onAdminClick,
  adminNeedsSetup = false,
  alwaysOpen = false,
  t
}: SideMenuProps) {

  const [isOpen, setIsOpen] = useState(false);
  const menuOpen = alwaysOpen || isOpen;

const compactInputStyle = isNarrowShortDesktop
  ? { width: 60, padding: "1px 4px", fontSize: 9 as const }
  : isShortDesktop
  ? { width: 78, padding: "3px 6px", fontSize: 11 as const }
  : { width: 90, padding: "4px 8px", fontSize: 12 as const };

const compactSmallTextStyle = isNarrowShortDesktop
  ? { color: "#6b7280", fontSize: 9 as const }
  : isShortDesktop
  ? { color: "#6b7280", fontSize: 11 as const }
  : { color: "#6b7280", fontSize: 12 as const };

const compactInlineRowStyle = isNarrowShortDesktop
  ? { display: "flex", alignItems: "center", gap: 2, marginTop: 0 }
  : isShortDesktop
  ? { display: "flex", alignItems: "center", gap: 4, marginTop: 2 }
  : { display: "flex", alignItems: "center", gap: 6, marginTop: 4 };

const compactSectionMarginTop = isNarrowShortDesktop ? 1 : isShortDesktop ? 3 : 10;
const compactAdminMarginTop = isNarrowShortDesktop ? 0 : isShortDesktop ? 2 : 12;

  // Enter: blur current input -> focus next input in menu
  function focusNextMenuInput(current: HTMLInputElement) {
    const root = current.closest('[data-menu-root="1"]') as HTMLElement | null;
    if (!root) return;

    const inputs = Array.from(
      root.querySelectorAll<HTMLInputElement>('input[data-menu-input="1"]:not([disabled])')
    );

    const idx = inputs.indexOf(current);
    if (idx >= 0 && idx + 1 < inputs.length) {
      inputs[idx + 1].focus();
      inputs[idx + 1].select?.();
    }
  }

  function onEnterNext(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.currentTarget as HTMLInputElement;
      el.blur();
      focusNextMenuInput(el);
    }
  }

return (
  <>
    {!alwaysOpen && (
      <button
        className="menu-toggle-button"
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span />
        <span />
        <span />
      </button>
    )}

    <div
      className={`side-menu ${menuOpen ? "side-menu--open" : ""}`}
      data-menu-root="1"
    >
      <div className="side-menu__section">
        <div className="side-menu__section-title">{t("vehicleType")}</div>
        <div className="vehicle-buttons">
          <button
            type="button"
            onClick={() => onVehicleChange("bus")}
            className={
              "vehicle-button" +
              (vehicleType === "bus" ? " vehicle-button--active" : "")
            }
          >
            {t("vehicleVan")}
          </button>
          <button
            type="button"
            onClick={() => onVehicleChange("solo")}
            className={
              "vehicle-button" +
              (vehicleType === "solo" ? " vehicle-button--active" : "")
            }
          >
            {t("vehicleSolo")}
          </button>
          <button
            type="button"
            onClick={() => onVehicleChange("trailer")}
            className={
              "vehicle-button" +
              (vehicleType === "trailer" ? " vehicle-button--active" : "")
            }
          >
            {t("vehicleTrailer")}
          </button>
        </div>
      </div>

      <div className="side-menu__section">
        <div className="side-menu__section-title">{t("extraOptions")}</div>
        <div className="extra-options-list">
          {EXTRA_OPTIONS.filter((o) => o.id !== "lift").map((opt) => {
            const enabled = !!extraOptions[opt.id];
            const userVal = extraUserAmounts[opt.id] ?? "";

            return (
              <div
                key={opt.id}
                className="extra-option"
                onClick={() => onToggleExtra(opt.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="extra-option__text">
                  <span>{t(opt.labelKey)}</span>

                  <div
                    style={compactInlineRowStyle}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      data-menu-input="1"
                      value={enabled ? userVal : ""}
                      disabled={!enabled}
                      onChange={(e) => {
                        const next = e.target.value.replace(",", ".");
                        if (/^\d*([.]\d*)?$/.test(next)) {
                          onChangeExtraUserAmount(opt.id, next);
                        }
                      }}
                      onKeyDown={onEnterNext}
                      onBlur={() => {
                        const v = (extraUserAmounts[opt.id] ?? "").trim();
                        if (v === "" || v === ".") return;
                        const n = Number(v);
                        onChangeExtraUserAmount(
                          opt.id,
                          Number.isFinite(n) ? String(n) : ""
                        );
                      }}
style={{
  ...compactInputStyle,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  opacity: enabled ? 1 : 0.5
}}
                    />
<small style={compactSmallTextStyle}>
  {opt.type === "perKm" ? ` ${t("perKm")}` : " €"}
</small>
                  </div>
                </div>

                <div
                  className="toggle-switch"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExtra(opt.id);
                  }}
                >
                  <input type="checkbox" checked={enabled} readOnly />
                  <span className="toggle-slider" />
                </div>
              </div>
            );
          })}

          {/* DOLOT (deadhead km) */}
          <div
            className="extra-option"
            onClick={onToggleDeadhead}
            style={{ cursor: "pointer" }}
          >
            <div className="extra-option__text">
              <span>{t("deadhead")}</span>

              <div
                style={compactInlineRowStyle}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  data-menu-input="1"
                  value={deadheadEnabled ? deadheadKmRaw : ""}
                  disabled={!deadheadEnabled}
                  onChange={(e) => {
                    const next = e.target.value.replace(",", ".");
                    if (/^\d*([.]\d*)?$/.test(next)) onChangeDeadheadKmRaw(next);
                  }}
                  onKeyDown={onEnterNext}
                  onBlur={() => {
                    const v = (deadheadKmRaw ?? "").trim();
                    if (v === "" || v === ".") return;
                    const n = Number(v);
                    onChangeDeadheadKmRaw(
                      Number.isFinite(n) ? String(Math.max(0, n)) : ""
                    );
                  }}
                  placeholder={t("deadheadPlaceholder")}
style={{
  ...compactInputStyle,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  opacity: deadheadEnabled ? 1 : 0.5
}}
                />
<small style={compactSmallTextStyle}> km</small>
              </div>
            </div>

            <div
              className="toggle-switch"
              onClick={(e) => {
                e.stopPropagation();
                onToggleDeadhead();
              }}
            >
              <input type="checkbox" checked={deadheadEnabled} readOnly />
              <span className="toggle-slider" />
            </div>
          </div>

          {/* WINDA (fixed amount) */}
          <div
            className="extra-option"
            onClick={() => onToggleExtra("lift")}
            style={{ cursor: "pointer" }}
          >
            <div className="extra-option__text">
              <span>{t("optLift")}</span>

              <div
                style={compactInlineRowStyle}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  inputMode="decimal"
                  data-menu-input="1"
                  value={extraOptions.lift ? (extraUserAmounts.lift ?? "") : ""}
                  disabled={!extraOptions.lift}
                  onChange={(e) => {
                    const next = e.target.value.replace(",", ".");
                    if (/^\d*([.]\d*)?$/.test(next)) {
                      onChangeExtraUserAmount("lift", next);
                    }
                  }}
                  onKeyDown={onEnterNext}
                  onBlur={() => {
                    const v = (extraUserAmounts.lift ?? "").trim();
                    if (v === "" || v === ".") return;
                    const n = Number(v);
                    onChangeExtraUserAmount(
                      "lift",
                      Number.isFinite(n) ? String(Math.max(0, n)) : ""
                    );
                  }}
                  placeholder="0"
style={{
  ...compactInputStyle,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  opacity: extraOptions.lift ? 1 : 0.5
}}
                />
<small style={compactSmallTextStyle}> €</small>
              </div>
            </div>

            <div
              className="toggle-switch"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExtra("lift");
              }}
            >
              <input type="checkbox" checked={!!extraOptions.lift} readOnly />
              <span className="toggle-slider" />
            </div>
          </div>
        </div>
      </div>

      <div className="side-menu__section" style={{ marginTop: compactSectionMarginTop }}>
        <div className="summary-row">
          <span>{t("vehicleRate")}</span>
          <strong>{price ? price.per_km.toFixed(2) + " EUR / km" : "—"}</strong>
        </div>
      </div>

{onAdminClick && (
  <div className="side-menu__section" style={{ marginTop: compactAdminMarginTop }}>
    <button
      type="button"
      onClick={() => {
        setIsOpen(false);
        onAdminClick();
      }}
      className="vehicle-button"
      style={{
        width: "100%",
        justifyContent: "center",
        background: adminNeedsSetup ? "#B11226" : undefined,
        borderColor: adminNeedsSetup ? "#B11226" : undefined,
        color: adminNeedsSetup ? "#ffffff" : undefined,
        fontWeight: adminNeedsSetup ? 800 : undefined,
        boxShadow: adminNeedsSetup
          ? "0 2px 8px rgba(177, 18, 38, 0.22)"
          : undefined
      }}
    >
      {adminNeedsSetup ? t("adminSetupButton") : `⚙ ${t("admin")}`}
    </button>
  </div>
)}
    </div>
  </>
);
}


export default App;
